import { Static, TLiteral, TNull, TNumber, TObject, TOptional, TUnion, Type } from '@sinclair/typebox';
import { PublicOAuthCallbackResponseObject, PublicOAuthCallbackResponseSchema } from './oauth';
import { AiFieldKind } from './ai-assist';
import { AvailabilityBookabilityImpactSchema } from './availability-bookability';
import { SMS_MARKETING_DEFAULT_SEND_TIME } from './sms-utils';
import { CatalogSelectionInputSchema, CatalogSelectionResponseSchema } from './catalog-selection';
import { COLOR_OPTIONS } from './colors/color_options';
import { CropDataSchema, CroppableImageSchema, CroppableImageResponseSchema, THUMBOR_META_KEY } from './image';
import { PrestationVideoSchema, PRESTATION_MAX_VIDEOS } from './vod';
import {
  DISTANCE_BAND_DISCOUNT_MAX,
  DISTANCE_BAND_DISCOUNT_MIN,
  DISTANCE_BAND_MIN_KM,
  DISTANCE_BANDS_MAX,
} from './ride-distance-bands';
import {
  LANDING_PATH_MAX_LENGTH,
  LP_VARIANT_MAX_LENGTH,
  REFERRER_DOMAIN_MAX_LENGTH,
} from './tracking/attribution-params';
import { VISITOR_ID_MAX_LENGTH } from './tracking/visitor-id';
import {
  CampaignErrorStatus,
  ProCampaignStatus,
  ProCampaignType,
  Subcription,
  SyncGoogleAccountType,
} from './namespace';
import { BillingPeriod } from './subscription';
import { intraEuVatNumberPattern } from './vat';
import { SubscriptionUsageSchema } from './subscription-limits';
import { GenreType } from './translation_namespace/gender_namespace';
import {
  ProfessionalAccessStatus,
  TeamMemberRole,
  TEAM_MEMBER_ROLE_TRANSLATE_KEY,
} from './translation_namespace/professional_namespace';
import {
  PrestationPaymentAmountMode,
  PrestationPaymentAmountModeSchema,
} from './translation_namespace/prestation_namespace';
import { ProfessionalOrigin } from './translation_namespace/professional_origin_namespace';
import { PriceOptionInputSchema, PriceOptionResponseSchema } from './price';
import { FormVisibilityPredicate } from './form-visibility';
import {
  BookingCustomFieldAnswerDisplaySchema,
  BookingCustomFieldAnswerSchema,
  BookingCustomFieldPublicSchema,
} from './booking_custom_field';
import {
  ProCustomerCustomFieldValueInputSchema,
  ProCustomerCustomFieldValueListSchema,
} from './pro-customer-custom-field';
import { BookingInstructionPublicSchema } from './booking_instruction';
import { RIDE_MAX_VEHICLES_PER_BOOKING } from './ride-vehicles';
import { AppointmentPaymentSummarySchema, BookingCancellationRefundInfoSchema } from './pro_payment';
import { PaymentInfosSchema } from './payment_infos';
import { ProResourceCategoryEnum, ProResourceTypeEnum } from './pro_resource/pro_resource.schema';
import {
  GmbPreviewReadStatus,
  GmbPushFieldStatus,
  GmbPushSkipReason,
  GmbPushStatus,
  SyncGmbStatus,
} from './translation_namespace/google_namespace';
import { PlaceKind } from './translation_namespace/place_kind_namespace';
import { PRO_PRESTATION_BOOKABILITY_STATUSES } from './translation_namespace/booking_bookability_reason_namespace';
import {
  MEMBER_ASSIGNMENT_STRATEGY_TRANSLATE_KEY,
  MemberAssignmentStrategy,
} from './translation_namespace/member_assignment_strategy_namespace';
import {
  AgendaPivotEnum,
  AgendaViewEnum,
  AppointmentType,
  BookingRecurrence,
  RecurrenceUpdateMode,
  ReservationCancelScope,
  ReservationStatus,
  RideDistanceBandsMode,
  RideTripLeg,
} from './translation_namespace/availabilities_namespace';
import { BookingSource, PublicBookingSourceSchema } from './translation_namespace/booking_source_namespace';
import {
  PrestationPaymentType,
  PrestationPaymentTypeSchema,
  PrestationType,
  PrestationTypeSchema,
  TRANSLATE_KEY_PRESTATION_PAYMENT_TYPE,
} from './translation_namespace/prestation_namespace';
import {
  ProPaymentOrigin,
  ProPaymentStatus,
  ProPaymentType,
  TRANSLATE_KEY_PRO_PAYMENT_ORIGIN,
  TRANSLATE_KEY_PRO_PAYMENT_STATUS,
  TRANSLATE_KEY_PRO_PAYMENT_TYPE,
} from './translation_namespace/pro_payment_namespace';
import {
  VoucherStatus,
  VoucherType,
  TRANSLATE_KEY_VOUCHER_STATUS,
  TRANSLATE_KEY_VOUCHER_TYPE,
} from './translation_namespace/voucher_namespace';
import { FilterConfig } from './filter/utils';
import { SortConfig, SortDirection, SortFieldConfig, sortNullsLast } from './filter/sort';
import {
  WebsiteConfigSchema,
  TrackingConfigSchema,
  SiteConfigSchema,
  RedirectsConfigSchema,
  ScriptsConfigSchema,
} from './website_types/WebsiteTypes';
import { PackResponseSchema, VoucherResponseSchema } from './pack';
import { PageTypeEnum } from './translation_namespace/page_type_namespace';
import { ReservationAmountsSchema } from './reservation_discount';

export { THUMBOR_META_KEY } from './image';
export {
  BOOKING_HORIZON_BOOKABLE_DAYS,
  BOOKING_MIN_HOURS_BEFORE_BOOKING,
  BOOKING_MIN_HOURS_BEFORE_CANCEL,
  BOOKING_REST_BETWEEN_BOOKING_MIN,
  BOOKING_STEP_MIN,
  FALLBACK_COUNTRY,
  maxCatalogCategorySelectable,
  maxStrLength,
  maxSupplementSelectable,
  maxTagSelectable,
} from './professional_constants';
export type { BookingParamOption } from './professional_constants';
import {
  BOOKING_HORIZON_BOOKABLE_DAYS,
  BOOKING_MIN_HOURS_BEFORE_BOOKING,
  BOOKING_MIN_HOURS_BEFORE_CANCEL,
  BOOKING_REST_BETWEEN_BOOKING_MIN,
  BOOKING_STEP_MIN,
  maxStrLength,
  maxSupplementSelectable,
  maxTagSelectable,
  type BookingParamOption,
} from './professional_constants';

const passwordMinLength = 8;
const passwordMaxLength = 128;

const TeamMemberRoleSchema = (extra?: Record<string, unknown>) =>
  Type.Union(
    [
      Type.Literal(TeamMemberRole.OWNER, { label: `${TEAM_MEMBER_ROLE_TRANSLATE_KEY}${TeamMemberRole.OWNER}` }),
      Type.Literal(TeamMemberRole.MANAGER, { label: `${TEAM_MEMBER_ROLE_TRANSLATE_KEY}${TeamMemberRole.MANAGER}` }),
      Type.Literal(TeamMemberRole.RECEPTIONIST, {
        label: `${TEAM_MEMBER_ROLE_TRANSLATE_KEY}${TeamMemberRole.RECEPTIONIST}`,
      }),
      Type.Literal(TeamMemberRole.EMPLOYEE, { label: `${TEAM_MEMBER_ROLE_TRANSLATE_KEY}${TeamMemberRole.EMPLOYEE}` }),
      Type.Literal(TeamMemberRole.APPRENTICE, {
        label: `${TEAM_MEMBER_ROLE_TRANSLATE_KEY}${TeamMemberRole.APPRENTICE}`,
      }),
      Type.Literal(TeamMemberRole.VIEWER, { label: `${TEAM_MEMBER_ROLE_TRANSLATE_KEY}${TeamMemberRole.VIEWER}` }),
    ],
    { title: 'TEAM_MEMBER_ROLE', meta: { multiple: false }, ...extra },
  );
const maxSmsTextSize = 1210;
const maxPhoneNumberLength = 15;
export const maxPlanning = 10;

export const passwordPattern = '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&]).+$';
export const hexColorPattern = '^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$';
export const phoneNumberPattern = '^(?:$|\\+\\d{1,3}\\d{4,14}(?:x.+)?)$';
const allMySmsSenderPattern = '^[A-Za-z][A-Za-z0-9]*$';
const urlPattern = '^https?://[^\\s]+$';
// Les sous-adresses (`alex+test@gmail.com`) sont ACCEPTÉES au signup depuis l'onboarding v4
// (écran 7) : le blocage historique anti multi-comptes renvoyait une erreur à des pros de bonne
// foi ; l'anti-abus passe par le numéro de téléphone (obligatoire, dédoublonné) et le captcha.
export const ERROR_TRAD_KEYS = {
  [hexColorPattern]: 'INVALID_COLOR_HEX',
  [phoneNumberPattern]: 'INVALID_PHONE',
  [passwordPattern]: 'INVALID_PASSWORD_FORMAT',
  [allMySmsSenderPattern]: 'INVALID_SMS_SENDER_FORMAT',
  [urlPattern]: 'INVALID_URL',
  [intraEuVatNumberPattern]: 'INVALID_VAT_NUMBER',
};

export const LoginProfessionalRequestSchema = Type.Object({
  email: Type.String({
    format: 'email',
    placeholder: 'INPUT_EMAIL',
    maxLength: maxStrLength,
  }),
  password: Type.String({
    minLength: 1,
    placeholder: 'INPUT_PASSWORD',
    meta: { input_type: 'password' },
    maxLength: maxStrLength,
  }),
});
const LoggedProfessionalDataSchema = Type.Object({
  email: Type.String({ format: 'email' }),
  name: Type.String(),
  professional_id: Type.Number(),
  role: TeamMemberRoleSchema(),
  account_firstname: Type.Optional(Type.String()),
  account_lastname: Type.Optional(Type.String()),
});

export const LoggedProfessionalResponseSchema = Type.Object({
  token: Type.String(),
  data: LoggedProfessionalDataSchema,
});

/**
 * Mobile-only login response. The native app cannot read the httpOnly refresh
 * cookie, so we hand it the token in the body for SecureStore persistence.
 * Never expose this schema on web routes.
 */
export const LoggedProfessionalMobileResponseSchema = Type.Object({
  token: Type.String(),
  refresh_token: Type.String(),
  refresh_token_expires_at: Type.String({ format: 'date-time' }),
  data: LoggedProfessionalDataSchema,
});

// Options de champs partagées par l'inscription et la création d'une entreprise
// supplémentaire (`CreateAdditionalCompanyRequestSchema`) : une seule définition des
// contraintes du nom commercial, de la ville, du téléphone et du fuseau horaire.
const PRO_BUSINESS_NAME_FIELD_OPTS = { placeholder: 'INPUT_BUSINESS_NAME', minLength: 1, maxLength: 255 };
const PRO_SIGNUP_CITY_FIELD_OPTS = { minLength: 1, maxLength: 128 };
const PRO_PHONE_NUMBER_FIELD_OPTS = {
  pattern: phoneNumberPattern,
  minLength: 6,
  maxLength: 30,
  meta: { input_type: 'phone_number' },
};
const PRO_TIMEZONE_FIELD_OPTS = { minLength: 1, maxLength: 64 };

export const SignupProfessionalRequestSchema = Type.Object({
  email: Type.String({
    format: 'email',
    placeholder: 'INPUT_EMAIL',
    maxLength: maxStrLength,
  }),
  // Mot de passe **optionnel** au signup pour réduire la friction. Si absent,
  // l'account est créé sans hash et l'user le définit plus tard via
  // `setInitialPassword` (nudge dashboard) ou « mot de passe oublié ».
  password: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PASSWORD',
      meta: { input_type: 'password' },
      minLength: passwordMinLength,
      maxLength: passwordMaxLength,
      pattern: passwordPattern,
    }),
  ),
  // Identité : `business_name` (chemin principal) OU `firstname + lastname`
  // (échappatoire « en création », l'identité devient "Prénom Nom"). Le backend
  // valide qu'au moins un des deux blocs est fourni (SIGNUP_IDENTITY_REQUIRED).
  business_name: Type.Optional(Type.String({ ...PRO_BUSINESS_NAME_FIELD_OPTS })),
  firstname: Type.Optional(
    Type.String({
      placeholder: 'INPUT_FIRSTNAME',
      minLength: 1,
      maxLength: 60,
    }),
  ),
  lastname: Type.Optional(
    Type.String({
      placeholder: 'INPUT_LASTNAME',
      minLength: 1,
      maxLength: 60,
    }),
  ),
  // Libellé ville saisi librement (pas géocodé — la route publique ne peut pas
  // appeler le proxy /app/places). Le géocodage précis (lat/lng → pro_place) se
  // fait au wizard ; ce texte amorce l'autocomplete et la détection business.
  // **Optionnel** : le tunnel preview (« on finalise ensemble ») ne saisit pas la ville,
  // le backend la dérive de `qualification.ville` du lead résolu par `preview_token`.
  // Le backend impose « city fournie OU résoluble depuis le lead » (SIGNUP_CITY_REQUIRED).
  city: Type.Optional(Type.String({ ...PRO_SIGNUP_CITY_FIELD_OPTS })),
  // E.164. Double usage : relance commerciale si abandon + recoupement avec le
  // téléphone renvoyé par la détection business (`place_found`).
  phone_number: Type.String({ ...PRO_PHONE_NUMBER_FIELD_OPTS }),
  // IANA timezone (ex: "Europe/Paris"). Validé côté service contre Intl.supportedValuesOf('timeZone').
  timezone: Type.String({ ...PRO_TIMEZONE_FIELD_OPTS }),
  accept_tos: Type.Literal(true),
  marketing_opt_in: Type.Optional(Type.Boolean()),
  // --- Attribution publicitaire (CAPI Meta / TikTok / OpenAI), tous optionnels ---
  // ⚠️ DEUX régimes, à ne jamais confondre (même politique que le widget Pulse, cf.
  // `captureUtmFromUrl`) :
  //   - identifiants de clic portés par l'URL (`fbclid`, `ttclid`, `oppref`) : simples
  //     paramètres d'URL, aucune lecture du terminal ⇒ joints DANS TOUS LES CAS. C'est
  //     la régie elle-même qui nous les a transmis en amenant le visiteur ; sans eux,
  //     un clic payant suivi d'un refus de cookies devient une conversion invisible
  //     pour la campagne qui l'a payée. Le backend en fait une conversion ANONYME
  //     (aucune PII, ni IP, ni cookie — cf. `MetaConversionProvider.buildAnonymousUserData`).
  //   - signaux issus de COOKIES (`fbp`, `fbc`, `ttp`, `obref`, `visitor_id`) : lecture du terminal
  //     ⇒ joints uniquement si `tracking_consent: true`, comme les PII hashées.
  tracking_consent: Type.Optional(Type.Boolean()),
  fbp: Type.Optional(Type.String({ maxLength: 128 })),
  fbc: Type.Optional(Type.String({ maxLength: 256 })),
  fbclid: Type.Optional(Type.String({ maxLength: 512 })),
  // TikTok : `ttclid` = identifiant de clic d'URL ; `ttp` = cookie du Pixel.
  ttclid: Type.Optional(Type.String({ maxLength: 512 })),
  ttp: Type.Optional(Type.String({ maxLength: 128 })),
  // OpenAI Ads : `oppref` = identifiant de clic (propagé de la LP par `?oppref=`, ou
  // cookie `__oppref` posé par le SDK sur ce sous-domaine) ; `obref` = référence
  // navigateur du SDK (cookie `__obref`, équivalent `_fbp`).
  oppref: Type.Optional(Type.String({ maxLength: 512 })),
  obref: Type.Optional(Type.String({ maxLength: 128 })),
  // Identifiant visiteur première partie (cookie `propelsia_vid` posé par la vitrine) :
  // `external_id` qui relie les pages vues d'avant l'inscription au compte créé.
  visitor_id: Type.Optional(Type.String({ maxLength: VISITOR_ID_MAX_LENGTH })),
  // --- Canal d'acquisition (mesure interne), envoyé SANS condition de consentement ---
  // Lus dans l'URL (`utm_*`, propagés depuis la LP) et dans le referrer de la visite :
  // aucun accès au terminal, donc hors régime cookies — et jamais transmis aux régies
  // publicitaires. Sans eux, un inscrit qui refuse les cookies pub est un inscrit dont
  // on ignore définitivement l'origine (cf. `tracking/attribution-params.ts`).
  utm_source: Type.Optional(Type.String({ maxLength: 128 })),
  utm_medium: Type.Optional(Type.String({ maxLength: 128 })),
  utm_campaign: Type.Optional(Type.String({ maxLength: 128 })),
  referrer_domain: Type.Optional(Type.String({ maxLength: REFERRER_DOMAIN_MAX_LENGTH })),
  utm_content: Type.Optional(Type.String({ maxLength: 128 })),
  utm_term: Type.Optional(Type.String({ maxLength: 128 })),
  utm_id: Type.Optional(Type.String({ maxLength: 64 })),
  // Identifiants Meta de la pub cliquée (`hsa_cam` / `hsa_grp` / `hsa_ad`, propagés depuis
  // la LP) : stables, contrairement au nom de campagne figé dans `utm_campaign`. Sans
  // `pattern` : une valeur abîmée ne doit jamais faire échouer l'inscription en 400, le
  // backend la filtre (`sanitizeAdPlatformId`).
  meta_campaign_id: Type.Optional(Type.String({ maxLength: 32 })),
  meta_adset_id: Type.Optional(Type.String({ maxLength: 32 })),
  meta_ad_id: Type.Optional(Type.String({ maxLength: 32 })),
  // Première page vue sur la vitrine (chemin seul, `?lp_path=` posé par les CTA).
  landing_page: Type.Optional(Type.String({ maxLength: LANDING_PATH_MAX_LENGTH })),
  // Bras de l'A/B de la LP (`?lp=` posé par les CTA de la vitrine, cf. `LP_VARIANT_FORWARD_QUERY_PARAM`) :
  // mesure interne, sans `pattern` (le backend filtre par `sanitizeLpVariant`, jamais de 400).
  lp_variant: Type.Optional(Type.String({ maxLength: LP_VARIANT_MAX_LENGTH })),
  // `event_id` généré côté front et réutilisé par le Pixel (`fbq(..., { eventID })`)
  // pour que Meta déduplique la remontée navigateur ↔ serveur de l'inscription.
  registration_event_id: Type.Optional(Type.String({ maxLength: 64 })),
  // Lead Pulse d'origine (funnel chat/preview). Transmis par le front à l'inscription pour poser
  // le pivot lead↔pro (`lead.professional_id`) — prérequis CRM 360, cf.
  // admin-cleanup-and-vision-plan §5.2. Non-fatal : un lead absent/déjà relié n'échoue pas le signup.
  lead_id: Type.Optional(Type.Number({ minimum: 1 })),
  // Token preview (funnel site preview → compte). Le front l'extrait du CTA (`?pt=`) posé
  // par la bannière du site preview. Le backend le résout en `lead_id` (lead.preview_token
  // UNIQUE) puis réutilise le chemin `lead_id` existant (pivot + handoff + hydratation).
  // On garde `lead_id` pour les autres funnels ; le funnel preview passe par le token.
  // Non-fatal : token absent/expiré n'échoue pas le signup.
  preview_token: Type.Optional(Type.String({ maxLength: 64 })),
  // Token visiteur du widget Pulse (`localStorage.propelsia_visitor_token`, même origine que
  // la page register). Rattrape le parcours « discute dans la bulle de la LP, puis s'inscrit
  // par le CTA de la LP » : ce lien-là ne porte aucun `?pt=`, le lead restait orphelin.
  // Le backend le résout en `lead_id` (lead.visitor_token UNIQUE). Volontairement SANS
  // `pattern` : une valeur abîmée dans le localStorage ne doit jamais faire échouer
  // l'inscription en 400 — token inconnu = ignoré, comme `preview_token`.
  visitor_token: Type.Optional(Type.String({ maxLength: 64 })),
  // Attribution affilié (funnel `?via=<ref_token>` → cookie `affiliate_ref` → signup).
  // Le backend résout `affiliate.ref_token` → `professional.referred_by_affiliate_id`
  // (refus silencieux si self-referral / token inconnu — non-fatal, comme preview_token).
  // cf. docs/to_implement/affiliation-v2-plan.md Lot 1.2.
  affiliate_ref: Type.Optional(Type.String({ maxLength: 64 })),
});

/**
 * Inscription web : la session, plus — quand l'inscrit vient du quiz (`preview_token` d'un lead
 * `source = quiz`) — le lien d'entrée dans l'app, jeton à usage unique et court
 * (`LEAD_QUIZ_HANDOFF_TTL_MS`). Le site l'ouvre hors du navigateur intégré ; le cookie de session
 * posé par cette réponse ne suivrait pas, le jeton dans l'URL si.
 */
export const SignedUpProfessionalResponseSchema = Type.Object({
  token: Type.String(),
  data: LoggedProfessionalDataSchema,
  handoff_url: Type.Optional(Type.String()),
});
export type SignedUpProfessionalResponseObject = Static<typeof SignedUpProfessionalResponseSchema>;
export type SignupProfessionalRequestObject = Static<typeof SignupProfessionalRequestSchema>;

/** Échange du jeton d'entrée du quiz (`/resume?handoff=`) : même contrat que le lien de reprise, jeton CONSOMMÉ. */
export const QuizHandoffExchangeBodySchema = Type.Object({
  token: Type.String({ minLength: 1, maxLength: 200 }),
});

/**
 * Échange d'un lien de reprise d'essai (email J0 → J7) contre une session pro.
 * Le token est posté (jamais consommé par le GET d'un scanner d'emails) et reste
 * réutilisable jusqu'à la fin de l'essai ou la pose d'un mot de passe.
 */
export const TrialResumeExchangeBodySchema = Type.Object({
  token: Type.String({ minLength: 1, maxLength: 200 }),
});

export const TrialResumeExchangeResponseSchema = Type.Object({
  token: Type.String(),
  data: LoggedProfessionalDataSchema,
  /** Chemin d'atterrissage (wizard en cours → `/onboarding`, sinon tableau de bord). */
  redirect_path: Type.String(),
});

export const SetInitialPasswordRequestSchema = Type.Object({
  password: Type.String({
    placeholder: 'INPUT_PASSWORD',
    meta: { input_type: 'password' },
    minLength: passwordMinLength,
    maxLength: passwordMaxLength,
    pattern: passwordPattern,
  }),
});

export const ForgotPasswordProfessionalRequestSchema = Type.Object({
  email: Type.String({
    format: 'email',
    placeholder: 'INPUT_EMAIL',
    maxLength: maxStrLength,
  }),
});

export const NewPasswordProfessionalRequestSchema = Type.Object({
  token: Type.String({ minLength: 32, maxLength: 64, meta: { hidden: true } }),
  password: Type.String({
    placeholder: 'INPUT_PASSWORD',
    meta: { input_type: 'password' },
    minLength: passwordMinLength,
    maxLength: passwordMaxLength,
    pattern: passwordPattern,
  }),
  confirm_password: Type.String({
    placeholder: 'INPUT_CONFIRM_PASSWORD',
    meta: { input_type: 'password' },
    minLength: passwordMinLength,
    maxLength: passwordMaxLength,
    pattern: passwordPattern,
  }),
});

/** Lien « mot de passe oublié » vérifié à l'ouverture de la page, SANS être consommé. */
export const RecoverPasswordTokenInfoRequestSchema = Type.Object({
  token: Type.String({ minLength: 32, maxLength: 64 }),
});

export const AcceptInvitationInfoRequestSchema = Type.Object({
  token: Type.String({ minLength: 32, maxLength: 64 }),
});

export const AcceptInvitationInfoResponseSchema = Type.Object({
  firstname: Type.Optional(Type.String()),
  lastname: Type.Optional(Type.String()),
  pro_name: Type.String(),
  /** true : compte sans mot de passe, il le choisit (`password`). false : il saisit le sien (`current_password`). */
  requires_password: Type.Boolean(),
});

export const AcceptInvitationRequestSchema = Type.Object({
  token: Type.String({ minLength: 32, maxLength: 64 }),
  /** Mot de passe CHOISI par un compte qui n'en a pas encore (règles de complexité). */
  password: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PASSWORD',
      meta: { input_type: 'password' },
      minLength: passwordMinLength,
      maxLength: passwordMaxLength,
      pattern: passwordPattern,
    }),
  ),
  /** Mot de passe ACTUEL d'un compte existant (vérifié, sans règles de complexité : ancien mot de passe). */
  current_password: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PASSWORD',
      meta: { input_type: 'password' },
      minLength: 1,
      maxLength: passwordMaxLength,
    }),
  ),
});

export const ChangePasswordRequestSchema = Type.Object({
  current_password: Type.String({
    placeholder: 'INPUT_PASSWORD',
    meta: { input_type: 'password' },
    minLength: 1,
    maxLength: passwordMaxLength,
  }),
  new_password: Type.String({
    placeholder: 'INPUT_PASSWORD',
    meta: { input_type: 'password' },
    minLength: passwordMinLength,
    maxLength: passwordMaxLength,
    pattern: passwordPattern,
  }),
});

export const CreateTagRequestSchema = Type.Object({
  title: Type.String({
    placeholder: 'INPUT_TAG_TITLE',
    title: 'INPUT_TAG_TOP_TITLE',
    minLength: 1,
    maxLength: maxStrLength,
  }),
  color: Type.String({
    title: 'INPUT_TAG_COLOR_TOP_TITLE',
    pattern: hexColorPattern,
    meta: {
      input_type: 'color_picker',
      options: COLOR_OPTIONS,
    },
  }),
});

// `PRO_TAG_TYPE`, `ProTagType`, `BANNED_SMART_TAG_FUNCTION_NAME` live in
// `./pro_tag_constants` so leaf modules (e.g. `filter/utils.ts`) can import
// them without dragging the full professional schema and triggering circular
// imports. The TypeBox enum stays here because it's part of the schema graph.
export const ProTagTypeEnum = Type.Union([Type.Literal('CUSTOM'), Type.Literal('SMART'), Type.Literal('PROPELSIA')]);

export const ProTagResponseSchema = Type.Object({
  pro_tag_id: Type.Number(),
  title: Type.String(),
  color: Type.String(),
  type: ProTagTypeEnum,
  smart_tag_function_name: Type.Optional(Type.String()),
  family: Type.Optional(Type.String()),
  importance: Type.Optional(Type.Number()),
});

export const UpdateTagRequestSchema = Type.Object({
  title: Type.String({
    placeholder: 'INPUT_TAG_TITLE',
    title: 'INPUT_TAG_TOP_TITLE',
    minLength: 1,
    maxLength: maxStrLength,
  }),
  color: Type.String({
    title: 'INPUT_TAG_COLOR_TOP_TITLE',
    pattern: hexColorPattern,
    meta: {
      input_type: 'color_picker',
      options: COLOR_OPTIONS,
    },
  }),
});
export type UpdateTagRequestObject = Static<typeof UpdateTagRequestSchema>;

export const AddressSchema = Type.Object(
  {
    formatted_address: Type.String({
      maxLength: 256,
    }),
    latitude: Type.Number({
      minimum: -90,
      maximum: 90,
    }),
    longitude: Type.Number({
      minimum: -180,
      maximum: 180,
    }),
    street: Type.Optional(
      Type.String({
        maxLength: 128,
      }),
    ),
    postal_code: Type.Optional(
      Type.String({
        maxLength: 16,
      }),
    ),
    city: Type.Optional(
      Type.String({
        maxLength: 128,
      }),
    ),
    region: Type.Optional(
      Type.String({
        maxLength: 400,
      }),
    ),
    country: Type.Optional(
      Type.String({
        maxLength: 64,
      }),
    ),
    country_code: Type.Optional(
      Type.String({
        maxLength: 3,
      }),
    ),
    // Identifiant opaque du provider externe (Google Places). Exposé pour
    // alimenter le cache /places/details (session_token) et lier un pro_place
    // à GMB. **NE PAS** persister dans `service_areas` (shape interne
    // provider-agnostique).
    place_id: Type.Optional(
      Type.String({
        maxLength: 200,
      }),
    ),
  },
  {
    meta: {
      input_type: 'address_object',
    },
    placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_ADDRESS',
    title: 'INPUT_TITLE_PRO_CUST_ADDRESS',
  },
);

export type AddressObject = Static<typeof AddressSchema>;

// Adresse d'un client final : le pro n'en connaît souvent que la ville (fiche prise au
// téléphone, clients migrés de la v1). `address_mode: 'geocode'` ouvre l'autocomplete aux
// villes / codes postaux en plus des adresses précises ; la shape reste celle d'`AddressSchema`
// (lat/lng = centre de la ville). Reconstruit via `Type.Object` (cf. UpsertProPlaceFormSchema).
export const CustomerAddressSchema = Type.Object(AddressSchema.properties, {
  ...AddressSchema,
  placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_ADDRESS_OR_CITY',
  meta: { ...(AddressSchema.meta ?? {}), address_mode: 'geocode' },
});

/**
 * « Client depuis » saisi par le pro (création / édition de la fiche) : date SEULE (`date_only`),
 * émise `yyyy-MM-ddT12:00:00` par le formulaire (midi : existe tous les jours, contrairement à
 * minuit les jours de passage à l'heure d'été) → `@TypeBoxBody` la convertit depuis le fuseau du
 * pro vers UTC, puis le service la ramène au début de ce jour chez le pro. Doit rester un
 * `Type.String` direct (une Union avec Null n'est pas convertie). Futur interdit : bloqué dans le
 * date picker (`max_date_offset_days: 0`, jour du pro) ET vérifié côté serveur
 * (`CUSTOMER_SINCE_IN_FUTURE`). Omis ou même jour que la valeur stockée = inchangé (édition) ;
 * omis = date du jour (création, DEFAULT de la colonne).
 */
const ProCustomerCustomerSinceInputSchema = Type.String({
  format: 'date-time',
  placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_CUSTOMER_SINCE',
  title: 'INPUT_TITLE_PRO_CUST_CUSTOMER_SINCE',
  description:
    "Date « client depuis » : date d'entrée du client chez le pro (ancienneté), distincte de la date de création de la fiche. Jamais dans le futur.",
  meta: { date_only: true, max_date_offset_days: 0 },
});

export const CreateProCustomerRequestSchema = Type.Object({
  firstname: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_NAME',
      title: 'INPUT_TITLE_PRO_CUST_NAME',
      maxLength: maxStrLength,
    }),
  ),
  lastname: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_LASTNAME',
      title: 'INPUT_TITLE_PRO_CUST_LASTNAME',
      maxLength: maxStrLength,
    }),
  ),
  gender: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_GENDER',
      title: 'INPUT_TITLE_PRO_CUST_GENDER',
      maxLength: maxStrLength,
      meta: {
        suggestions: Object.values(GenreType),
        disable_search: false,
        allow_free_text: true,
        multiple: false,
      },
    }),
  ),
  birthday: Type.Optional(
    Type.Object(
      {
        day: Type.Integer({
          format: 'int32',
          minimum: 1,
          maximum: 31,
        }),
        month: Type.Integer({
          format: 'int32',
          minimum: 1,
          maximum: 12,
        }),
        year: Type.Optional(
          Type.Integer({
            format: 'int32',
            minimum: 1900,
          }),
        ),
      },
      {
        meta: { input_type: 'birthday' },
        title: 'INPUT_TITLE_PRO_CUST_BIRTHDAY_YEAR_OPTIONNAL',
      },
    ),
  ),
  customer_since: Type.Optional(ProCustomerCustomerSinceInputSchema),
  // Facultatif : un client sans email (au comptoir, au téléphone) reste créable s'il est
  // identifiable autrement — cf. `hasProCustomerIdentifier`, vérifié par le backend.
  email: Type.Optional(
    Type.String({
      format: 'email',
      placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_EMAIL',
      title: 'INPUT_TITLE_PRO_CUST_EMAIL',
      maxLength: maxStrLength,
    }),
  ),
  phone_number: Type.Optional(
    Type.String({
      meta: { input_type: 'phone_number' },
      placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_PHONE_NUMBER',
      title: 'INPUT_TITLE_PRO_CUST_PHONE_NUMBER',
      pattern: phoneNumberPattern,
      maxLength: maxPhoneNumberLength,
    }),
  ),
  address: Type.Optional(CustomerAddressSchema),
  comment: Type.Optional(
    Type.String({
      meta: { multiline: true },
      placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_COMMENT',
      title: 'INPUT_TITLE_PRO_CUST_COMMENT',
      maxLength: 2000,
    }),
  ),
  pro_tags: Type.Optional(
    Type.Array(Type.Number(), {
      minItems: 0,
      maxItems: maxTagSelectable,
      meta: { input_type: 'pro_tag_selector' },
      placeholder: 'INPUT_PLACEHOLDER_CHOOSE',
      title: 'INPUT_TITLE_PRO_CUST_TAGS',
    }),
  ),
  /**
   * Champs personnalisés de la fiche (définis par le pro). Seuls les ids qui lui
   * appartiennent sont retenus ; une valeur vide efface le champ. Champ omis =
   * champs personnalisés inchangés.
   *
   * Pas de `title` : le widget rend lui-même son intitulé, uniquement quand le
   * pro a au moins un champ (sinon DynamicForm afficherait un libellé orphelin).
   * `keep_empty_values` : les `value: ''` doivent atteindre le serveur.
   */
  custom_fields: Type.Optional(
    Type.Array(ProCustomerCustomFieldValueInputSchema, {
      meta: { input_type: 'pro_customer_custom_fields', keep_empty_values: true },
    }),
  ),
});

export const UpdateProCustomerRequestSchema = Type.Object({
  firstname: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_NAME',
      title: 'INPUT_TITLE_PRO_CUST_NAME',
      maxLength: maxStrLength,
    }),
  ),
  lastname: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_LASTNAME',
      title: 'INPUT_TITLE_PRO_CUST_LASTNAME',
      maxLength: maxStrLength,
    }),
  ),
  gender: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_GENDER',
      title: 'INPUT_TITLE_PRO_CUST_GENDER',
      maxLength: maxStrLength,
      meta: {
        suggestions: Object.values(GenreType),
        disable_search: false,
        allow_free_text: true,
        multiple: false,
      },
    }),
  ),
  birthday: Type.Optional(
    Type.Object(
      {
        day: Type.Integer({
          format: 'int32',
          minimum: 1,
          maximum: 31,
        }),
        month: Type.Integer({
          format: 'int32',
          minimum: 1,
          maximum: 12,
        }),
        year: Type.Optional(
          Type.Integer({
            format: 'int32',
            minimum: 1900,
          }),
        ),
      },
      {
        meta: { input_type: 'birthday' },
        title: 'INPUT_TITLE_PRO_CUST_BIRTHDAY_YEAR_OPTIONNAL',
      },
    ),
  ),
  customer_since: Type.Optional(ProCustomerCustomerSinceInputSchema),
  email: Type.Optional(
    Type.String({
      format: 'email',
      placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_EMAIL',
      title: 'INPUT_TITLE_PRO_CUST_EMAIL',
      maxLength: maxStrLength,
    }),
  ),
  phone_number: Type.Optional(
    Type.String({
      meta: { input_type: 'phone_number' },
      placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_PHONE_NUMBER',
      title: 'INPUT_TITLE_PRO_CUST_PHONE_NUMBER',
      pattern: phoneNumberPattern,
      maxLength: maxPhoneNumberLength,
    }),
  ),
  address: Type.Optional(CustomerAddressSchema),
  comment: Type.Optional(
    Type.String({
      meta: { multiline: true },
      placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_COMMENT',
      title: 'INPUT_TITLE_PRO_CUST_COMMENT',
      maxLength: 2000,
    }),
  ),
  pro_tags: Type.Optional(
    Type.Array(Type.Number(), {
      minItems: 0,
      maxItems: 50,
      meta: { input_type: 'pro_tag_selector' },
      placeholder: 'INPUT_PLACEHOLDER_CHOOSE',
      title: 'INPUT_TITLE_PRO_CUST_TAGS',
    }),
  ),
  send_marketing_email: Type.Optional(
    Type.Boolean({
      placeholder: 'INPUT_TITLE_PRO_MARKETING_EMAIL',
    }),
  ),
  send_marketing_sms: Type.Optional(
    Type.Boolean({
      placeholder: 'INPUT_TITLE_PRO_MARKETING_SMS',
    }),
  ),
  /**
   * Champs personnalisés de la fiche (définis par le pro). Seuls les ids qui lui
   * appartiennent sont retenus ; une valeur vide efface le champ. Champ omis =
   * champs personnalisés inchangés.
   *
   * Pas de `title` : le widget rend lui-même son intitulé, uniquement quand le
   * pro a au moins un champ (sinon DynamicForm afficherait un libellé orphelin).
   * `keep_empty_values` : les `value: ''` doivent atteindre le serveur.
   */
  custom_fields: Type.Optional(
    Type.Array(ProCustomerCustomFieldValueInputSchema, {
      meta: { input_type: 'pro_customer_custom_fields', keep_empty_values: true },
    }),
  ),
});

export type UpdateProCustomerRequestObject = Static<typeof UpdateProCustomerRequestSchema>;

export type CreateProCustomerRequestObject = Static<typeof CreateProCustomerRequestSchema>;

/**
 * Une fiche client doit permettre de retrouver le client : un email, OU un téléphone, OU son
 * prénom ET son nom. Règle unique de la création manuelle (fiche, création rapide depuis
 * l'agenda) et de l'import de fichier.
 */
export const hasProCustomerIdentifier = (customer: {
  email?: string | null;
  phone_number?: string | null;
  firstname?: string | null;
  lastname?: string | null;
}): boolean => {
  const filled = (value: string | null | undefined): boolean => (value ?? '').trim().length > 0;
  return (
    filled(customer.email) || filled(customer.phone_number) || (filled(customer.firstname) && filled(customer.lastname))
  );
};

/**
 * Création RAPIDE d'un client depuis l'agenda (sélecteur de client, détail / pied du formulaire
 * de RDV) : même contrat que la création complète, sans la date « client depuis » — un client
 * créé au fil d'une prise de RDV est un nouveau client (date du jour = DEFAULT de la colonne).
 * Sert uniquement au rendu du formulaire : l'appel reste `POST /app/pro-customer`.
 */
export const QuickCreateProCustomerRequestSchema = Type.Omit(CreateProCustomerRequestSchema, ['customer_since']);

export const ExpertiseDomainsSchema = Type.Array(Type.String(), {
  meta: { input_type: 'string_list', sortable: true },
  title: 'INPUT_TITLE_EXPERTISE_DOMAINS',
  placeholder: 'INPUT_PLACEHOLDER_EXPERTISE_DOMAIN',
});

export const CareerPathSchema = Type.Array(
  Type.Object({
    name: Type.String({
      placeholder: 'INPUT_PLACEHOLDER_CAREER_NAME',
      // Plus long que `maxStrLength` : une ligne porte souvent intitulé + organisme + intervenants.
      maxLength: 255,
    }),
    start_year: Type.Integer({
      minimum: 1900,
      maximum: 2100,
      placeholder: 'INPUT_PLACEHOLDER_START_YEAR',
    }),
    end_year: Type.Optional(
      Type.Integer({
        minimum: 1900,
        maximum: 2100,
        placeholder: 'INPUT_PLACEHOLDER_END_YEAR',
      }),
    ),
  }),
  {
    meta: { input_type: 'career_path', sortable: true },
    title: 'INPUT_TITLE_CAREER_PATH',
  },
);

export const ProCustomerResponseSchema = Type.Object({
  pro_customer_id: Type.Number(),
  firstname: Type.String(),
  lastname: Type.String(),
  gender: Type.String(),
  email: Type.String(),
  birthday: Type.Object({
    day: Type.Number(),
    month: Type.Number(),
    year: Type.Optional(Type.Number()),
  }),
  phone_number: Type.String(),
  comment: Type.String(),
  address: Type.Optional(AddressSchema),
  pro_tags: Type.Array(ProTagResponseSchema),
  send_marketing_email: Type.Boolean(),
  send_marketing_sms: Type.Boolean(),
  has_refused_email: Type.Boolean(),
  has_refused_sms: Type.Boolean(),
  // « Client depuis » : date d'entrée MÉTIER du client (`pro_customer.customer_since`, jamais
  // `created_at` qui reste technique), formatée par le backend (règle date). Modifiable par le pro,
  // importable, restaurée depuis la v1, min des deux fiches à la fusion. Optionnel : une fiche sans
  // date lisible (select partiel) ne casse pas la réponse.
  // Position FIXE (ex-`created_at_formatted`) : `buildCsvFromSchema` suit l'ordre des clés, une
  // insertion au milieu décalerait les colonnes de l'export clients existant.
  customer_since_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'customer_since' } })),
  // Valeur brute (heure murale du pro `yyyy-MM-ddTHH:mm:ss`, cf. transformToSchema) : pré-remplit
  // le champ date du formulaire d'édition. Hors export CSV (la colonne formatée suffit).
  customer_since: Type.Optional(Type.String({ format: 'date-time', meta: { skip_csv: true } })),
  /**
   * Une entrée par champ personnalisé ACTIF du pro, y compris non renseigné
   * (`value: ''`) — l'UI rend le formulaire complet sans second appel.
   * `meta.skip_csv` : l'export émet une colonne par champ (cf.
   * `ProCustomerCustomFieldValueListSchema`), pas ce tableau aplati.
   */
  custom_fields: Type.Optional(ProCustomerCustomFieldValueListSchema),
});
export type ProCustomerResponseObject = Static<typeof ProCustomerResponseSchema>;

/**
 * Fiche client embarquée dans les réponses RDV / réservation (agenda, détail d'un RDV,
 * historique RDV d'un client). Dérivée de `ProCustomerResponseSchema` SANS `custom_fields` :
 * ces routes ne chargent pas les champs personnalisés (l'agenda n'affiche que téléphone et
 * e-mail) — les annoncer ferait promettre à Swagger/Orval un champ toujours absent. Les
 * champs personnalisés se lisent via les routes client (`GET /app/pro-customer/:id`, liste).
 */
export const AppointmentProCustomerResponseSchema = Type.Omit(ProCustomerResponseSchema, [
  'custom_fields',
  // Brut réservé au formulaire d'édition de la fiche : inutile dans les payloads agenda.
  'customer_since',
]);
export type AppointmentProCustomerResponseObject = Static<typeof AppointmentProCustomerResponseSchema>;

export const ListProCustomerResponseSchema = Type.Object({
  has_elements: Type.Boolean(),
  total: Type.Number(),
  items: Type.Array(ProCustomerResponseSchema),
});

export type ListProCustomerResponseObject = Static<typeof ListProCustomerResponseSchema>;

/**
 * Ligne de prestation d'un rendez-vous pro : la sélection catalogue partagée, plus les bornes
 * OPTIONNELLES de son sous-créneau.
 *
 * Ces bornes viennent du picker « par disponibilité » (le moteur renvoie un créneau par ligne de
 * la chaîne). Fournies, le backend les utilise telles quelles ; absentes, il redécoupe la chaîne
 * lui-même (`buildChainSegments`). Elles ne sont lues que sur une création manuelle
 * multi-prestations. `TypeBoxBody` convertit ces champs `date-time` depuis le fuseau du pro.
 */
/** Plafond du temps de battement d'un rendez-vous (une journée). */
export const APPOINTMENT_MAX_REST_MINUTES = 24 * 60;

const PrestationInputSchema = Type.Object({
  ...CatalogSelectionInputSchema.properties,
  slot_start: Type.Optional(Type.String({ format: 'date-time' })),
  slot_end: Type.Optional(Type.String({ format: 'date-time' })),
});
export const CreateAppointmentRequestSchema = Type.Object({
  start_pro: Type.String({
    format: 'date-time',
  }),
  end_pro: Type.String({
    format: 'date-time',
  }),
  effective_rest_minutes: Type.Optional(
    Type.Integer({
      minimum: 0,
      maximum: APPOINTMENT_MAX_REST_MINUTES,
      description:
        'Temps de battement (minutes) DÉJÀ inclus dans `end_pro` : le client voit une fin à `end_pro - effective_rest_minutes`. Absent = aucun battement. Ignoré sur une création multi-prestations, dont le backend calcule lui-même les battements.',
    }),
  ),
  name: Type.Optional(Type.String()),
  note: Type.Optional(Type.String()),
  color: Type.Optional(Type.String()),
  is_busy: Type.Optional(Type.Boolean()),
  pro_customer_id: Type.Optional(Type.Number()),
  pro_place_id: Type.Optional(Type.Number()),
  recurrence: Type.Enum(BookingRecurrence, {
    description:
      "Récurrence du rendez-vous : utiliser `ONCE` pour un rendez-vous simple sans récurrence (cas le plus courant). Il n'existe pas de valeur `NONE`.",
  }),
  // Règle RRULE canonique (sous-ensemble whitelisté, cf. lib/recurrence-rule.ts).
  // Quand présent, le backend dérive `recurrence` du FREQ ; absent + enum récurrent = synthèse legacy.
  recurrence_rrule: Type.Optional(Type.String({ maxLength: 255 })),
  prestation_input: Type.Optional(Type.Array(PrestationInputSchema)),
  team_member_ids: Type.Optional(Type.Array(Type.Number())),
  total_price_override: Type.Optional(Type.Number({ minimum: 0 })),
  type: Type.Optional(
    Type.Enum(AppointmentType, {
      description:
        'Seul `RIDE` est honoré : `GROUP` / `INDIVIDUAL` sont DÉRIVÉS côté serveur du format des prestations (`is_session`), jamais lus depuis le corps de la requête.',
    }),
  ),
  ride_details: Type.Optional(
    Type.Object({
      pickup_address: AddressSchema,
      dropoff_address: AddressSchema,
      trip_leg: Type.Enum(RideTripLeg),
    }),
  ),
  force_session_overbooking: Type.Optional(
    Type.Boolean({
      description:
        'Le pro a confirmé explicitement le dépassement de la capacité d’une séance après un refus `SESSION_FULL`. Desserre UNIQUEMENT la jauge de places (les ressources relèvent de `force_resource_overbooking`). Réservé au chemin pro — le tunnel client garde le refus dur.',
    }),
  ),
  force_resource_overbooking: Type.Optional(
    Type.Boolean({
      description:
        'Le pro a confirmé explicitement l’occupation d’une ressource déjà prise après un refus `RESOURCE_RESERVED`, `RESOURCE_CAPACITY_EXCEEDED` ou `RESOURCE_POOL_UNAVAILABLE`. La ressource est consommée quand même (surbooking visible). Un groupe trop grand pour la ressource relève de `force_resource_party_size`. Réservé au chemin pro — le tunnel client garde le refus dur.',
    }),
  ),
  force_resource_party_size: Type.Optional(
    Type.Boolean({
      description:
        'Le pro a confirmé explicitement un groupe plus grand que la ressource après un refus `RESOURCE_PARTY_SIZE_EXCEEDED`. Le groupe la prend quand même : en entier pour une ressource partagée, qui doit alors être libre (sinon `force_resource_overbooking`) ; dans un pool, le plus grand candidat libre est affecté. Réservé au chemin pro — le tunnel client garde le refus dur.',
    }),
  ),
  reservation_id: Type.Optional(Type.Number()),
});

export const UpdateAppointmentRequestSchema = Type.Object({
  start_pro: Type.Optional(
    Type.String({
      format: 'date-time',
    }),
  ),
  end_pro: Type.Optional(
    Type.String({
      format: 'date-time',
    }),
  ),
  effective_rest_minutes: Type.Optional(
    Type.Integer({
      minimum: 0,
      maximum: APPOINTMENT_MAX_REST_MINUTES,
      description:
        'Temps de battement (minutes) inclus dans `end_pro` : le client voit une fin à `end_pro - effective_rest_minutes`. Absent = battement actuel du rendez-vous conservé.',
    }),
  ),
  name: Type.Optional(Type.String()),
  note: Type.Optional(Type.String()),
  color: Type.Optional(Type.String()),
  is_busy: Type.Optional(Type.Boolean()),
  pro_customer_id: Type.Optional(Type.Number()),
  pro_place_id: Type.Optional(Type.Number()),
  update_mode: Type.Optional(Type.Enum(RecurrenceUpdateMode)),
  prestation_input: Type.Optional(Type.Array(PrestationInputSchema)),
  team_member_ids: Type.Optional(Type.Array(Type.Number())),
  total_price_override: Type.Optional(Type.Number({ minimum: 0 })),
  type: Type.Optional(
    Type.Enum(AppointmentType, {
      description:
        'Seul `RIDE` est honoré : `GROUP` / `INDIVIDUAL` sont DÉRIVÉS côté serveur du format des prestations (`is_session`), jamais lus depuis le corps de la requête.',
    }),
  ),
  ride_details: Type.Optional(
    Type.Object({
      pickup_address: AddressSchema,
      dropoff_address: AddressSchema,
      trip_leg: Type.Enum(RideTripLeg),
    }),
  ),
  /**
   * Course multi-véhicules : prix du VÉHICULE de ce créneau (unités mineures). Écrit sur le leg
   * ALLER du véhicule, puis le total de la réservation est recalculé (Σ des allers actifs).
   * `total_price_override` reste prioritaire : le montant saisi globalement par le pro fait foi.
   */
  ride_vehicle_price_minor: Type.Optional(Type.Number({ minimum: 0 })),
  shift_following_appointment_ids: Type.Optional(
    Type.Array(Type.Number(), {
      description:
        'Créneaux SUIVANTS de la même réservation à décaler du même écart que la fin du créneau édité (« Décaler les créneaux suivants »). Appliqué dans la même requête, pour que l’email de modification décrive l’état final de la réservation. Ignoré sur une occurrence de série récurrente ; seuls les créneaux qui suivent le créneau édité sont décalés.',
    }),
  ),
  force_session_overbooking: Type.Optional(
    Type.Boolean({
      description:
        'Le pro a confirmé explicitement le dépassement de la capacité d’une séance après un refus `SESSION_FULL`. Desserre UNIQUEMENT la jauge de places (les ressources relèvent de `force_resource_overbooking`). Réservé au chemin pro — le tunnel client garde le refus dur.',
    }),
  ),
  force_resource_overbooking: Type.Optional(
    Type.Boolean({
      description:
        'Le pro a confirmé explicitement l’occupation d’une ressource déjà prise après un refus `RESOURCE_RESERVED`, `RESOURCE_CAPACITY_EXCEEDED` ou `RESOURCE_POOL_UNAVAILABLE`. La ressource est consommée quand même (surbooking visible). Un groupe trop grand pour la ressource relève de `force_resource_party_size`. Réservé au chemin pro — le tunnel client garde le refus dur.',
    }),
  ),
  force_resource_party_size: Type.Optional(
    Type.Boolean({
      description:
        'Le pro a confirmé explicitement un groupe plus grand que la ressource après un refus `RESOURCE_PARTY_SIZE_EXCEEDED`. Le groupe la prend quand même : en entier pour une ressource partagée, qui doit alors être libre (sinon `force_resource_overbooking`) ; dans un pool, le plus grand candidat libre est affecté. Réservé au chemin pro — le tunnel client garde le refus dur.',
    }),
  ),
});

export type CreateAppointmentRequestObject = Static<typeof CreateAppointmentRequestSchema>;
export type UpdateAppointmentRequestObject = Static<typeof UpdateAppointmentRequestSchema>;

/**
 * POST /app/appointment/:appointment_id/amounts-preview — bloc Montant du formulaire d'édition pendant la
 * saisie : la réservation telle qu'un enregistrement de ces prestations la laisserait (rien n'est écrit).
 */
export const AppointmentAmountsPreviewRequestSchema = Type.Object({
  prestation_input: Type.Array(PrestationInputSchema),
});
export type AppointmentAmountsPreviewRequestObject = Static<typeof AppointmentAmountsPreviewRequestSchema>;

// Aperçu des prochaines occurrences d'une règle de récurrence (picker frontend).
// Les dates sont formatées côté backend (locale + timezone du pro) — règle Date Handling.
export const RecurrencePreviewRequestSchema = Type.Object({
  recurrence_rrule: Type.String({ minLength: 1, maxLength: 255 }),
  start_pro: Type.String({
    format: 'date-time',
  }),
  count: Type.Optional(Type.Integer({ minimum: 1, maximum: 10 })),
});

export type RecurrencePreviewRequestObject = Static<typeof RecurrencePreviewRequestSchema>;

export const RecurrencePreviewResponseSchema = Type.Object({
  occurrences_formatted: Type.Array(Type.String()),
  label: Type.String(),
  has_end: Type.Boolean(),
});

export type RecurrencePreviewResponseObject = Static<typeof RecurrencePreviewResponseSchema>;

/**
 * Per-user agenda display preferences, persisted on the logged-in team member.
 * Every field is optional so the frontend can send partial updates.
 */
export const AgendaSettingsSchema = Type.Object({
  slot_duration: Type.Optional(Type.Number()),
  first_hour: Type.Optional(Type.String()),
  pivot: Type.Optional(Type.Enum(AgendaPivotEnum)),
  default_view: Type.Optional(Type.Enum(AgendaViewEnum)),
  member_ids: Type.Optional(Type.Array(Type.Number())),
  resource_ids: Type.Optional(Type.Array(Type.Number())),
  resource_category: Type.Optional(Type.String()),
});

export type AgendaSettingsObject = Static<typeof AgendaSettingsSchema>;

export const CancelAppointmentRequestSchema = Type.Object({
  cancel_mode: Type.Optional(Type.Enum(RecurrenceUpdateMode)),
  delete_mode: Type.Optional(Type.Enum(RecurrenceUpdateMode)),
  cancellation_reason: Type.Optional(Type.String({ maxLength: 1000 })),
});

export type CancelAppointmentRequestObject = Static<typeof CancelAppointmentRequestSchema>;

export type DeleteAppointmentRequestObject = CancelAppointmentRequestObject;

export const CancelReservationRequestSchema = Type.Object({
  appointment_id: Type.Optional(Type.Number()),
  /**
   * Portée de l'annulation. `APPOINTMENT` détache UN créneau d'une réservation multi-créneaux
   * (la réservation reste active, son total est recalculé) et exige `appointment_id`.
   */
  scope: Type.Optional(Type.Enum(ReservationCancelScope)),
  cancel_mode: Type.Optional(Type.Enum(RecurrenceUpdateMode)),
  delete_mode: Type.Optional(Type.Enum(RecurrenceUpdateMode)),
  cancellation_reason: Type.Optional(Type.String({ maxLength: 1000 })),
  // Si true, re-crédite le solde des bons (voucher_usage) utilisés pour cette réservation.
  // Le remboursement du paiement, lui, se fait via l'endpoint dédié refundProPayment.
  restore_voucher: Type.Optional(Type.Boolean()),
});

export type CancelReservationRequestObject = Static<typeof CancelReservationRequestSchema>;

export const BulkCancelAppointmentsRequestSchema = Type.Object({
  appointment_ids: Type.Array(Type.Number(), { minItems: 1, maxItems: 1000 }),
  cancellation_reason: Type.Optional(Type.String({ maxLength: 1000 })),
});

// ── Bulk operations (generic target + customer-specific) ──

export const BulkDeleteCustomersRequestSchema = Type.Object({
  mode: Type.Union([Type.Literal('specific'), Type.Literal('all_matching')]),
  ids: Type.Optional(Type.Array(Type.Number(), { maxItems: 1000 })),
  excluded_ids: Type.Optional(Type.Array(Type.Number(), { maxItems: 1000 })),
  filters: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
});
export type BulkDeleteCustomersRequestObject = Static<typeof BulkDeleteCustomersRequestSchema>;

export const BulkUpdateCustomerTagsActionSchema = Type.Object({
  action: Type.Union([Type.Literal('add'), Type.Literal('remove'), Type.Literal('set')]),
  ids: Type.Array(Type.Number(), { minItems: 1, maxItems: 50 }),
});

export const BulkUpdateCustomersRequestSchema = Type.Object({
  mode: Type.Union([Type.Literal('specific'), Type.Literal('all_matching')]),
  ids: Type.Optional(Type.Array(Type.Number(), { maxItems: 1000 })),
  excluded_ids: Type.Optional(Type.Array(Type.Number(), { maxItems: 1000 })),
  filters: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
  updates: Type.Object({
    send_marketing_email: Type.Optional(Type.Boolean()),
    send_marketing_sms: Type.Optional(Type.Boolean()),
    comment: Type.Optional(Type.String({ maxLength: 2000 })),
    gender: Type.Optional(Type.String({ maxLength: 128 })),
    pro_tags: Type.Optional(BulkUpdateCustomerTagsActionSchema),
  }),
});
export type BulkUpdateCustomersRequestObject = Static<typeof BulkUpdateCustomersRequestSchema>;

export const BulkUpdateProCustomerFormSchema = Type.Object({
  send_marketing_email: Type.Optional(
    Type.Boolean({
      title: 'FILTER_TTTLE_ACCEPTED_MARKETING_EMAIL',
    }),
  ),
  send_marketing_sms: Type.Optional(
    Type.Boolean({
      title: 'FILTER_TTTLE_ACCEPTED_MARKETING_SMS',
    }),
  ),
  comment: Type.Optional(
    Type.String({
      title: 'INPUT_TITLE_PRO_CUST_COMMENT',
      maxLength: 2000,
      meta: { input_type: 'multiline' },
    }),
  ),
  add_tags: Type.Optional(
    Type.Array(Type.Number(), {
      title: 'BULK_ADD_TAGS_LABEL',
      meta: { input_type: 'pro_tag_selector' },
    }),
  ),
  remove_tags: Type.Optional(
    Type.Array(Type.Number(), {
      title: 'BULK_REMOVE_TAGS_LABEL',
      meta: { input_type: 'pro_tag_selector' },
    }),
  ),
});

// ── Bulk prestation schemas ──

// Bloc composite « règle de montant » d'une prestation (PaymentAmountRuleInput, porté par
// `payment_amount_mode` : renderer `payment_amount_rule` du DynamicForm, et même bloc dans la
// modification en masse) : un libellé COURT selon le type de paiement (meta.title_by), le détail
// (débité / prélevé / conservé, par personne…) est rendu en aide sous le champ. Déclaré avant les
// schémas bulk qui le référencent.
const PRESTATION_PAYMENT_RULE_TITLE_BY = {
  field: 'payment_type',
  titles: {
    [PrestationPaymentType.DEPOSIT]: 'INPUT_TITLE_PRO_PRESTATION_PAYMENT_RULE_DEPOSIT',
    [PrestationPaymentType.CARD_SAVE]: 'INPUT_TITLE_PRO_PRESTATION_PAYMENT_RULE_IMPRINT',
    [PrestationPaymentType.BANK_IMPRINT]: 'INPUT_TITLE_PRO_PRESTATION_PAYMENT_RULE_IMPRINT',
    [PrestationPaymentType.FULL_PAYMENT]: 'INPUT_TITLE_PRO_PRESTATION_PAYMENT_RULE_FULL',
    [PrestationPaymentType.FULL_PAYMENT_OPTIONAL]: 'INPUT_TITLE_PRO_PRESTATION_PAYMENT_RULE_FULL',
  },
};

// Visibilité des réglages de montant : jamais sans encaissement (payment_type FREE), dans le
// formulaire comme dans la modale bulk. En bulk, `percentage` / `fixed_amount` n'ont pas de saisie
// propre mais leurs conditions décident de ce qui part à la sauvegarde (le BulkEditSheet accepte une
// LISTE : `hidden_when` exclut dès qu'une est vraie, `visible_when` exige toutes) : le pourcentage
// en mode pourcentage, le montant fixe QU'en mode montant fixe.
const PRESTATION_PAYMENT_FREE_HIDDEN_WHEN = { field: 'payment_type', value: PrestationPaymentType.FREE };
const PRESTATION_PERCENTAGE_HIDDEN_WHEN = [
  PRESTATION_PAYMENT_FREE_HIDDEN_WHEN,
  { field: 'payment_amount_mode', value: PrestationPaymentAmountMode.FIXED_AMOUNT },
];
const PRESTATION_FIXED_AMOUNT_VISIBLE_WHEN = {
  field: 'payment_amount_mode',
  value: PrestationPaymentAmountMode.FIXED_AMOUNT,
};

export const BulkDeletePrestationsRequestSchema = Type.Object({
  mode: Type.Union([Type.Literal('specific'), Type.Literal('all_matching')]),
  ids: Type.Optional(Type.Array(Type.Number(), { maxItems: 1000 })),
  excluded_ids: Type.Optional(Type.Array(Type.Number(), { maxItems: 1000 })),
  filters: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
});
export type BulkDeletePrestationsRequestObject = Static<typeof BulkDeletePrestationsRequestSchema>;

export const BulkUpdatePrestationsRequestSchema = Type.Object({
  mode: Type.Union([Type.Literal('specific'), Type.Literal('all_matching')]),
  ids: Type.Optional(Type.Array(Type.Number(), { maxItems: 1000 })),
  excluded_ids: Type.Optional(Type.Array(Type.Number(), { maxItems: 1000 })),
  filters: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
  updates: Type.Object({
    catalog_category_id: Type.Optional(Type.Integer({ minimum: 1 })),
    payment_type: Type.Optional(PrestationPaymentTypeSchema()),
    payment_amount_mode: Type.Optional(PrestationPaymentAmountModeSchema()),
    percentage: Type.Optional(Type.Integer({ minimum: 0, maximum: 100 })),
    // Unités mineures, par unité facturée. Requis (> 0) dès que le mode demandé est FIXED_AMOUNT
    // (PRESTATION_FIXED_AMOUNT_REQUIRED, erreur métier du service).
    fixed_amount: Type.Optional(Type.Integer({ minimum: 0 })),
  }),
});
export type BulkUpdatePrestationsRequestObject = Static<typeof BulkUpdatePrestationsRequestSchema>;

export const BulkUpdatePrestationFormSchema = Type.Object({
  catalog_category_id: Type.Optional(
    Type.Integer({
      minimum: 1,
      meta: { input_type: 'catalog_category_selector' },
      title: 'INPUT_TITLE_PRO_PRESTATION_CATEGORY',
    }),
  ),
  payment_type: Type.Optional(
    PrestationPaymentTypeSchema({
      title: 'INPUT_TITLE_PAYMENT_TYPE',
    }),
  ),
  // Sous-champs de payment_type dans la modale bulk (meta.bulk_child_of) : pas de case à cocher
  // propre, ils s'éditent (et s'envoient) uniquement avec le type de paiement — tout ou rien.
  // Même bloc « règle de montant » que le formulaire de prestation (renderer `payment_amount_rule`,
  // pourcentage par défaut) : il écrit lui-même `percentage` / `fixed_amount` (meta.hidden).
  payment_amount_mode: Type.Optional(
    PrestationPaymentAmountModeSchema({
      title: 'INPUT_TITLE_PRO_PRESTATION_PAYMENT_RULE',
      default: PrestationPaymentAmountMode.PERCENTAGE,
      meta: {
        input_type: 'payment_amount_rule',
        title_by: PRESTATION_PAYMENT_RULE_TITLE_BY,
        bulk_child_of: 'payment_type',
        hidden_when: PRESTATION_PAYMENT_FREE_HIDDEN_WHEN,
      },
    }),
  ),
  percentage: Type.Optional(
    Type.Integer({
      minimum: 0,
      maximum: 100,
      meta: { hidden: true, bulk_child_of: 'payment_type', hidden_when: PRESTATION_PERCENTAGE_HIDDEN_WHEN },
    }),
  ),
  fixed_amount: Type.Optional(
    Type.Integer({
      minimum: 0,
      meta: {
        hidden: true,
        bulk_child_of: 'payment_type',
        visible_when: PRESTATION_FIXED_AMOUNT_VISIBLE_WHEN,
        hidden_when: PRESTATION_PAYMENT_FREE_HIDDEN_WHEN,
      },
    }),
  ),
});

export const PRO_PRESTATION_FILTERS: FilterConfig = {
  name: {
    type: 'string',
    label: 'INPUT_TITLE_PRO_PRESTATION_NAME',
    placeholder: 'FILTER_PLACEHOLDER_PRO_PRESTATION_SEARCH',
    api_description: 'Recherche texte libre parmi les prestations du pro : nom ou description (insensible à la casse).',
    customHandler: (value: unknown): Record<string, unknown> => {
      const text = typeof value === 'string' ? value.trim() : '';
      if (!text) return {};
      return {
        OR: [
          { name: { contains: text, mode: 'insensitive' } },
          { description: { contains: text, mode: 'insensitive' } },
        ],
      };
    },
  },
  catalog_category_id: {
    type: 'catalog_category_selector',
    label: 'INPUT_TITLE_PRO_PRESTATION_CATEGORY',
    placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_CATEGORY',
  },
  payment_type: {
    type: 'enum',
    label: 'INPUT_TITLE_PAYMENT_TYPE',
    placeholder: 'INPUT_TITLE_PAYMENT_TYPE',
    enum_values: Object.values(PrestationPaymentType),
    translate_key: TRANSLATE_KEY_PRESTATION_PAYMENT_TYPE,
  },
  supplements: {
    type: 'supplement_selector',
    multiple: true,
    advanced: true,
    label: 'FILTER_TITLE_SUPPLEMENTS',
    placeholder: 'FILTER_PLACEHOLDER_SUPPLEMENTS',
    customHandler: (value: unknown): Record<string, unknown> => {
      const rawValues = Array.isArray(value) ? value : [value];
      const ids = rawValues.map((v: unknown): number => Number(v)).filter(Number.isFinite);
      if (ids.length === 0) return {};
      return {
        supplements: { some: { supplement_id: { in: ids } } },
      };
    },
  },
  duration: {
    type: 'number-range',
    advanced: true,
    label: 'FILTER_TITLE_DURATION',
    placeholder: 'FILTER_PLACEHOLDER_DURATION',
    customHandler: (value: unknown): Record<string, unknown> => {
      const range = value as Record<string, unknown>;
      const min = range?.min != null ? Number(range.min) : undefined;
      const max = range?.max != null ? Number(range.max) : undefined;
      if (min === undefined && max === undefined) return {};
      const priceFilter: Record<string, number> = {};
      if (min !== undefined) priceFilter.gte = min;
      if (max !== undefined) priceFilter.lte = max;
      return {
        prestation_prices: { some: { duration_minutes: priceFilter, deleted_at: null } },
      };
    },
  },
  price: {
    type: 'number-range',
    advanced: true,
    label: 'FILTER_TITLE_PRICE',
    placeholder: 'FILTER_PLACEHOLDER_PRICE',
    customHandler: (value: unknown): Record<string, unknown> => {
      const range = value as Record<string, unknown>;
      const min = range?.min != null ? Number(range.min) * 100 : undefined;
      const max = range?.max != null ? Number(range.max) * 100 : undefined;
      if (min === undefined && max === undefined) return {};
      const priceFilter: Record<string, number> = {};
      if (min !== undefined) priceFilter.gte = min;
      if (max !== undefined) priceFilter.lte = max;
      return {
        prestation_prices: { some: { price: priceFilter, deleted_at: null } },
      };
    },
  },
};

// Préfixe commun à tous les tris de prestations : catégories dans leur ordre personnalisé (mêmes
// en-têtes que le front, qui regroupe par `catalog_category_id`). Le tri choisi par le pro
// s'applique donc À L'INTÉRIEUR de chaque catégorie — sans quoi la pagination plate entrelacerait
// les catégories et casserait les groupes.
const prestationCategoryOrderBy = (): Record<string, unknown>[] => [
  { catalog_category: { sort_order: SortDirection.ASC } },
  { catalog_category_id: SortDirection.ASC },
];

/**
 * Ordre des prestations au sein d'une catégorie, réglé par le pro (glisser-déposer) : position, puis
 * id croissant. Seule définition, pour TOUS les écrans : site public, tunnel de réservation, VTC,
 * achat de carte cadeau (le backend en dérive PRESTATION_DISPLAY_ORDER) et back-office (« Ordre
 * personnalisé » ci-dessous, derrière le préfixe catégorie). Un départage unique garde le même ordre
 * partout, même à positions égales (seed, migration v1).
 */
export const prestationCatalogOrderBy = (): Record<string, unknown>[] => [
  { sort_order: SortDirection.ASC },
  { prestation_id: SortDirection.ASC },
];

/** Tri de la liste des prestations (`/catalog/prestations`). */
export const PRO_PRESTATION_SORTS: SortConfig = {
  fields: {
    // Ordre personnalisé (glisser-déposer) : ordre par défaut de la liste, et ordre sur lequel
    // `reorderPrestation` (backend) calcule les positions, qui en dérive.
    manual: {
      label: 'SORT_FIELD_MANUAL',
      value_type: 'fixed',
      default_direction: SortDirection.ASC,
      order_by: (): Record<string, unknown>[] => [...prestationCategoryOrderBy(), ...prestationCatalogOrderBy()],
      api_description:
        "Ordre personnalisé du catalogue, réglé par le pro par glisser-déposer : catégories dans leur ordre, puis prestations dans leur ordre au sein de chaque catégorie. Sens unique (sort_dir ignoré). C'est l'ordre par défaut.",
    },
    name: {
      label: 'SORT_FIELD_NAME',
      value_type: 'text',
      default_direction: SortDirection.ASC,
      order_by: (direction: SortDirection): Record<string, unknown>[] => [
        ...prestationCategoryOrderBy(),
        { name: direction },
      ],
      api_description:
        "Nom de la prestation, par ordre alphabétique. Le tri s'applique à l'intérieur de chaque catégorie : les catégories gardent leur ordre personnalisé (la liste est regroupée par catégorie).",
    },
    created_at: {
      label: 'SORT_FIELD_ADDED_AT',
      value_type: 'date',
      default_direction: SortDirection.DESC,
      order_by: (direction: SortDirection): Record<string, unknown>[] => [
        ...prestationCategoryOrderBy(),
        { created_at: direction },
      ],
      api_description:
        "Date d'ajout de la prestation au catalogue. Le tri s'applique à l'intérieur de chaque catégorie : les catégories gardent leur ordre personnalisé (la liste est regroupée par catégorie).",
    },
  },
  default_field: 'manual',
  tiebreaker: 'prestation_id',
};

export const PRO_CUSTOMER_FILTERS: FilterConfig = {
  pro_customer_id: {
    type: 'string',
    label: 'INPUT_TITLE_PRO_PAYMENT_CUSTOMER',
    placeholder: 'INPUT_PLACEHOLDER_PRO_PAYMENT_CUSTOMER',
    api_description:
      "Recherche texte libre parmi les clients du pro : prénom, nom, email ou téléphone (insensible à la casse). Plusieurs mots sont combinés en ET et peuvent matcher des colonnes différentes (« prénom nom »). Un numéro saisi au format national est interprété dans le pays du pro. C'est LE paramètre à utiliser pour retrouver un client.",
  },
  updated_at: {
    type: 'date-range',
    advanced: true,
    label: 'FILTER_TTTLE_UPDATED_AT',
    placeholder: 'FILTER_PLACEHOLDER_DATE',
  },
  // Clé publique `created_at` CONSERVÉE (URL, Orval, catalogue MCP/Pulse) mais le filtre porte
  // sur `customer_since` (« Client depuis »). Ne pas renommer la clé : `sanitizeFilterKeys` ignore
  // silencieusement une clé inconnue — un ancien bundle lançant une action de masse « tous les
  // résultats » filtrée par date perdrait ce filtre et toucherait TOUS les clients du pro.
  created_at: {
    type: 'date-range',
    advanced: true,
    db_field: 'customer_since',
    // Date SEULE stockée au début du jour chez le pro : bornes lues en jours du pro (cf. FilterFieldConfig).
    day_bounds: true,
    label: 'FILTER_TITLE_CUSTOMER_SINCE',
    placeholder: 'FILTER_PLACEHOLDER_DATE',
    api_description:
      "Filtre sur la date « client depuis » (date d'entrée du client chez le pro, modifiable et importable — PAS la date d'ajout de la fiche, cf. added_at). Jours du pro, bornes incluses (AAAA-MM-JJ).",
  },
  // Date d'ajout TECHNIQUE de la fiche dans l'outil (`created_at`, jamais réécrite) : seul moyen de
  // retrouver un lot importé — ses clients portent leur date « client depuis » d'origine — pour le
  // contrôler ou le supprimer en masse.
  added_at: {
    type: 'date-range',
    advanced: true,
    db_field: 'created_at',
    day_bounds: true,
    label: 'FILTER_TITLE_ADDED_AT',
    placeholder: 'FILTER_PLACEHOLDER_DATE',
    api_description:
      "Filtre sur la date d'ajout de la fiche dans l'outil (création manuelle, réservation, import) — pas la date « client depuis ». Utile pour retrouver les clients d'un import. Jours du pro, bornes incluses (AAAA-MM-JJ).",
  },
  pro_tags: {
    type: 'pro_tag_selector',
    label: 'FILTER_TTTLE_TAGS',
    placeholder: 'FILTER_PLACEHOLDER_TAGS',
    db_field: 'pro_tags',
    // primary désactivé temporairement : les filtres mis en avant en haut de la
    // page clients sont à retravailler (UX). Ils restent dispo dans le panneau.
    include_types: ['CUSTOM', 'SMART', 'PROPELSIA'],
  },
  send_marketing_email: {
    type: 'boolean',
    advanced: true,
    label: 'FILTER_TTTLE_ACCEPTED_MARKETING_EMAIL',
    placeholder: 'FILTER_PLACEHOLDER_ACCEPTED_MARKETING_EMAIL',
  },
  send_marketing_sms: {
    type: 'boolean',
    advanced: true,
    label: 'FILTER_TTTLE_ACCEPTED_MARKETING_SMS',
    placeholder: 'FILTER_PLACEHOLDER_ACCEPTED_MARKETING_SMS',
  },
};

/**
 * Tri de la liste des clients (`/customers`). Défaut : « client depuis » (`customer_since`)
 * décroissant + tiebreaker `pro_customer_id` décroissant. La clé publique reste `created_at`
 * (URL, Orval, MCP) mais trie sur `customer_since` : clients migrés de la v1, importés avec leur
 * date d'entrée ou corrigés à la main reprennent leur place chronologique.
 */
export const PRO_CUSTOMER_SORTS: SortConfig = {
  fields: {
    created_at: {
      label: 'SORT_FIELD_CUSTOMER_SINCE',
      value_type: 'date',
      default_direction: SortDirection.DESC,
      order_by: (direction: SortDirection): Record<string, unknown>[] => [{ customer_since: direction }],
      api_description:
        "Date « client depuis » : date d'entrée du client chez le pro (modifiable par le pro, importable, date d'origine conservée pour les clients migrés de l'ancienne version) — pas la date de création technique de la fiche. C'est l'ordre par défaut, du plus récent au plus ancien.",
    },
    lastname: {
      label: 'SORT_FIELD_LASTNAME',
      value_type: 'text',
      default_direction: SortDirection.ASC,
      order_by: (direction: SortDirection): Record<string, unknown>[] => [
        { lastname: sortNullsLast(direction) },
        { firstname: sortNullsLast(direction) },
      ],
      api_description:
        'Nom de famille du client puis prénom, par ordre alphabétique. Les clients sans nom (ou sans prénom) sont toujours placés en dernier, quel que soit le sens.',
    },
    firstname: {
      label: 'SORT_FIELD_FIRSTNAME',
      value_type: 'text',
      default_direction: SortDirection.ASC,
      order_by: (direction: SortDirection): Record<string, unknown>[] => [
        { firstname: sortNullsLast(direction) },
        { lastname: sortNullsLast(direction) },
      ],
      api_description:
        'Prénom du client puis nom de famille, par ordre alphabétique. Les clients sans prénom (ou sans nom) sont toujours placés en dernier, quel que soit le sens.',
    },
  },
  default_field: 'created_at',
  tiebreaker: 'pro_customer_id',
};

// Nom puis prénom du client lié (relation `pro_customer`, obligatoire sur paiements et bons),
// valeurs vides en dernier : partagé par les tris des ventes et des bons.
const proCustomerNameOrderBy = (direction: SortDirection): Record<string, unknown>[] => [
  { pro_customer: { lastname: sortNullsLast(direction) } },
  { pro_customer: { firstname: sortNullsLast(direction) } },
];

export const PRO_PAYMENT_FILTERS: FilterConfig = {
  // Clé volontairement identique à la colonne : le périmètre imposé par l'onglet Ventes d'une
  // fiche client écrit lui aussi `pro_customer_id` et est appliqué APRÈS les filtres
  // (`_buildProPaymentWhereClauses`), donc une query string ne peut que restreindre, jamais élargir.
  pro_customer_id: {
    type: 'pro_customer_selector',
    label: 'FILTER_TITLE_PRO_PAYMENT_CUSTOMER',
    placeholder: 'FILTER_PLACEHOLDER_PRO_PAYMENT_CUSTOMER',
    api_description:
      "Filtre les paiements sur UN client précis, désigné par son `pro_customer_id`. Pour retrouver cet id à partir d'un nom, passer d'abord par la recherche texte de la liste des clients.",
  },
  status: {
    type: 'enum',
    label: 'FILTER_TITLE_PRO_PAYMENT_STATUS',
    placeholder: 'FILTER_PLACEHOLDER_PRO_PAYMENT_STATUS',
    enum_values: Object.values(ProPaymentStatus),
    translate_key: TRANSLATE_KEY_PRO_PAYMENT_STATUS,
    multiple: true,
  },
  payment_type: {
    type: 'enum',
    label: 'FILTER_TITLE_PRO_PAYMENT_TYPE',
    placeholder: 'FILTER_PLACEHOLDER_PRO_PAYMENT_TYPE',
    enum_values: Object.values(ProPaymentType),
    translate_key: TRANSLATE_KEY_PRO_PAYMENT_TYPE,
    multiple: true,
  },
  origin: {
    type: 'enum',
    label: 'FILTER_TITLE_PRO_PAYMENT_ORIGIN',
    placeholder: 'FILTER_PLACEHOLDER_PRO_PAYMENT_ORIGIN',
    enum_values: Object.values(ProPaymentOrigin),
    translate_key: TRANSLATE_KEY_PRO_PAYMENT_ORIGIN,
    multiple: true,
    // Pas de colonne `origin` en base : elle se déduit des relations `reservation` / `voucher`.
    // D'où le customHandler — `db_field` ne saurait pas exprimer ça.
    customHandler: (value: unknown): Record<string, unknown> => {
      const raw = Array.isArray(value)
        ? value.map(String)
        : typeof value === 'string'
          ? value.split(',').map((v: string): string => v.trim())
          : [];
      const origins = raw.filter((v: string): boolean =>
        (Object.values(ProPaymentOrigin) as string[]).includes(v),
      ) as ProPaymentOrigin[];
      if (origins.length === 0) return {};

      const clauses = origins.map((origin: ProPaymentOrigin): Record<string, unknown> => {
        if (origin === ProPaymentOrigin.RESERVATION) return { reservation_id: { not: null } };
        if (origin === ProPaymentOrigin.GIFT_CARD) return { voucher: { isNot: null } };
        return { reservation_id: null, voucher: { is: null } };
      });
      return { OR: clauses };
    },
  },
  created_at: {
    type: 'date-range',
    label: 'FILTER_TTTLE_CREATED_AT',
    placeholder: 'FILTER_PLACEHOLDER_DATE',
  },
  amount: {
    type: 'number-range',
    advanced: true,
    label: 'FILTER_TITLE_AMOUNT',
    placeholder: 'FILTER_PLACEHOLDER_AMOUNT',
    // Montants saisis en unité majeure (€) côté UI, stockés en centimes en base.
    customHandler: (value: unknown): Record<string, unknown> => {
      const range = value as Record<string, unknown>;
      const min = range?.min != null && range.min !== '' ? Number(range.min) * 100 : undefined;
      const max = range?.max != null && range.max !== '' ? Number(range.max) * 100 : undefined;
      const amountFilter: Record<string, number> = {};
      if (min !== undefined && Number.isFinite(min)) amountFilter.gte = min;
      if (max !== undefined && Number.isFinite(max)) amountFilter.lte = max;
      if (Object.keys(amountFilter).length === 0) return {};
      return { amount: amountFilter };
    },
  },
};

/** Tri de la liste des ventes (`/sales`). Défaut = ordre historique (`created_at` desc + `pro_payment_id` desc). */
export const PRO_PAYMENT_SORTS: SortConfig = {
  fields: {
    created_at: {
      label: 'SORT_FIELD_CREATED_AT',
      value_type: 'date',
      default_direction: SortDirection.DESC,
      order_by: (direction: SortDirection): Record<string, unknown>[] => [{ created_at: direction }],
      api_description:
        "Date de création du paiement (enregistrement de la vente ou émission de la demande de paiement). C'est l'ordre par défaut, du plus récent au plus ancien.",
    },
    paid_at: {
      label: 'SORT_FIELD_PAID_AT',
      value_type: 'date',
      default_direction: SortDirection.DESC,
      order_by: (direction: SortDirection): Record<string, unknown>[] => [{ paid_at: sortNullsLast(direction) }],
      api_description:
        "Date d'encaissement effectif du paiement. Les paiements jamais encaissés (en attente de paiement, échoués…) n'en ont pas et sont toujours placés en dernier, quel que soit le sens.",
    },
    amount: {
      label: 'SORT_FIELD_AMOUNT',
      value_type: 'number',
      default_direction: SortDirection.DESC,
      order_by: (direction: SortDirection): Record<string, unknown>[] => [{ amount: direction }],
      api_description: 'Montant total du paiement, brut, en centimes (avant remboursement éventuel).',
    },
    customer_name: {
      label: 'SORT_FIELD_CUSTOMER_NAME',
      value_type: 'text',
      default_direction: SortDirection.ASC,
      order_by: (direction: SortDirection): Record<string, unknown>[] => proCustomerNameOrderBy(direction),
      api_description:
        'Nom de famille puis prénom du client payeur, par ordre alphabétique. Les clients sans nom (ou sans prénom) sont toujours placés en dernier.',
    },
  },
  default_field: 'created_at',
  tiebreaker: 'pro_payment_id',
};

export const VOUCHER_FILTERS: FilterConfig = {
  status: {
    type: 'enum',
    label: 'FILTER_TITLE_VOUCHER_STATUS',
    placeholder: 'FILTER_PLACEHOLDER_VOUCHER_STATUS',
    // « En attente de paiement » (achat non finalisé) est masqué de la liste : on ne propose donc
    // pas ce statut dans le filtre. Les autres statuts restent disponibles.
    enum_values: Object.values(VoucherStatus).filter((status): boolean => status !== VoucherStatus.WAITING_PAYMENT),
    translate_key: TRANSLATE_KEY_VOUCHER_STATUS,
    multiple: true,
  },
  type: {
    type: 'enum',
    label: 'FILTER_TITLE_VOUCHER_TYPE',
    placeholder: 'FILTER_PLACEHOLDER_VOUCHER_TYPE',
    enum_values: Object.values(VoucherType),
    translate_key: TRANSLATE_KEY_VOUCHER_TYPE,
    multiple: true,
  },
  prestation_id: {
    type: 'prestation_selector',
    label: 'FILTER_TITLE_VOUCHER_PRESTATION',
    placeholder: 'FILTER_PLACEHOLDER_VOUCHER_PRESTATION',
    db_field: 'voucher_items',
    multiple: true,
  },
  supplement_id: {
    type: 'supplement_selector',
    label: 'FILTER_TITLE_VOUCHER_SUPPLEMENT',
    placeholder: 'FILTER_PLACEHOLDER_VOUCHER_SUPPLEMENT',
    multiple: true,
    advanced: true,
    // Sur un voucher_item les suppléments sont un tableau d'IDs (`selected_supplement_ids`),
    // d'où `hasSome`. On passe par `AND` pour composer avec le filtre prestation (qui écrit
    // aussi sur la relation `voucher_items`) sans l'écraser.
    customHandler: (value: unknown): Record<string, unknown> => {
      const ids = (Array.isArray(value) ? value : String(value ?? '').split(','))
        .map((v): number => Number(v))
        .filter((n): boolean => Number.isInteger(n) && n > 0);
      if (ids.length === 0) return {};
      return {
        AND: [{ voucher_items: { some: { selected_supplement_ids: { hasSome: ids }, deleted_at: null } } }],
      };
    },
  },
};

// Critères de tri des bons, déclarés une fois : réutilisés tels quels par SOLD_PACK_SORTS, qui
// n'en est qu'un sous-ensemble d'affichage (même endpoint GET /app/voucher, même liste blanche).
const VOUCHER_SORT_CREATED_AT: SortFieldConfig = {
  label: 'SORT_FIELD_CREATED_AT',
  value_type: 'date',
  default_direction: SortDirection.DESC,
  order_by: (direction: SortDirection): Record<string, unknown>[] => [{ created_at: direction }],
  api_description:
    "Date de création du bon (date d'achat pour une vente en ligne, date d'émission pour un bon créé par le pro). C'est l'ordre par défaut, du plus récent au plus ancien.",
};

const VOUCHER_SORT_EXPIRATION_DATE: SortFieldConfig = {
  label: 'SORT_FIELD_EXPIRATION_DATE',
  value_type: 'date',
  default_direction: SortDirection.ASC,
  order_by: (direction: SortDirection): Record<string, unknown>[] => [{ expiration_date: direction }],
  api_description:
    "Date d'expiration du bon (toujours renseignée). Par défaut les bons qui expirent le plus tôt en premier ; tous statuts confondus (combiner avec le filtre statut pour ne voir que les bons actifs).",
};

const VOUCHER_SORT_REMAINING_AMOUNT: SortFieldConfig = {
  label: 'SORT_FIELD_REMAINING_AMOUNT',
  value_type: 'number',
  default_direction: SortDirection.DESC,
  order_by: (direction: SortDirection): Record<string, unknown>[] => [{ remaining_amount: sortNullsLast(direction) }],
  api_description:
    "Solde restant du bon, en centimes. Ne concerne que les bons de type montant : les bons prestation et les packs n'ont pas de solde et sont toujours placés en dernier.",
};

const VOUCHER_SORT_CUSTOMER_NAME: SortFieldConfig = {
  label: 'SORT_FIELD_CUSTOMER_NAME',
  value_type: 'text',
  default_direction: SortDirection.ASC,
  order_by: (direction: SortDirection): Record<string, unknown>[] => proCustomerNameOrderBy(direction),
  api_description:
    "Nom de famille puis prénom du client acheteur (pas du bénéficiaire d'un cadeau), par ordre alphabétique. Les clients sans nom (ou sans prénom) sont toujours placés en dernier.",
};

/** Tri de la liste des bons (`/sales/coupons`, GET /app/voucher). Défaut = ordre historique (`created_at` desc + `voucher_id` desc). */
export const VOUCHER_SORTS: SortConfig = {
  fields: {
    created_at: VOUCHER_SORT_CREATED_AT,
    expiration_date: VOUCHER_SORT_EXPIRATION_DATE,
    remaining_amount: VOUCHER_SORT_REMAINING_AMOUNT,
    customer_name: VOUCHER_SORT_CUSTOMER_NAME,
  },
  default_field: 'created_at',
  tiebreaker: 'voucher_id',
};

// Onglet « Packs vendus » : le type est fixé à PACK côté page, on ne filtre que par statut.
export const SOLD_PACK_FILTERS: FilterConfig = {
  status: {
    type: 'enum',
    label: 'FILTER_TITLE_VOUCHER_STATUS',
    placeholder: 'FILTER_PLACEHOLDER_VOUCHER_STATUS',
    // « En attente de paiement » (achat non finalisé) est masqué de la liste : on ne propose donc
    // pas ce statut dans le filtre. Les autres statuts restent disponibles.
    enum_values: Object.values(VoucherStatus).filter((status): boolean => status !== VoucherStatus.WAITING_PAYMENT),
    translate_key: TRANSLATE_KEY_VOUCHER_STATUS,
    multiple: true,
  },
};

/**
 * Onglet « Packs vendus » : sous-ensemble d'AFFICHAGE de VOUCHER_SORTS (le solde restant n'a pas de
 * sens pour un pack). Mêmes critères, même `order_by` — seul le libellé de la date change. Le
 * backend (GET /app/voucher) continue de valider avec VOUCHER_SORTS.
 */
export const SOLD_PACK_SORTS: SortConfig = {
  fields: {
    created_at: { ...VOUCHER_SORT_CREATED_AT, label: 'SORT_FIELD_PURCHASE_DATE' },
    expiration_date: VOUCHER_SORT_EXPIRATION_DATE,
    customer_name: VOUCHER_SORT_CUSTOMER_NAME,
  },
  default_field: VOUCHER_SORTS.default_field,
  tiebreaker: VOUCHER_SORTS.tiebreaker,
};

export const CreateProTeamMemberRequestSchema = Type.Object({
  firstname: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_NAME',
      title: 'INPUT_TITLE_PRO_CUST_NAME',
      maxLength: maxStrLength,
    }),
  ),
  lastname: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_LASTNAME',
      title: 'INPUT_TITLE_PRO_CUST_LASTNAME',
      maxLength: maxStrLength,
    }),
  ),
  email: Type.Optional(
    Type.String({
      format: 'email',
      placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_EMAIL',
      title: 'INPUT_TITLE_PRO_CUST_EMAIL',
      maxLength: maxStrLength,
    }),
  ),
  description: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_TEAM_MEMBER_DESCRIPTION',
      title: 'INPUT_TITLE_TEAM_MEMBER_DESCRIPTION',
      maxLength: 5000,
      meta: {
        input_type: 'rich_text',
        allowed_tags: ['b', 'i', 'u', 'a', 'ul', 'ol'],
        ai: { kind: AiFieldKind.PERSON_BIO },
      },
    }),
  ),
  picture: Type.Optional(
    Type.Array(CroppableImageSchema, {
      meta: { picture: true, multiple: false, crop_ratio: 1 },
      placeholder: 'INPUT_PLACEHOLDER_TEAM_MEMBER_PICTURE',
      title: 'INPUT_TITLE_TEAM_MEMBER_PICTURE',
    }),
  ),
  expertise_domains: Type.Optional(ExpertiseDomainsSchema),
  career_path: Type.Optional(CareerPathSchema),
  has_agenda: Type.Boolean({
    default: true,
    title: 'INPUT_TITLE_TEAM_MEMBER_HAS_AGENDA',
  }),
  has_account: Type.Boolean({
    default: true,
    title: 'INPUT_TITLE_TEAM_MEMBER_HAS_ACCOUNT',
  }),
  role: Type.Optional(
    TeamMemberRoleSchema({
      default: TeamMemberRole.EMPLOYEE,
      meta: { multiple: false, visible_when: { field: 'has_account', value: true } },
    }),
  ),
});

export const UpdateProTeamMemberRequestSchema = Type.Object({
  firstname: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_NAME',
      title: 'INPUT_TITLE_PRO_CUST_NAME',
      maxLength: maxStrLength,
    }),
  ),
  lastname: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_LASTNAME',
      title: 'INPUT_TITLE_PRO_CUST_LASTNAME',
      maxLength: maxStrLength,
    }),
  ),
  email: Type.Optional(
    Type.String({
      format: 'email',
      placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_EMAIL',
      title: 'INPUT_TITLE_PRO_CUST_EMAIL',
      maxLength: maxStrLength,
    }),
  ),
  description: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_TEAM_MEMBER_DESCRIPTION',
      title: 'INPUT_TITLE_TEAM_MEMBER_DESCRIPTION',
      maxLength: 5000,
      meta: {
        input_type: 'rich_text',
        allowed_tags: ['b', 'i', 'u', 'a', 'ul', 'ol'],
        ai: { kind: AiFieldKind.PERSON_BIO },
      },
    }),
  ),
  picture: Type.Optional(
    Type.Array(CroppableImageSchema, {
      meta: { picture: true, multiple: false, crop_ratio: 1 },
      placeholder: 'INPUT_PLACEHOLDER_TEAM_MEMBER_PICTURE',
      title: 'INPUT_TITLE_TEAM_MEMBER_PICTURE',
    }),
  ),
  expertise_domains: Type.Optional(ExpertiseDomainsSchema),
  career_path: Type.Optional(CareerPathSchema),
  has_agenda: Type.Optional(
    Type.Boolean({
      title: 'INPUT_TITLE_TEAM_MEMBER_HAS_AGENDA',
    }),
  ),
  has_account: Type.Optional(
    Type.Boolean({
      title: 'INPUT_TITLE_TEAM_MEMBER_HAS_ACCOUNT',
    }),
  ),
  role: Type.Optional(
    TeamMemberRoleSchema({
      meta: { multiple: false, visible_when: { field: 'has_account', value: true } },
    }),
  ),
  // `sort_order` is intentionally NOT exposed in the update schema: ordering is
  // mutated only through the dedicated `/app/team-member/reorder` endpoint, which
  // validates the full set of ids in one transaction. Allowing per-row updates
  // here would let a client set arbitrary values and bypass that invariant.
});

export type CreateProTeamMemberRequestObject = Static<typeof CreateProTeamMemberRequestSchema>;
export type UpdateProTeamMemberRequestObject = Static<typeof UpdateProTeamMemberRequestSchema>;

export const ReorderProTeamMembersRequestSchema = Type.Object({
  ordered_ids: Type.Array(Type.Integer({ minimum: 1 })),
});

export type ReorderProTeamMembersRequestObject = Static<typeof ReorderProTeamMembersRequestSchema>;

export const ProTeamMemberArrayContentResponseSchema = Type.Object({
  team_member_id: Type.Number(),
  firstname: Type.String(),
  lastname: Type.String(),
  email: Type.Optional(Type.String()),
  description: Type.Optional(Type.String()),
  picture: Type.Optional(Type.Array(CroppableImageResponseSchema)),
  expertise_domains: ExpertiseDomainsSchema,
  career_path: CareerPathSchema,
  role: Type.Optional(TeamMemberRoleSchema()),
  professional_access_status: Type.Optional(
    Type.Union([Type.Literal(ProfessionalAccessStatus.ACTIVE), Type.Literal(ProfessionalAccessStatus.PENDING)]),
  ),
  has_agenda: Type.Boolean(),
  has_account: Type.Boolean(),
  sort_order: Type.Integer({ minimum: 0 }),
  /** Changement d'email de connexion en attente de confirmation par la nouvelle adresse. */
  pending_email: Type.Optional(Type.String()),
  /** Identifiant de cette demande (relancer / annuler depuis la liste d'équipe). */
  pending_email_change_request_id: Type.Optional(Type.Number()),
});

export type ProTeamMemberArrayContentResponseObject = Static<typeof ProTeamMemberArrayContentResponseSchema>;

export const ProTeamMemberResponseSchema = Type.Object({
  total: Type.Number(),
  items: Type.Array(ProTeamMemberArrayContentResponseSchema),
});

export type ProTeamMemberResponseObject = Static<typeof ProTeamMemberResponseSchema>;

export const SwitchProRequestSchema = Type.Object({
  professional_id: Type.Number(),
});

export const ProAccessResponseSchema = Type.Object({
  professional_id: Type.Number(),
  name: Type.String(),
  role: TeamMemberRoleSchema(),
  is_current: Type.Boolean(),
  account_firstname: Type.Optional(Type.String()),
  account_lastname: Type.Optional(Type.String()),
  // Optionnels : `getPendingInvitations` renvoie le même objet sans les calculer.
  origin: Type.Optional(Type.Enum(ProfessionalOrigin)),
  // Entreprise supplémentaire jamais payée : badge « À activer » dans le sélecteur,
  // entrée désactivée dans l'app mobile (achat impossible en WebView).
  requires_activation: Type.Optional(Type.Boolean()),
});

export type ProAccessResponseObject = Static<typeof ProAccessResponseSchema>;

/** Nombre maximum d'entreprises dont un même compte peut être propriétaire. */
export const MAX_OWNED_PROFESSIONALS_PER_ACCOUNT = 5;

/**
 * Raison pour laquelle un compte ne peut pas créer d'entreprise supplémentaire. Les valeurs
 * sont EXACTEMENT des clés `API_ERRORS` (fr-FR) : le front affiche la raison telle quelle,
 * et le backend lève la même clé sur le POST.
 */
export enum CompanyCreationBlockReason {
  IMPERSONATION_FORBIDDEN = 'COMPANY_CREATION_FORBIDDEN_DURING_IMPERSONATION',
  PASSWORD_AUTH_REQUIRED = 'PASSWORD_AUTH_REQUIRED',
  OWNER_REQUIRED = 'COMPANY_CREATION_OWNER_REQUIRED',
  PAST_DUE = 'COMPANY_CREATION_PAST_DUE',
  PENDING_ACTIVATION = 'COMPANY_CREATION_PENDING_ACTIVATION',
  LIMIT_REACHED = 'COMPANY_LIMIT_REACHED',
}

export const CompanyCreationEligibilityResponseSchema = Type.Object({
  eligible: Type.Boolean(),
  reason: Type.Optional(Type.Enum(CompanyCreationBlockReason)),
  owned_count: Type.Number(),
  max_owned: Type.Number(),
  // Entreprise supplémentaire jamais payée qui bloque la création (raison PENDING_ACTIVATION) :
  // le front propose « Finaliser » (bascule dessus) ou « Abandonner ».
  pending_professional_id: Type.Optional(Type.Number()),
  pending_professional_name: Type.Optional(Type.String()),
});
export type CompanyCreationEligibilityResponseObject = Static<typeof CompanyCreationEligibilityResponseSchema>;

/**
 * Création d'une entreprise supplémentaire depuis un compte existant. Volontairement SANS
 * champ email : l'email de contact de la nouvelle entreprise est celui du compte, et ne
 * change ensuite que par le flux confirmé `email_change`. Sans champ de tracking ni
 * `affiliate_ref` : ce n'est pas une acquisition.
 */
export const CreateAdditionalCompanyRequestSchema = Type.Object({
  business_name: Type.String({ ...PRO_BUSINESS_NAME_FIELD_OPTS }),
  city: Type.String({ ...PRO_SIGNUP_CITY_FIELD_OPTS }),
  // Code pays ISO-3166 alpha-2 de la ville choisie dans l'autocomplete : détermine le régime
  // de TVA initial (une ville DOM le laisse à arbitrer), le paiement passant avant l'onboarding.
  city_country_code: Type.Optional(Type.String({ pattern: '^[A-Za-z]{2}$' })),
  phone_number: Type.String({ ...PRO_PHONE_NUMBER_FIELD_OPTS }),
  timezone: Type.String({ ...PRO_TIMEZONE_FIELD_OPTS }),
});
export type CreateAdditionalCompanyRequestObject = Static<typeof CreateAdditionalCompanyRequestSchema>;

export const ExternalUrlTypeEnum = Type.Union([
  Type.Literal('website', { label: 'EXTERNAL_URL_TYPE_WEBSITE' }),
  Type.Literal('ecommerce', { label: 'EXTERNAL_URL_TYPE_ECOMMERCE' }),
  Type.Literal('facebook', { label: 'EXTERNAL_URL_TYPE_FACEBOOK' }),
  Type.Literal('instagram', { label: 'EXTERNAL_URL_TYPE_INSTAGRAM' }),
  Type.Literal('tiktok', { label: 'EXTERNAL_URL_TYPE_TIKTOK' }),
  Type.Literal('linkedin', { label: 'EXTERNAL_URL_TYPE_LINKEDIN' }),
  Type.Literal('twitter', { label: 'EXTERNAL_URL_TYPE_TWITTER' }),
  Type.Literal('pinterest', { label: 'EXTERNAL_URL_TYPE_PINTEREST' }),
  Type.Literal('youtube', { label: 'EXTERNAL_URL_TYPE_YOUTUBE' }),
  Type.Literal('soundcloud', { label: 'EXTERNAL_URL_TYPE_SOUNDCLOUD' }),
]);

export const EXTERNAL_URL_DOMAIN_TYPE_MAP: Record<string, string> = {
  'facebook.com': 'facebook',
  'fb.com': 'facebook',
  'instagram.com': 'instagram',
  'tiktok.com': 'tiktok',
  'linkedin.com': 'linkedin',
  'twitter.com': 'twitter',
  'x.com': 'twitter',
  'pinterest.com': 'pinterest',
  'pinterest.fr': 'pinterest',
  'youtube.com': 'youtube',
  'youtu.be': 'youtube',
  'soundcloud.com': 'soundcloud',
};

export function detectExternalUrlType(url: string): string | null {
  try {
    const hostname = new URL(url).hostname.replace(/^www\./, '');
    for (const [domain, type] of Object.entries(EXTERNAL_URL_DOMAIN_TYPE_MAP)) {
      if (hostname === domain || hostname.endsWith('.' + domain)) return type;
    }
  } catch {
    // invalid URL
  }
  return null;
}

export const ExternalUrlInputSchema = Type.Object({
  url: Type.String({ maxLength: 512, pattern: urlPattern, title: 'INPUT_TITLE_EXTERNAL_LINK' }),
  name: Type.Optional(Type.String({ maxLength: 128, title: 'INPUT_TITLE_EXTERNAL_LINK_NAME' })),
});

export const ExternalUrlSchema = Type.Object({
  url: Type.String({ maxLength: 512 }),
  type: ExternalUrlTypeEnum,
  name: Type.Optional(Type.String({ maxLength: 128 })),
});
export type ExternalUrlObject = Static<typeof ExternalUrlSchema>;

export const UpsertProProfileRequestSchema = Type.Object({
  name: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_COMMERCIAL_NAME',
      title: 'INPUT_TITLE_PRO_COMMERCIAL_NAME',
      maxLength: maxStrLength,
      section_title: 'UPSERT_SECTION_TITLE_PROFILE',
    }),
  ),
  slug: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_SLUG',
      title: 'INPUT_TITLE_PRO_SLUG',
      maxLength: 255,
      meta: { input_type: 'slug', linked_field: 'name' },
    }),
  ),
  phone_number: Type.Optional(
    Type.String({
      meta: { input_type: 'phone_number' },
      placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_PHONE_NUMBER',
      title: 'INPUT_TITLE_PRO_CUST_PHONE_NUMBER',
      pattern: phoneNumberPattern,
      maxLength: maxPhoneNumberLength,
    }),
  ),
  sms_sender_name: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_SMS_SENDER',
      title: 'INPUT_TITLE_PRO_SMS_SENDER',
      minLength: 3,
      maxLength: 11,
      pattern: allMySmsSenderPattern,
      meta: { hidden: true },
    }),
  ),
  // Expéditeur des SMS marketing (campagnes), distinct de l'expéditeur transactionnel
  // ci-dessus. Null = retour au défaut (nom du pro normalisé AllMySMS) : `keep_null`
  // empêche le pipe de validation de le confondre avec « absent = inchangé ».
  sms_marketing_sender_name: Type.Optional(
    Type.Union(
      [
        Type.String({
          minLength: 3,
          maxLength: 11,
          pattern: allMySmsSenderPattern,
        }),
        Type.Null(),
      ],
      {
        placeholder: 'INPUT_PLACEHOLDER_PRO_SMS_SENDER',
        title: 'INPUT_TITLE_PRO_SMS_MARKETING_SENDER',
        meta: { hidden: true, keep_null: true },
      },
    ),
  ),
  email_sender_name: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_EMAIL_SENDER',
      title: 'INPUT_TITLE_PRO_EMAIL_SENDER',
      maxLength: maxStrLength,
      meta: { hidden: true },
    }),
  ),
  legal_name: Type.Optional(
    Type.String({
      placeholder: 'INPUT_TITLE_PRO_LEGAL_NAME',
      title: 'INPUT_TITLE_PRO_LEGAL_NAME',
      maxLength: maxStrLength,
      section_title: 'UPSERT_SECTION_TITLE_LEGAL',
    }),
  ),
  siret_number: Type.Optional(
    Type.String({
      placeholder: 'INPUT_TITLE_PRO_SIRET',
      title: 'INPUT_TITLE_PRO_SIRET',
      maxLength: maxStrLength,
    }),
  ),
  vat_number: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_VAT_NUMBER',
      title: 'INPUT_TITLE_PRO_VAT_NUMBER',
      maxLength: 32,
      pattern: intraEuVatNumberPattern,
    }),
  ),
  billing_address: Type.Optional(
    Type.Object(AddressSchema.properties, {
      meta: {
        input_type: 'address_object',
      },
      placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_ADDRESS',
      title: 'INPUT_TITLE_PRO_BILLING_ADDRESS',
    }),
  ),
  address: Type.Optional(
    Type.Object(AddressSchema.properties, {
      meta: {
        input_type: 'address_object',
      },
      placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_ADDRESS',
      title: 'INPUT_TITLE_PRO_SIEGE_ADDRESS',
    }),
  ),
  description: Type.Optional(
    Type.String({
      meta: {
        input_type: 'rich_text',
        allowed_tags: ['h2', 'h3', 'b', 'i', 'u', 'a', 'ul', 'ol'],
        ai: { kind: AiFieldKind.BUSINESS_BIO },
      },
      placeholder: 'INPUT_PLACEHOLDER_PRO_DESCRIPTION',
      title: 'INPUT_TITLE_PRO_DESCRIPTION',
      section_title: 'UPSERT_SECTION_TITLE_PROFILE',
    }),
  ),
  // Le champ `metier` (free-text) a été supprimé en faveur de la table
  // `professional_activity` — l'onboarding utilise désormais `<ActivityPicker />`
  // (cf. docs/professional-activities-plan.md §7.2). Aucun champ ici ; les
  // mutations passent par `POST/PATCH/DELETE /app/professional/activities*`.
  external_urls: Type.Optional(
    Type.Array(ExternalUrlInputSchema, {
      title: 'INPUT_TITLE_PRO_EXTERNAL_URLS',
    }),
  ),
});
export type UpsertProProfileRequestObject = Static<typeof UpsertProProfileRequestSchema>;

export const UpsertProMediaRequestSchema = Type.Object({
  logo: Type.Optional(
    Type.Array(CroppableImageSchema, {
      meta: { picture: true, multiple: false },
      placeholder: 'INPUT_PLACEHOLDER_PRO_LOGO',
      title: 'INPUT_TITLE_PRO_LOGO',
    }),
  ),
  pictures: Type.Optional(
    Type.Array(CroppableImageSchema, {
      // 16/9 keeps establishment photos aligned with the website hero carousel
      // (HeroSchema.imagesUrl, same crop_ratio) so they can be connected to it
      // without re-cropping. Cf. `pro:pictures` collection binding in Bindings.ts.
      meta: { picture: true, multiple: true, crop_ratio: 16 / 9 },
      placeholder: 'INPUT_PLACEHOLDER_PRO_PICTURES',
      title: 'INPUT_TITLE_PRO_PICTURES',
    }),
  ),
});
export type UpsertProMediaRequestObject = Static<typeof UpsertProMediaRequestSchema>;

/**
 * Champ `<select>` d'un paramètre de réservation, dérivé de sa liste d'options partagée
 * (`professional_constants.ts`) — jamais de littéraux en dur ici.
 *
 * L'imbrication `Type.Number(Type.Union(...))` est volontaire : le schéma final reste
 * `type: 'number'` (donc `Static = number`, ce qu'attendent Prisma et l'API) tout en portant
 * le `anyOf` que `DynamicForm` lit pour rendre le select.
 *
 * `disable_search` masque la barre de recherche du `MultiSelect` : on la garde pour les listes
 * courtes, on la laisse pour celles qui dépassent une demi-douzaine d'entrées.
 */
const BookingParamSelectSchema = (
  translationKey: string,
  options: BookingParamOption[],
  disableSearch: boolean,
): TNumber =>
  Type.Number(
    Type.Union(
      options.map((option) => Type.Literal(option.value, { label: option.label })),
      {
        placeholder: translationKey,
        title: translationKey,
        meta: {
          multiple: false,
          disable_search: disableSearch,
        },
      },
    ),
  );

export const UpsertProBookingParametersRequestSchema = Type.Object({
  // « Recevoir une notification avant chaque RDV » ne vit plus ici : c'est l'événement
  // PRO_BOOKING_REMINDER, réglable par canal ET par délai depuis /marketing/notifications.
  confirm_every_booking: Type.Optional(
    Type.Boolean({
      placeholder: 'INPUT_PLACEHOLDER_PRO_CONFIRM_EVERY_BOOKING',
    }),
  ),
  minimum_time_before_booking: Type.Optional(
    BookingParamSelectSchema(
      'INPUT_PLACEHOLDER_PRO_MINIMUM_HOURS_BEFORE_BOOKING',
      BOOKING_MIN_HOURS_BEFORE_BOOKING,
      true,
    ),
  ),
  minimum_time_before_start_cancel_booking: Type.Optional(
    BookingParamSelectSchema(
      'INPUT_PLACEHOLDER_PRO_MINIMUM_HOURS_BEFORE_CANCEL_BOOKING',
      BOOKING_MIN_HOURS_BEFORE_CANCEL,
      false,
    ),
  ),
  rest_between_booking: Type.Optional(
    BookingParamSelectSchema('INPUT_PLACEHOLDER_PRO_GAP_BETWEEN_BOOKING', BOOKING_REST_BETWEEN_BOOKING_MIN, true),
  ),
  horizon_bookable: Type.Optional(
    BookingParamSelectSchema('INPUT_PLACEHOLDER_PRO_MAX_BOOKABLE_WINDOW', BOOKING_HORIZON_BOOKABLE_DAYS, false),
  ),
  booking_step_min: Type.Optional(BookingParamSelectSchema('INPUT_PLACEHOLDER_PRO_SLOT_STEP', BOOKING_STEP_MIN, true)),
  /* Sans fréquence (0), la grille repart déjà de la fin de chaque rendez-vous : le réglage n'a
   * d'effet qu'avec une fréquence configurée, d'où son masquage sinon. */
  anchor_slot_after_booking: Type.Optional(
    Type.Boolean({
      placeholder: 'INPUT_PLACEHOLDER_ANCHOR_SLOT_AFTER_BOOKING',
      meta: {
        visible_when: { field: 'booking_step_min', predicate: FormVisibilityPredicate.IS_POSITIVE_NUMBER },
      },
    }),
  ),
  rest_within_opening_hours: Type.Optional(
    Type.Boolean({
      placeholder: 'INPUT_PLACEHOLDER_REST_WITHIN_OPENING_HOURS',
    }),
  ),
  allow_multi_prestation: Type.Optional(
    Type.Boolean({
      placeholder: 'INPUT_PLACEHOLDER_ALLOW_MULTI_PRESTATION',
    }),
  ),
  allow_multi_member: Type.Optional(
    Type.Boolean({
      placeholder: 'INPUT_PLACEHOLDER_ALLOW_MULTI_MEMBER',
    }),
  ),
  /* Ordre d'essai des membres quand le client choisit « Sans préférence » dans le tunnel.
   * Union explicite (vs Type.Enum) pour attacher un `label` traduit à chaque littéral —
   * DynamicForm lit `anyOf[].label` pour rendre le select (cf. UpsertProPlaceFormSchema.kind).
   * Pas de défaut ici : la base pose PRIORITY à la création, et un corps partiel ne doit pas
   * écraser un choix BALANCED déjà enregistré. */
  member_assignment_strategy: Type.Optional(
    Type.Union(
      [
        Type.Literal(MemberAssignmentStrategy.PRIORITY, {
          label: `${MEMBER_ASSIGNMENT_STRATEGY_TRANSLATE_KEY}${MemberAssignmentStrategy.PRIORITY}`,
        }),
        Type.Literal(MemberAssignmentStrategy.BALANCED, {
          label: `${MEMBER_ASSIGNMENT_STRATEGY_TRANSLATE_KEY}${MemberAssignmentStrategy.BALANCED}`,
        }),
      ],
      {
        title: 'INPUT_TITLE_MEMBER_ASSIGNMENT_STRATEGY',
        placeholder: 'INPUT_PLACEHOLDER_MEMBER_ASSIGNMENT_STRATEGY',
        meta: { multiple: false, disable_search: true },
      },
    ),
  ),
  /* Compteur « X places » sur les créneaux CÔTÉ CLIENT (séances de groupe et libre-service porté
   * par des ressources). Les vues du pro (agenda, sélecteur de créneaux) l'affichent toujours :
   * ce réglage ne gouverne que ce que voit le client dans le tunnel. */
  show_remaining_places: Type.Optional(
    Type.Boolean({
      placeholder: 'INPUT_PLACEHOLDER_SHOW_REMAINING_PLACES',
    }),
  ),
  /* Retire du tunnel les prestations « Pas réservable en ligne » (aucun planning, aucun membre) au lieu
   * de les afficher grisées. Jamais une prestation complète, sur devis ni une carte Course (P3, § 7.8). */
  hide_not_bookable_online: Type.Optional(
    Type.Boolean({
      placeholder: 'INPUT_PLACEHOLDER_HIDE_NOT_BOOKABLE_ONLINE',
      meta: { hint: 'INPUT_HINT_HIDE_NOT_BOOKABLE_ONLINE' },
    }),
  ),
  /* Default booking-tunnel appearance (applied to every tunnel unless overridden per-link).
   * meta.hidden → excluded from the generic DynamicForm (agenda Calendar tab); edited only
   * by the dedicated BookingTunnelShareModal. */
  tunnel_accent_color: Type.Optional(Type.String({ meta: { hidden: true } })),
  tunnel_background_color: Type.Optional(Type.String({ meta: { hidden: true } })),
  tunnel_text_color: Type.Optional(Type.String({ meta: { hidden: true } })),
  tunnel_border_radius: Type.Optional(Type.Integer({ minimum: 0, meta: { hidden: true } })),
});
export type UpsertProBookingParametersRequestObject = Static<typeof UpsertProBookingParametersRequestSchema>;

const GEO_JSON_OBJ = Type.Object(
  {
    type: Type.String({
      enum: ['FeatureCollection'],
    }),
    features: Type.Array(
      Type.Object({
        type: Type.String({
          enum: ['Feature'],
        }),
        geometry: Type.Object({
          type: Type.String({
            enum: ['Polygon'],
          }),
          coordinates: Type.Array(
            Type.Array(
              Type.Array(Type.Number(), {
                minItems: 2,
                maxItems: 2,
              }),
              {
                minItems: 1,
              },
            ),
            {
              minItems: 1,
            },
          ),
        }),
      }),
      {
        minItems: 1,
      },
    ),
  },
  {
    title: 'INPUT_TITLE_GEO_JSON_LABEL',
    meta: {
      input_type: 'geo_json',
    },
  },
);

// ─── Opening hours (display-only) ────────────────────────────────────────
// Horaires informatifs d'un pro_place (ESTABLISHMENT principalement). Ne sert pas
// au moteur de disponibilités — un bouton « Convertir en disponibilités » est prévu.
const OpeningHoursTimeRangeSchema = Type.Object({
  open: Type.String({ pattern: '^\\d{2}:\\d{2}$' }),
  close: Type.String({ pattern: '^\\d{2}:\\d{2}$' }),
});
const OpeningHoursDaySchema = Type.Optional(Type.Array(OpeningHoursTimeRangeSchema, { maxItems: 4 }));
export const OpeningHoursSchema = Type.Object(
  {
    monday: OpeningHoursDaySchema,
    tuesday: OpeningHoursDaySchema,
    wednesday: OpeningHoursDaySchema,
    thursday: OpeningHoursDaySchema,
    friday: OpeningHoursDaySchema,
    saturday: OpeningHoursDaySchema,
    sunday: OpeningHoursDaySchema,
    exceptions: Type.Optional(
      Type.Array(
        Type.Object({
          date: Type.String({ format: 'date' }),
          ranges: Type.Optional(Type.Array(OpeningHoursTimeRangeSchema)),
          label: Type.Optional(Type.String({ maxLength: 120 })),
        }),
      ),
    ),
  },
  { meta: { input_type: 'opening_hours' } },
);
export type OpeningHoursObject = Static<typeof OpeningHoursSchema>;

// ─── type_config polymorphe (discriminé par kind côté Upsert) ────────────
// ⚠️ video_link et phone_number NE DOIVENT JAMAIS être exposés côté API publique.
// La diffusion se fait UNIQUEMENT via les notifications post-booking.
const EstablishmentConfigSchema = Type.Object({
  instructions: Type.Optional(Type.String({ maxLength: 1000 })),
});
/**
 * Centre de la zone « ville + rayon » d'un lieu en déplacement (onboarding v4, étape 2). Avec
 * `travel_radius_km`, le backend calcule `geo_json` (cercle) quand aucun polygone n'est dessiné,
 * pour qu'un lieu en déplacement ait TOUJOURS une zone (sinon « aucun lieu commun »).
 */
export const TravelingCenterSchema = Type.Object({
  label: Type.Optional(Type.String({ maxLength: 255 })),
  latitude: Type.Number({ minimum: -90, maximum: 90 }),
  longitude: Type.Number({ minimum: -180, maximum: 180 }),
});
export type TravelingCenterObject = Static<typeof TravelingCenterSchema>;

const TravelingConfigSchema = Type.Object({
  instructions: Type.Optional(Type.String({ maxLength: 1000 })),
  travel_radius_km: Type.Optional(Type.Integer({ minimum: 1, maximum: 500 })),
  center: Type.Optional(TravelingCenterSchema),
});
const PhoneConfigSchema = Type.Object({
  phone_number: Type.String({ minLength: 6, maxLength: 30, meta: { input_type: 'phone_number' } }),
  instructions: Type.Optional(Type.String({ maxLength: 1000 })),
});
const VideoConfigSchema = Type.Object({
  video_link: Type.String({ format: 'uri', maxLength: 2000 }),
  provider: Type.Optional(Type.String({ maxLength: 50 })),
  instructions: Type.Optional(Type.String({ maxLength: 1000 })),
});

const placeLabelString = Type.String({
  placeholder: 'INPUT_PLACEHOLDER_PRO_PLACES_LABEL',
  title: 'INPUT_PLACEHOLDER_PRO_PLACES_LABEL',
  minLength: 1,
  maxLength: maxStrLength,
});

// Schéma "permissif" englobant les 4 variantes possibles de type_config.
// Validation stricte des combinaisons kind ↔ champs requis = côté service.
const PolyTypeConfigSchema = Type.Object({
  instructions: Type.Optional(Type.String({ maxLength: 1000 })),
  travel_radius_km: Type.Optional(Type.Integer({ minimum: 1, maximum: 500 })),
  center: Type.Optional(TravelingCenterSchema),
  phone_number: Type.Optional(Type.String({ minLength: 6, maxLength: 30 })),
  video_link: Type.Optional(Type.String({ maxLength: 2000, pattern: '^https?://.+' })),
  provider: Type.Optional(Type.String({ maxLength: 50 })),
});

// Body de POST /app/pro-place et PUT /app/pro-place/:id.
//
// On N'utilise PAS de `Type.Union` discriminé : AJV est configuré avec
// `removeAdditional: 'all'` (mass-assignment guard) qui strip les champs absents
// de la première variante qu'il évalue — `type_config.video_link` serait dégagé
// avant la validation de la variante VIDEO. Voir typebox.validator.ts.
//
// Solution : schéma plat (tous optionnels sauf `kind`/`label`) + validation
// des contraintes kind-spécifiques côté service (createProPlace / putProPlace).
export const UpsertProPlaceRequestSchema = Type.Object({
  kind: Type.Enum(PlaceKind),
  label: placeLabelString,
  address: Type.Optional(AddressSchema),
  geo_json: Type.Optional(GEO_JSON_OBJ),
  type_config: Type.Optional(PolyTypeConfigSchema),
  opening_hours: Type.Optional(OpeningHoursSchema),
});
export type UpsertProPlaceRequestObject = Static<typeof UpsertProPlaceRequestSchema>;

// ─── Form view (plat) pour DynamicForm ───────────────────────────────────
// DynamicForm ne sait pas valider une Type.Union discriminée — il attend un TObject.
// Ce schéma plat regroupe tous les champs possibles, tous optionnels sauf `kind` et
// `label`. La validation stricte par variante reste portée par le serveur via
// `UpsertProPlaceRequestSchema` (le body est parsé par @TypeBoxBody).
// Pour aplatir `type_config.*` au top-level du form (DynamicForm ne supporte pas le
// `visible_when` sur les sous-champs d'un nested object). La page recombine ces
// champs en `type_config` avant l'API call si nécessaire.
export const UpsertProPlaceFormSchema = Type.Object({
  // Union explicite (vs Type.Enum) pour pouvoir attacher un `label` traduit sur chaque
  // littéral — DynamicForm lit `anyOf[].label` pour rendre le select.
  kind: Type.Union(
    [
      Type.Literal(PlaceKind.ESTABLISHMENT, { label: 'PLACE_KIND_ESTABLISHMENT' }),
      Type.Literal(PlaceKind.TRAVELING, { label: 'PLACE_KIND_TRAVELING' }),
      Type.Literal(PlaceKind.PHONE, { label: 'PLACE_KIND_PHONE' }),
      Type.Literal(PlaceKind.VIDEO, { label: 'PLACE_KIND_VIDEO' }),
    ],
    {
      title: 'INPUT_TITLE_PLACE_KIND',
      placeholder: 'INPUT_PLACEHOLDER_PLACE_KIND',
      meta: { multiple: false, disable_search: true },
    },
  ),
  label: placeLabelString,
  // On reconstruit le schéma via `Type.Object(properties, options)` au lieu de spreader le schéma —
  // le spread `{ ...AddressSchema }` perd le symbole TypeBox `[Kind]` (non-enumerable). Type.Object
  // le repose proprement et garantit que Static<>/TypeCompiler continuent à fonctionner.
  address: Type.Optional(
    Type.Object(AddressSchema.properties, {
      ...AddressSchema,
      meta: { ...(AddressSchema.meta ?? {}), visible_when: { field: 'kind', value: PlaceKind.ESTABLISHMENT } },
    }),
  ),
  geo_json: Type.Optional(
    Type.Object(GEO_JSON_OBJ.properties, {
      ...GEO_JSON_OBJ,
      meta: { ...(GEO_JSON_OBJ.meta ?? {}), visible_when: { field: 'kind', value: PlaceKind.TRAVELING } },
    }),
  ),
  phone_number: Type.Optional(
    Type.String({
      minLength: 6,
      maxLength: 30,
      title: 'INPUT_TITLE_PRO_CUST_PHONE_NUMBER',
      placeholder: 'INPUT_PLACEHOLDER_PRO_CUST_PHONE_NUMBER',
      meta: { input_type: 'phone_number', visible_when: { field: 'kind', value: PlaceKind.PHONE } },
    }),
  ),
  // `format: 'uri'` n'est pas enregistré dans le TypeCompiler côté frontend ;
  // la validation URL stricte reste portée par le serveur via le schéma discriminé.
  // Ici on se contente d'un pattern HTTP(S) minimal pour l'UX (feedback rapide).
  video_link: Type.Optional(
    Type.String({
      pattern: '^https?://.+',
      maxLength: 2000,
      title: 'INPUT_TITLE_VIDEO_LINK',
      placeholder: 'INPUT_PLACEHOLDER_VIDEO_LINK',
      meta: { visible_when: { field: 'kind', value: PlaceKind.VIDEO } },
    }),
  ),
  opening_hours: Type.Optional(
    Type.Object(OpeningHoursSchema.properties, {
      ...OpeningHoursSchema,
    }),
  ),
});
export type UpsertProPlaceFormObject = Static<typeof UpsertProPlaceFormSchema>;

// Types de lieu liables à une fiche Google. TRAVELING couvre les fiches « zone desservie »
// (sans adresse publique) : aucun champ poussé ni la lecture des avis ne dépend de l'adresse.
// PHONE / VIDEO restent exclus (aucune présence physique à représenter sur Maps).
export const GMB_LINKABLE_PLACE_KINDS: readonly PlaceKind[] = [PlaceKind.ESTABLISHMENT, PlaceKind.TRAVELING];

export const isGmbLinkablePlaceKind = (kind: PlaceKind | null | undefined): boolean =>
  !!kind && GMB_LINKABLE_PLACE_KINDS.includes(kind);

// Champs Cfixe poussables vers une fiche Google (opt-in par fiche). Le nom (`title`)
// n'est volontairement jamais poussé.
/* eslint-disable typebox-snake-case/typebox-object-keys-snake-case -- camelCase keys mirror the Google Business Profile API field-mask names, consumed verbatim as updateMask values in buildGmbPushPlan */
// Pas de `placeholder` : ce schéma n'est plus rendu par DynamicForm. Les libellés affichés
// vivent dans le namespace GMB_SYNC et sont portés par `labelKey` dans le registre backend.
export const GmbPushFieldsSchema = Type.Object({
  regularHours: Type.Optional(Type.Boolean()),
  websiteUri: Type.Optional(Type.Boolean()),
  description: Type.Optional(Type.Boolean()),
  phoneNumbers: Type.Optional(Type.Boolean()),
  url_appointment: Type.Optional(Type.Boolean()),
});
/* eslint-enable typebox-snake-case/typebox-object-keys-snake-case */
export type GmbPushFieldsObject = Static<typeof GmbPushFieldsSchema>;

export const SyncedGoogleLocationsResponseSchema = Type.Object({
  google_location_id: Type.Number(),
  name: Type.String(),
  title: Type.String(),
  /**
   * Lien public de la fiche sur Google Maps (`metadata.mapsUri`), tel que renvoyé par
   * l'API Business Profile. `Optional` : Google ne le fournit pas pour une fiche non
   * vérifiée / en cours de validation, et les fiches importées de la v1 peuvent l'avoir
   * vide. Le frontend n'affiche le lien « Voir ma fiche Google » que s'il est présent —
   * jamais d'URL reconstruite à la main, elle tomberait sur la mauvaise fiche.
   */
  maps_uri: Type.Optional(Type.String()),
  push_fields: Type.Optional(GmbPushFieldsSchema),
  pro_place: Type.Optional(
    Type.Object({
      pro_place_id: Type.Number(),
      label: Type.String(),
    }),
  ),
});

// Réponse pour l'API pro (authentifiée) — peut contenir video_link / phone_number.
// La réponse PUBLIQUE (PublicPlaceSchema) est restreinte côté public-professional.ts.
export const ProPlacesResponseSchema = Type.Object({
  pro_place_id: Type.Number(),
  kind: Type.Enum(PlaceKind),
  label: Type.String(),
  address: Type.Optional(AddressSchema),
  geo_json: Type.Optional(GEO_JSON_OBJ),
  type_config: Type.Optional(
    Type.Union([EstablishmentConfigSchema, TravelingConfigSchema, PhoneConfigSchema, VideoConfigSchema]),
  ),
  opening_hours: Type.Optional(OpeningHoursSchema),
  is_default: Type.Boolean(),
  google_location: Type.Optional(SyncedGoogleLocationsResponseSchema),
  slug: Type.Optional(Type.String()),
  publish_dedicated_page: Type.Boolean(),
});
export type ProPlacesResponseObject = Static<typeof ProPlacesResponseSchema>;

export const ProPlacesListResponseSchema = Type.Object({
  total: Type.Number(),
  items: Type.Array(ProPlacesResponseSchema),
});
export type ProPlacesListResponseObject = Static<typeof ProPlacesListResponseSchema>;

export const ProSyncGmbRequestSchema = Type.Object({
  url_type: Type.Enum(SyncGoogleAccountType),
  redirect_uri: Type.Optional(Type.String()),
  // Parcours lancé depuis l'app mobile : la page de callback renverra dans l'app
  // (lien `propelsia://`) au lieu de rester dans le navigateur système.
  from_mobile_app: Type.Optional(Type.Boolean()),
});
export type ProSyncGmbRequestObject = Static<typeof ProSyncGmbRequestSchema>;

export const ProSyncGmbResponseSchema = Type.Object({
  google_sync_url: Type.String(),
});
export type ProSyncGmbResponseObject = Static<typeof ProSyncGmbResponseSchema>;

export const ProSyncGmbCodeRequestSchema = Type.Object({
  google_code: Type.String(),
});
export type ProSyncGmbCodeRequestObject = Static<typeof ProSyncGmbCodeRequestSchema>;

export const PublicSyncGmbCodeRequestSchema = Type.Object({
  google_code: Type.String({ minLength: 1 }),
  state: Type.String({ minLength: 1 }),
});
export type PublicSyncGmbCodeRequestObject = Static<typeof PublicSyncGmbCodeRequestSchema>;

export const PublicSyncGmbCodeResponseSchema = PublicOAuthCallbackResponseSchema;
export type PublicSyncGmbCodeResponseObject = PublicOAuthCallbackResponseObject;

export const SyncedGoogleLocationsListResponseSchema = Type.Object({
  total: Type.Number(),
  items: Type.Array(SyncedGoogleLocationsResponseSchema),
});
export type SyncedGoogleLocationsListResponseObject = Static<typeof SyncedGoogleLocationsListResponseSchema>;

export const SyncPlaceWithGoogleRequestSchema = Type.Object({
  google_location_id: Type.Number(),
  pro_place_id: Type.Number(),
});
export type SyncPlaceWithGoogleRequestObject = Static<typeof SyncPlaceWithGoogleRequestSchema>;

export const UpdateGmbPushFieldsRequestSchema = GmbPushFieldsSchema;
export type UpdateGmbPushFieldsRequestObject = Static<typeof UpdateGmbPushFieldsRequestSchema>;

/**
 * Une ligne de l'aperçu « Fiche Google » : ce que Propelsia enverra, ce qui figure
 * aujourd'hui sur la fiche, et le résultat du dernier envoi.
 *
 * `source_value` / `google_value` sont des chaînes DÉJÀ FORMATÉES par le backend (horaires
 * lisibles, valeur tronquée comme à l'envoi) : le frontend les affiche telles quelles.
 */
export const GmbSyncFieldDiffSchema = Type.Object({
  /** Clé de `GmbPushFieldsSchema`. */
  field: Type.String(),
  /** Clé i18n du libellé, dans le namespace GMB_SYNC. */
  label_key: Type.String(),
  is_enabled: Type.Boolean(),
  /** `false` = champ activé mais rien à envoyer ; `not_pushable_reason` explique pourquoi. */
  is_pushable: Type.Boolean(),
  not_pushable_reason: Type.Optional(Type.Enum(GmbPushSkipReason)),
  source_value: Type.Optional(Type.String()),
  google_value: Type.Optional(Type.String()),
  is_different: Type.Boolean(),
  last_push_status: Type.Optional(Type.Enum(GmbPushFieldStatus)),
  /** Message Google tronqué — jamais affiché brut au pro, sert au support. */
  last_push_message: Type.Optional(Type.String()),
});
export type GmbSyncFieldDiffObject = Static<typeof GmbSyncFieldDiffSchema>;

export const GmbSyncStatusResponseSchema = Type.Object({
  google_location_id: Type.Number(),
  google_location_title: Type.String(),
  maps_uri: Type.Optional(Type.String()),
  /** Fraîcheur de la comparaison. L'onglet reste utilisable dans tous les cas. */
  read_status: Type.Enum(GmbPreviewReadStatus),
  google_read_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'google_snapshot_at', with_time: true } }),
  ),
  last_push_status: Type.Enum(GmbPushStatus),
  last_push_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'last_push_at', with_time: true } }),
  ),
  fields: Type.Array(GmbSyncFieldDiffSchema),
  /**
   * Autres fiches Google du pro — cible de l'action « appliquer à mes N lieux ».
   *
   * ⚠️ `Optional` à dessein : `transformToSchema` remplace un tableau VIDE par `null`
   * (cf. son traitement des `Type.Array`). Un pro mono-fiche — le cas le plus courant —
   * reçoit donc `null` et non `[]`. Le déclarer obligatoire donnait un type Orval
   * non-nullable qui masquait le trou au compilateur.
   */
  other_linked_places: Type.Optional(
    Type.Array(
      Type.Object({
        google_location_id: Type.Number(),
        label: Type.String(),
      }),
    ),
  ),
});
export type GmbSyncStatusResponseObject = Static<typeof GmbSyncStatusResponseSchema>;

export const GmbApplyPushFieldsResponseSchema = Type.Object({
  message: Type.String(),
  updated_count: Type.Number(),
});
export type GmbApplyPushFieldsResponseObject = Static<typeof GmbApplyPushFieldsResponseSchema>;

export const SyncGoogleAccountResponseSchema = Type.Object({
  sync_google_account_id: Type.Number(),
  id: Type.String(),
  verified_email: Type.Boolean(),
  email: Type.String(),
  name: Type.String(),
  given_name: Type.String(),
  family_name: Type.String(),
  picture: Type.String(),
  type: Type.Enum(SyncGoogleAccountType),
  status: Type.Enum(SyncGmbStatus),
  last_sync_at: Type.Optional(Type.String({ format: 'date-time' })),
  last_sync_at_string: Type.Optional(
    Type.String({
      format: 'date-time',
      meta: { is_short: true, with_time: true },
    }),
  ),
  nb_google_locations: Type.Optional(Type.Number()),
  linked_google_locations: Type.Optional(
    Type.Array(
      Type.Object({
        google_location_id: Type.Number(),
        google_location_name: Type.String(),
        pro_place_name: Type.String(),
        /** Lien public de la fiche sur Google Maps — cf. `SyncedGoogleLocationsResponseSchema`. */
        maps_uri: Type.Optional(Type.String()),
        push_fields: Type.Optional(GmbPushFieldsSchema),
      }),
    ),
  ),
});
export const ListSyncGoogleAccountResponseSchema = Type.Object({
  total: Type.Number(),
  items: Type.Array(SyncGoogleAccountResponseSchema),
});
export type ListSyncGoogleAccountResponseObject = Static<typeof ListSyncGoogleAccountResponseSchema>;

export const ProInfosSubResponseSchema = Type.Object({
  subscription_label: Type.Enum(Subcription),
  subscription_end_date: Type.String({ format: 'date-time' }),
  subscription_end_date_string: Type.String({
    format: 'date-time',
    meta: { is_short: true, with_time: false },
  }),
  subscription_commitment_end_date: Type.String({ format: 'date-time' }),
  subscription_commitment_end_date_string: Type.String({
    format: 'date-time',
    meta: { is_short: true, with_time: false },
  }),
  billing_period: Type.Enum(BillingPeriod),
  period_price_string: Type.String(),
  billing_cycle_anchor: Type.String({ format: 'date-time' }),
  billing_cycle_anchor_string: Type.String({
    format: 'date-time',
    meta: { is_short: true, with_time: true },
  }),
  amount: Type.Number(),
  amount_string: Type.String({
    meta: { is_price: true },
  }),
  amount_tax_free: Type.Number(),
  amount_tax_free_string: Type.String({
    meta: { is_price: true },
  }),
  vat_applicable_perc: Type.Number(),
  is_trial: Type.Boolean(),
  /**
   * Option marque blanche active : retire le branding Propelsia des emails et SMS
   * transactionnels, du footer du site vitrine et des pages publiques /book, /gift-card
   * et /manage, et déverrouille la personnalisation des modèles de notification.
   * Jamais active en essai — cf. resolveWhiteLabelEntitlement (source de vérité unique).
   */
  is_white_label: Type.Boolean(),
});

export const ProInfosSubSegmentResponseSchema = Type.Object({
  pro_subscription_segment_id: Type.Number(),
  subscription_label: Type.Enum(Subcription),
  start: Type.String({ format: 'date-time' }),
  start_string: Type.String({
    format: 'date-time',
    meta: { is_short: true, with_time: false },
  }),
  end: Type.String({ format: 'date-time' }),
  end_string: Type.String({
    format: 'date-time',
    meta: { is_short: true, with_time: false },
  }),
  discount_percentage: Type.Number(),

  amount_with_discount_tax_free: Type.Number(),
  amount_with_discount_tax_free_string: Type.String({
    meta: { is_price: true },
  }),

  amount_with_discount: Type.Number(),
  amount_with_discount_string: Type.String({ meta: { is_price: true } }),

  amount_tax_free_discount: Type.Number(),
  amount_tax_free_discount_string: Type.String({ meta: { is_price: true } }),

  amount: Type.Number(),
  amount_string: Type.String({ meta: { is_price: true } }),
  vat_applicable_perc: Type.Number(),
  is_trial: Type.Boolean(),
});

export const SmsPricesResponseSchema = Type.Object({
  iso_country_code: Type.String(),
  quota_sms_cost: Type.Number(),
  phone_prefix: Type.String(),
});
export const ListSmsPricesResponseSchema = Type.Object({
  list_popular_countries: Type.Array(SmsPricesResponseSchema),
  list_prices: Type.Array(SmsPricesResponseSchema),
});
export type ListSmsPricesResponseObject = Static<typeof ListSmsPricesResponseSchema>;

export const SmsPricesAcceptedRequestSchema = Type.Object({
  sms_country_code: Type.Array(Type.String(), {
    minItems: 0,
    maxItems: 300,
  }),
});
export type SmsPricesAcceptedRequestObject = Static<typeof SmsPricesAcceptedRequestSchema>;

const SmsPricesAccepted = Type.Object({
  iso_country_code: Type.String(),
  phone_prefix: Type.String(),
});

export const SmsPricesAcceptedResponseSchema = Type.Object({
  list_sms_accepted: Type.Array(SmsPricesAccepted),
});
export type SmsPricesAcceptedResponseObject = Static<typeof SmsPricesAcceptedResponseSchema>;

export const ProCampaignSmsCreateRequestSchema = Type.Object({
  name: Type.String({
    placeholder: 'GLOBAL_NAME',
    title: 'INPUT_TITLE_PRO_CAMPAIGN_NAME',
  }),
  pro_tags: Type.Optional(
    Type.Array(Type.Number(), {
      minItems: 0,
      maxItems: maxTagSelectable,
      meta: { input_type: 'pro_tag_selector', show_all_customers: true },
      placeholder: 'INPUT_PLACEHOLDER_CHOOSE',
      title: 'INPUT_TITLE_PRO_CAMPAIGN_RECIPIENTS',
    }),
  ),
  sender: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_SMS_CAMPAIGN_SENDER',
      title: 'INPUT_TITLE_PRO_SMS_CAMPAIGN_SENDER',
      minLength: 3,
      maxLength: 11,
      pattern: allMySmsSenderPattern,
    }),
  ),
  content: Type.String({
    meta: { multiline: true, show_campaign_cost: true, ai: { kind: AiFieldKind.SMS_BODY } },
    placeholder: 'INPUT_PLACEHOLDER_PRO_SMS_CAMPAIGN_CONTENT',
    title: 'INPUT_TITLE_PRO_SMS_CAMPAIGN_CONTENT',
    maxLength: maxSmsTextSize,
  }),
  track_links: Type.Optional(
    Type.Boolean({
      title: 'INPUT_TITLE_PRO_SMS_TRACK_LINKS',
      default: true,
      meta: {
        hidden: true,
        info_tooltip: 'INPUT_TOOLTIP_PRO_SMS_TRACK_LINKS',
      },
    }),
  ),
  // Promotion annoncée (automatique, code partagé ou codes personnels) : variables `promo:*` du texte ;
  // codes personnels : un code unique par destinataire, généré à l'envoi (`campaign-promotion.ts`).
  pro_promo_code_id: Type.Optional(
    Type.Number({
      meta: { input_type: 'pro_promo_code_selector' },
      title: 'INPUT_TITLE_PRO_CAMPAIGN_PROMO_CODE',
      placeholder: 'INPUT_PLACEHOLDER_PRO_CAMPAIGN_PROMO_CODE',
    }),
  ),
});
export type ProCampaignSmsCreateRequestObject = Static<typeof ProCampaignSmsCreateRequestSchema>;

export const ProCampaignEmailCreateRequestSchema = Type.Object({
  subject: Type.String({
    meta: { ai: { kind: AiFieldKind.EMAIL_SUBJECT } },
    placeholder: 'INPUT_PLACEHOLDER_CAMPAIGN_EMAIL_SUBJECT',
    title: 'INPUT_TITLE_CAMPAIGN_EMAIL_SUBJECT',
  }),
  pro_tags: Type.Optional(
    Type.Array(Type.Number(), {
      minItems: 0,
      maxItems: maxTagSelectable,
      meta: { input_type: 'pro_tag_selector', show_all_customers: true },
      placeholder: 'INPUT_PLACEHOLDER_CHOOSE',
      title: 'INPUT_TITLE_PRO_CAMPAIGN_RECIPIENTS',
    }),
  ),
  sender: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_CAMPAIGN_SENDER',
      title: 'INPUT_TITLE_PRO_CAMPAIGN_SENDER',
    }),
  ),
  reply_to: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_CAMPAIGN_REPLY_TO',
      title: 'INPUT_TITLE_PRO_CAMPAIGN_REPLY_TO',
      format: 'email',
    }),
  ),
  // Promotion annoncée (automatique, code partagé ou codes personnels) : variables `promo:*` et bloc
  // Code promo ; codes personnels : un code unique par destinataire, généré à l'envoi.
  pro_promo_code_id: Type.Optional(
    Type.Number({
      meta: { input_type: 'pro_promo_code_selector' },
      title: 'INPUT_TITLE_PRO_CAMPAIGN_PROMO_CODE',
      placeholder: 'INPUT_PLACEHOLDER_PRO_CAMPAIGN_PROMO_CODE',
    }),
  ),
});
export type ProCampaignEmailCreateRequestObject = Static<typeof ProCampaignEmailCreateRequestSchema>;

export const ScheduleProCampaignRequestSchema = Type.Object({
  scheduled_at: Type.String({
    format: 'date-time',
    title: 'INPUT_TITLE_PRO_CAMPAIGN_SCHEDULED_DATE',
    meta: { min_date_offset_days: 0, default_time: SMS_MARKETING_DEFAULT_SEND_TIME },
  }),
});
// Variante SMS : mêmes données (le backend valide avec ScheduleProCampaignRequestSchema),
// mais le champ porte le warning « fenêtre horaire » (déontologie SMS marketing :
// pas d'envoi 20h-8h ni le dimanche). Présentation uniquement, non bloquant.
export const ScheduleProCampaignSmsRequestSchema = Type.Object({
  scheduled_at: Type.String({
    format: 'date-time',
    title: 'INPUT_TITLE_PRO_CAMPAIGN_SCHEDULED_DATE',
    meta: { min_date_offset_days: 0, default_time: SMS_MARKETING_DEFAULT_SEND_TIME, quiet_hours_warning: true },
  }),
});
export const ProCampaignEmailSaveConfigSchema = Type.Object({
  email_config: Type.Unsafe<unknown>({ type: 'object' }),
});
export type ProCampaignEmailSaveConfigObject = Static<typeof ProCampaignEmailSaveConfigSchema>;

// Génération de contenu pilotée par l'IA : Pulse fournit du TEXTE (corps généré
// + CTA optionnel), le serveur l'assemble dans un email_config valide via
// `buildPromoEmailConfig`. Le LLM ne manipule jamais le blob du builder.
export const ComposeEmailContentRequestSchema = Type.Object({
  body_html: Type.String({
    minLength: 1,
    maxLength: 20000,
    title: 'INPUT_TITLE_CAMPAIGN_EMAIL_BODY',
    placeholder: 'INPUT_PLACEHOLDER_CAMPAIGN_EMAIL_BODY',
  }),
  cta_label: Type.Optional(Type.String({ maxLength: 80 })),
  cta_url: Type.Optional(Type.String({ maxLength: 2000 })),
  preview_text: Type.Optional(Type.String({ maxLength: 200 })),
});
export type ComposeEmailContentRequestObject = Static<typeof ComposeEmailContentRequestSchema>;

export const SendTestCampaignRequestSchema = Type.Object({
  recipient: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_CAMPAIGN_TEST_RECIPIENT',
      title: 'INPUT_TITLE_PRO_CAMPAIGN_TEST_RECIPIENT',
    }),
  ),
});
export type SendTestCampaignRequestObject = Static<typeof SendTestCampaignRequestSchema>;

/**
 * Aperçu d'une campagne email, rendu par le backend avec le même contexte que l'envoi (données
 * des blocs prestation, liens vers les pages du site) : la prévisualisation montre ce que les
 * clients recevront. Variables client remplacées par des valeurs d'exemple. `html` vide = aucun
 * contenu email configuré.
 */
export const ProCampaignEmailPreviewResponseSchema = Type.Object({
  html: Type.String(),
});
export type ProCampaignEmailPreviewResponseObject = Static<typeof ProCampaignEmailPreviewResponseSchema>;

export const ProCampaignStatsResponseSchema = Type.Object({
  clicked_perc: Type.String(),
  opened_perc: Type.String(),
  delivered_count: Type.Number(),
  processed_count: Type.Number(),
});

export const ProCampaignResponseSchema = Type.Object({
  pro_campaign_id: Type.Number(),
  name: Type.String(),
  sender: Type.String(),
  reply_to: Type.Optional(Type.String()),
  subject: Type.String(),
  type: Type.Enum(ProCampaignType),
  pro_tags: Type.Array(ProTagResponseSchema),
  content: Type.String(),
  track_links: Type.Boolean(),
  email_config: Type.Optional(Type.Unsafe<unknown>({ type: 'object' })),
  status: Type.Enum(ProCampaignStatus),
  error_status: Type.Optional(Type.Enum(CampaignErrorStatus)),
  last_error_date: Type.Optional(Type.String({ format: 'date-time' })),
  last_error_date_string: Type.Optional(
    Type.String({
      format: 'date-time',
      meta: { is_short: true, with_time: true },
    }),
  ),
  stats_data: Type.Optional(ProCampaignStatsResponseSchema),
  nb_recipients: Type.Number(),
  sent_date: Type.Optional(Type.String({ format: 'date-time' })),
  sent_date_string: Type.Optional(
    Type.String({
      format: 'date-time',
      meta: { is_short: true, with_time: true },
    }),
  ),
  scheduled_at: Type.Optional(Type.String({ format: 'date-time' })),
  // Promotion jointe — pré-remplit le champ « Promotion » à l'édition.
  pro_promo_code_id: Type.Optional(Type.Number()),
  scheduled_at_string: Type.Optional(
    Type.String({
      format: 'date-time',
      meta: { is_short: true, with_time: true },
    }),
  ),
});
export type ProCampaignResponseObject = Static<typeof ProCampaignResponseSchema>;

export const ProCampaignListResponseSchema = Type.Object({
  total: Type.Number(),
  items: Type.Array(ProCampaignResponseSchema),
});
export type ProCampaignListResponseObject = Static<typeof ProCampaignListResponseSchema>;

/** Tri de la liste des campagnes (`/marketing/campaigns`). */
export const PRO_CAMPAIGN_SORTS: SortConfig = {
  fields: {
    // Seule définition de l'ordre de la liste des campagnes (email + SMS, même table) : le backend en
    // dérive PRO_CAMPAIGN_LIST_ORDER_BY (GET /app/pro-sms-campaign). Trier par `pro_campaign_id` ne
    // tient plus depuis la migration v1 (historique inséré d'un bloc, sans rapport avec la chronologie).
    // 1. `status` croissant suit l'ordre de déclaration de l'enum Postgres `CampaignStatus` (DRAFT,
    //    SCHEDULED, SENDING, TREAT_SENDING, SENT) : campagnes actionnables d'abord. Réordonner l'enum
    //    dans `schema.prisma` change donc cet affichage.
    // 2. Groupes de statut homogènes : `sent_date` ne départage que les envoyées, `scheduled_at`
    //    (prochain envoi en tête) que les planifiées, `created_at` que les brouillons.
    // 3. `pro_campaign_id` en dernier recours : les campagnes migrées de la v1 partagent toutes le
    //    `created_at` de l'import.
    default: {
      label: 'SORT_FIELD_DEFAULT',
      value_type: 'fixed',
      default_direction: SortDirection.DESC,
      order_by: (): Record<string, unknown>[] => [
        { status: SortDirection.ASC },
        { sent_date: sortNullsLast(SortDirection.DESC) },
        { scheduled_at: sortNullsLast(SortDirection.ASC) },
        { created_at: SortDirection.DESC },
        { pro_campaign_id: SortDirection.DESC },
      ],
      api_description:
        "Ordre par défaut : campagnes actionnables d'abord — brouillons (plus récents en tête), planifiées (prochain envoi en tête), en cours d'envoi, puis envoyées (plus récentes en tête). Sens unique (sort_dir ignoré).",
    },
    sent_date: {
      label: 'SORT_FIELD_SENT_DATE',
      value_type: 'date',
      default_direction: SortDirection.DESC,
      order_by: (direction: SortDirection): Record<string, unknown>[] => [{ sent_date: sortNullsLast(direction) }],
      api_description:
        "Date d'envoi effective de la campagne. Les campagnes pas encore envoyées (brouillons, planifiées) n'en ont pas et sont toujours placées en dernier, quel que soit le sens.",
    },
    created_at: {
      label: 'SORT_FIELD_CREATED_AT',
      value_type: 'date',
      default_direction: SortDirection.DESC,
      order_by: (direction: SortDirection): Record<string, unknown>[] => [{ created_at: direction }],
      api_description: 'Date de création de la campagne.',
    },
    name: {
      label: 'SORT_FIELD_NAME',
      value_type: 'text',
      default_direction: SortDirection.ASC,
      order_by: (direction: SortDirection): Record<string, unknown>[] => [{ name: direction }],
      api_description: 'Nom de la campagne, par ordre alphabétique.',
    },
  },
  default_field: 'default',
  tiebreaker: 'pro_campaign_id',
};

export const CategoryCatalogRequestSchema = Type.Object({
  name: Type.String({
    placeholder: 'INPUT_PLACEHOLDER_PRO_CAT_NAME',
    title: 'INPUT_TITLE_PRO_CAT_NAME',
    maxLength: maxStrLength,
  }),
  slug: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_SLUG',
      title: 'INPUT_TITLE_PRO_SLUG',
      maxLength: 255,
      meta: { input_type: 'slug', linked_field: 'name' },
    }),
  ),
  description: Type.String({
    meta: { multiline: true, ai: { kind: AiFieldKind.CATEGORY_DESCRIPTION } },
    placeholder: 'INPUT_PLACEHOLDER_PRO_CAT_DESC',
    title: 'INPUT_TITLE_PRO_CAT_DESC',
  }),
  pictures: Type.Optional(
    Type.Array(CroppableImageSchema, {
      minItems: 1,
      maxItems: 1,
      meta: { picture: true, multiple: false, crop_ratio: 3 / 2 },
      placeholder: 'INPUT_PLACEHOLDER_PRO_CAT_PICTURES',
      title: 'INPUT_TITLE_PRO_CAT_PICTURES',
    }),
  ),
});
export type CategoryCatalogRequestObject = Static<typeof CategoryCatalogRequestSchema>;

export const UpdateCategoryOnlySchema = Type.Object({
  name: Type.String({
    placeholder: 'INPUT_PLACEHOLDER_PRO_CAT_NAME',
    title: 'INPUT_TITLE_PRO_CAT_NAME',
    maxLength: maxStrLength,
  }),
  slug: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_SLUG',
      title: 'INPUT_TITLE_PRO_SLUG',
      maxLength: 255,
      meta: { input_type: 'slug', linked_field: 'name' },
    }),
  ),
  description: Type.String({
    meta: {
      input_type: 'rich_text',
      allowed_tags: ['h2', 'h3', 'b', 'i', 'u', 'a', 'ul', 'ol'],
      ai: { kind: AiFieldKind.CATEGORY_DESCRIPTION },
    },
    placeholder: 'INPUT_PLACEHOLDER_PRO_CAT_DESC',
    title: 'INPUT_TITLE_PRO_CAT_DESC',
  }),
  pictures: Type.Optional(
    Type.Array(CroppableImageSchema, {
      minItems: 0,
      maxItems: 10,
      meta: { picture: true, multiple: true, crop_ratio: 3 / 2 },
      placeholder: 'INPUT_PLACEHOLDER_PRO_CAT_PICTURES',
      title: 'INPUT_TITLE_PRO_CAT_PICTURES',
    }),
  ),
});
export const CategoryCatalogResponseSchema = Type.Object({
  catalog_category_id: Type.Number(),
  name: Type.String(),
  slug: Type.String(),
  description: Type.String(),
  sort_order: Type.Number(),
  // Même forme que `PrestationResponseSchema.pictures` : des objets picture signés
  // par `transformToSchema` (`{ url: clé S3 brute, preview_url, original_url }`).
  // Indispensable pour que le widget d'upload (ImageUploadInput) affiche la
  // miniature au reload via `preview_url` ET puisse re-soumettre la clé brute
  // (`url`). Un simple `string[]` casse l'édition (carré blanc, cf. prestations).
  pictures: Type.Optional(Type.Array(CroppableImageResponseSchema)),
});
export type CategoryCatalogResponseObject = Static<typeof CategoryCatalogResponseSchema>;

export const ReorderCatalogCategoriesRequestSchema = Type.Object({
  ordered_ids: Type.Array(Type.Integer({ minimum: 1 }), { minItems: 1 }),
});
export type ReorderCatalogCategoriesRequestObject = Static<typeof ReorderCatalogCategoriesRequestSchema>;

export const TeamMemberBufferConfigItemSchema = Type.Object({
  team_member_id: Type.Integer({ minimum: 1 }),
  duration_minutes: Type.Integer({ minimum: 1 }),
  extra_buffer_minutes: Type.Integer({ minimum: 0 }),
});
export type TeamMemberBufferConfigItemObject = Static<typeof TeamMemberBufferConfigItemSchema>;

/** Une règle de réservation propre à une prestation : une option du réglage général, ou `null`. */
type PrestationBookingRuleOverrideTSchema = TOptional<TUnion<Array<TLiteral<number> | TNull>>>;

/**
 * Règle de réservation en ligne propre à une prestation : mêmes valeurs que le réglage général
 * correspondant (liste partagée de `professional_constants.ts`, jamais redéclarée ici), plus
 * stricte OU plus souple que lui. `null` = hérite du réglage général (`pro_booking_params`) :
 * `keep_null` empêche le pipe de validation de confondre ce retour à l'héritage avec « absent =
 * inchangé ». `hidden` : éditée par le bloc dédié du panneau avancé, hors rendu générique du
 * DynamicForm.
 */
const PrestationBookingRuleOverrideSchema = (
  options: BookingParamOption[],
  translationKeys: { title: string; placeholder: string },
): PrestationBookingRuleOverrideTSchema =>
  Type.Optional(
    Type.Union(
      [...options.map((option): TLiteral<number> => Type.Literal(option.value, { label: option.label })), Type.Null()],
      { ...translationKeys, meta: { hidden: true, keep_null: true } },
    ),
  );

/**
 * Délai minimum de réservation propre à une prestation, en heures (`BOOKING_MIN_HOURS_BEFORE_BOOKING`).
 * `null` = hérite de `pro_booking_params.minimum_time_before_booking`. Délai effectif :
 * `resolvePrestationMinBookingDelayHours` (availabilities.ts).
 */
export const PrestationMinimumTimeBeforeBookingSchema = PrestationBookingRuleOverrideSchema(
  BOOKING_MIN_HOURS_BEFORE_BOOKING,
  {
    title: 'INPUT_TITLE_PRO_PRESTATION_MINIMUM_TIME_BEFORE_BOOKING',
    placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_MINIMUM_TIME_BEFORE_BOOKING',
  },
);

/**
 * Horizon de réservation maximum propre à une prestation, en jours (`BOOKING_HORIZON_BOOKABLE_DAYS`).
 * `null` = hérite de `pro_booking_params.horizon_bookable`. Horizon effectif :
 * `resolvePrestationBookingHorizonDays` (availabilities.ts).
 */
export const PrestationHorizonBookableSchema = PrestationBookingRuleOverrideSchema(BOOKING_HORIZON_BOOKABLE_DAYS, {
  title: 'INPUT_TITLE_PRO_PRESTATION_HORIZON_BOOKABLE',
  placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_HORIZON_BOOKABLE',
});

/** Règles de réservation en ligne qu'une prestation peut remplacer (colonne NULL = hérite). */
export const PRESTATION_BOOKING_RULE_FIELDS = ['minimum_time_before_booking', 'horizon_bookable'] as const;
export type PrestationBookingRuleField = (typeof PRESTATION_BOOKING_RULE_FIELDS)[number];
/** Règles propres d'une prestation, telles que l'API les renvoie (`null` ou absent = hérite). */
export type PrestationBookingRuleValues = Partial<Record<PrestationBookingRuleField, number | null>>;

/** Vrai ⟺ la prestation remplace au moins une règle du réglage général (« Règles spécifiques »). */
export const hasPrestationBookingRuleOverride = (prestation: PrestationBookingRuleValues): boolean =>
  PRESTATION_BOOKING_RULE_FIELDS.some((field): boolean => prestation[field] != null);

/**
 * Corps du PUT prestation : pose `null` (= revenir au réglage général) sur les seules règles que
 * l'utilisateur a EXPLICITEMENT remises sur « Réglage général » pendant cette édition. Le
 * DynamicForm retire les `null` des données soumises : ce choix voyage donc à part. Une règle
 * absente du formulaire reste absente (= inchangée côté API, jamais remise à l'héritage par
 * défaut) et une valeur saisie l'emporte toujours.
 */
export const withExplicitBookingRuleInherits = <T extends object>(
  data: T,
  explicitInherits: ReadonlySet<PrestationBookingRuleField>,
): T & PrestationBookingRuleValues => {
  const body: T & PrestationBookingRuleValues = { ...data };
  const rules: PrestationBookingRuleValues = body;
  for (const field of PRESTATION_BOOKING_RULE_FIELDS) {
    if (explicitInherits.has(field) && rules[field] == null) rules[field] = null;
  }
  return body;
};

export const CreatePrestationRequestSchema = Type.Object({
  name: Type.String({
    meta: { ai: { kind: AiFieldKind.SERVICE_NAME } },
    placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_NAME',
    title: 'INPUT_TITLE_PRO_PRESTATION_NAME',
    minLength: 3,
    maxLength: maxStrLength,
  }),
  type: PrestationTypeSchema({
    placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_TYPE',
    title: 'INPUT_TITLE_PRO_PRESTATION_TYPE',
    meta: {
      multiple: false,
      disable_search: true,
    },
  }),
  slug: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_SLUG',
      title: 'INPUT_TITLE_PRO_SLUG',
      maxLength: 255,
      meta: { input_type: 'slug', linked_field: 'name' },
    }),
  ),
  catalog_category_id: Type.Optional(
    Type.Integer({
      minimum: 1,
      meta: { input_type: 'catalog_category_selector' },
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_CATEGORY',
      title: 'INPUT_TITLE_PRO_PRESTATION_CATEGORY',
    }),
  ),
  description: Type.Optional(
    Type.String({
      meta: {
        input_type: 'rich_text',
        allowed_tags: ['h2', 'h3', 'b', 'i', 'u', 'a', 'ul', 'ol'],
        ai: { kind: AiFieldKind.SERVICE_DESCRIPTION },
      },
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_DESC',
      title: 'INPUT_TITLE_PRO_PRESTATION_DESC',
    }),
  ),
  pictures: Type.Optional(
    Type.Array(CroppableImageSchema, {
      minItems: 0,
      maxItems: 10,
      meta: { picture: true, multiple: true, crop_ratio: 3 / 2 },
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_PICTURES',
      title: 'INPUT_TITLE_PRO_PRESTATION_PICTURES',
    }),
  ),
  videos: Type.Optional(
    Type.Array(PrestationVideoSchema, {
      minItems: 0,
      maxItems: PRESTATION_MAX_VIDEOS,
      meta: { video: true, multiple: true },
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_VIDEOS',
      title: 'INPUT_TITLE_PRO_PRESTATION_VIDEOS',
    }),
  ),
  prices: Type.Optional(
    Type.Array(PriceOptionInputSchema, {
      title: 'INPUT_TITLE_PRO_PRESTATION_PRICE',
    }),
  ),
  supplements: Type.Optional(
    Type.Array(
      Type.Integer({
        minimum: 1,
      }),
      {
        meta: { input_type: 'supplement_selector' },
        placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_SUPPLEMENT',
        title: 'INPUT_TITLE_PRO_PRESTATION_SUPPLEMENT',
        minimum: 1,
        maximum: maxSupplementSelectable,
      },
    ),
  ),
  pro_resource_ids: Type.Optional(
    Type.Array(Type.Integer({ minimum: 1 }), {
      meta: {
        input_type: 'pro_resource_selector',
        group: 'advanced',
        // Pour une course, le champ générique « Matériels / salles » disparaît du panneau
        // avancé : les véhicules s'éditent via le bloc dédié RideVehiclesBlock, affiché en
        // clair (hors panneau) et branché sur ce même champ.
        hidden_when: { field: 'type', value: PrestationType.RIDE },
      },
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_RESOURCES',
      title: 'INPUT_TITLE_PRO_PRESTATION_RESOURCES',
    }),
  ),
  color: Type.String({
    title: 'INPUT_TITLE_PRO_PRESTATION_COLOR',
    pattern: hexColorPattern,
    meta: {
      input_type: 'color_picker',
      options: COLOR_OPTIONS,
    },
  }),
  payment_type: Type.Optional(
    PrestationPaymentTypeSchema({
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_PAYMENT_TYPE',
      title: 'INPUT_TITLE_PRO_PRESTATION_PAYMENT_TYPE',
      meta: {
        multiple: false,
        disable_search: true,
        // Masqué tant qu'aucun prix n'est renseigné (toutes les lignes en « Aucun prix » ou
        // aucune ligne) : choisir un type de paiement sans prix n'a pas de sens. Condition
        // évaluée en live par le DynamicForm (cf. FORM_VISIBILITY_PREDICATES).
        visible_when: { field: 'prices', predicate: FormVisibilityPredicate.HAS_PRICED_LINE },
      },
    }),
  ),
  // Règle de montant — acompte (DEPOSIT) ou frais d'annulation tardive (autres types payants) : UN
  // bloc composite (renderer `payment_amount_rule`) porté par ce champ, bascule « % du prix /
  // Montant fixe » + saisie adaptée. Il écrit lui-même `percentage` / `fixed_amount` (meta.hidden
  // ci-dessous). Ancre du panneau « avancé » du formulaire (cf. page catalogue). Visible seulement
  // avec un prix ET un type de paiement choisi, jamais sans encaissement (FREE).
  payment_amount_mode: Type.Optional(
    PrestationPaymentAmountModeSchema({
      title: 'INPUT_TITLE_PRO_PRESTATION_PAYMENT_RULE',
      meta: {
        input_type: 'payment_amount_rule',
        title_by: PRESTATION_PAYMENT_RULE_TITLE_BY,
        visible_when: [
          { field: 'prices', predicate: FormVisibilityPredicate.HAS_PRICED_LINE },
          { field: 'payment_type', predicate: FormVisibilityPredicate.IS_DEFINED },
        ],
        hidden_when: PRESTATION_PAYMENT_FREE_HIDDEN_WHEN,
      },
    }),
  ),
  // Pourcentage du prix (0-100) — saisi via le bloc `payment_amount_mode`, jamais rendu seul.
  percentage: Type.Optional(Type.Integer({ minimum: 0, maximum: 100, meta: { hidden: true } })),
  // Montant fixe en unités mineures, PAR unité facturée (personne × quantité), plafonné au prix de
  // la ligne à la réservation — saisi via le bloc `payment_amount_mode`. Requis (> 0) en mode
  // montant fixe : PRESTATION_FIXED_AMOUNT_REQUIRED (service).
  fixed_amount: Type.Optional(Type.Integer({ minimum: 0, meta: { hidden: true } })),
  clients_per_booking_max: Type.Optional(
    Type.Integer({
      minimum: 1,
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_CLIENTS_PER_BOOKING_MAX',
      title: 'INPUT_TITLE_PRO_PRESTATION_CLIENTS_PER_BOOKING_MAX',
      meta: { hidden: true },
    }),
  ),
  is_party_size_open: Type.Optional(
    Type.Boolean({
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_IS_PARTY_SIZE_OPEN',
      title: 'INPUT_TITLE_PRO_PRESTATION_IS_PARTY_SIZE_OPEN',
      meta: { hidden: true },
    }),
  ),
  required_team_members_count: Type.Optional(
    Type.Integer({
      minimum: 0,
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_REQUIRED_TEAM_MEMBERS_COUNT',
      title: 'INPUT_TITLE_PRO_PRESTATION_REQUIRED_TEAM_MEMBERS_COUNT',
      meta: { hidden: true },
    }),
  ),
  is_session: Type.Optional(
    Type.Boolean({
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_IS_SESSION',
      title: 'INPUT_TITLE_PRO_PRESTATION_IS_SESSION',
      meta: { hidden: true },
    }),
  ),
  max_session_capacity: Type.Optional(
    Type.Integer({
      minimum: 1,
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_MAX_SESSION_CAPACITY',
      title: 'INPUT_TITLE_PRO_PRESTATION_MAX_SESSION_CAPACITY',
      meta: { hidden: true },
    }),
  ),
  minimum_time_before_booking: PrestationMinimumTimeBeforeBookingSchema,
  horizon_bookable: PrestationHorizonBookableSchema,
  // ── Vitrine « Course » dans le tunnel classique (type RIDE uniquement, section dédiée du
  // formulaire — hors DynamicForm). La carte bascule vers le flux course avec le trajet par
  // défaut prérempli ; une adresse « locked » n'est pas modifiable par le client.
  ride_show_in_classic_tunnel: Type.Optional(Type.Boolean({ meta: { hidden: true } })),
  ride_default_pickup_address: Type.Optional(AddressSchema),
  ride_default_dropoff_address: Type.Optional(AddressSchema),
  ride_pickup_address_locked: Type.Optional(Type.Boolean({ meta: { hidden: true } })),
  ride_dropoff_address_locked: Type.Optional(Type.Boolean({ meta: { hidden: true } })),
});
export type CreatePrestationRequestObject = Static<typeof CreatePrestationRequestSchema>;

export const UpdatePrestationRequestSchema = Type.Object({
  name: Type.String({
    meta: { ai: { kind: AiFieldKind.SERVICE_NAME } },
    placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_NAME',
    title: 'INPUT_TITLE_PRO_PRESTATION_NAME',
    minLength: 3,
    maxLength: maxStrLength,
  }),
  type: PrestationTypeSchema({
    placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_TYPE',
    title: 'INPUT_TITLE_PRO_PRESTATION_TYPE',
    meta: {
      multiple: false,
      disable_search: true,
    },
  }),
  slug: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_SLUG',
      title: 'INPUT_TITLE_PRO_SLUG',
      maxLength: 255,
      meta: { input_type: 'slug', linked_field: 'name' },
    }),
  ),
  catalog_category_id: Type.Optional(
    Type.Integer({
      minimum: 1,
      meta: { input_type: 'catalog_category_selector' },
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_CATEGORY',
      title: 'INPUT_TITLE_PRO_PRESTATION_CATEGORY',
    }),
  ),
  description: Type.Optional(
    Type.String({
      meta: {
        input_type: 'rich_text',
        allowed_tags: ['h2', 'h3', 'b', 'i', 'u', 'a', 'ul', 'ol'],
        ai: { kind: AiFieldKind.SERVICE_DESCRIPTION },
      },
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_DESC',
      title: 'INPUT_TITLE_PRO_PRESTATION_DESC',
      // maxLength: 2000,
    }),
  ),
  pictures: Type.Optional(
    Type.Array(CroppableImageSchema, {
      minItems: 0,
      maxItems: 10,
      meta: { picture: true, multiple: true, crop_ratio: 3 / 2 },
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_PICTURES',
      title: 'INPUT_TITLE_PRO_PRESTATION_PICTURES',
    }),
  ),
  videos: Type.Optional(
    Type.Array(PrestationVideoSchema, {
      minItems: 0,
      maxItems: PRESTATION_MAX_VIDEOS,
      meta: { video: true, multiple: true },
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_VIDEOS',
      title: 'INPUT_TITLE_PRO_PRESTATION_VIDEOS',
    }),
  ),
  prices: Type.Optional(
    Type.Array(PriceOptionInputSchema, {
      title: 'INPUT_TITLE_PRO_PRESTATION_PRICE',
    }),
  ),
  supplements: Type.Optional(
    Type.Array(
      Type.Integer({
        minimum: 1,
      }),
      {
        meta: { input_type: 'supplement_selector' },
        placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_SUPPLEMENT',
        title: 'INPUT_TITLE_PRO_PRESTATION_SUPPLEMENT',
        minimum: 1,
        maximum: maxSupplementSelectable,
      },
    ),
  ),
  pro_resource_ids: Type.Optional(
    Type.Array(Type.Integer({ minimum: 1 }), {
      meta: {
        input_type: 'pro_resource_selector',
        group: 'advanced',
        // Pour une course, le champ générique « Matériels / salles » disparaît du panneau
        // avancé : les véhicules s'éditent via le bloc dédié RideVehiclesBlock, affiché en
        // clair (hors panneau) et branché sur ce même champ.
        hidden_when: { field: 'type', value: PrestationType.RIDE },
      },
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_RESOURCES',
      title: 'INPUT_TITLE_PRO_PRESTATION_RESOURCES',
    }),
  ),
  color: Type.String({
    title: 'INPUT_TITLE_PRO_PRESTATION_COLOR',
    pattern: hexColorPattern,
    meta: {
      input_type: 'color_picker',
      options: COLOR_OPTIONS,
    },
  }),
  payment_type: Type.Optional(
    PrestationPaymentTypeSchema({
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_PAYMENT_TYPE',
      title: 'INPUT_TITLE_PRO_PRESTATION_PAYMENT_TYPE',
      meta: {
        multiple: false,
        disable_search: true,
        // Masqué tant qu'aucun prix n'est renseigné (toutes les lignes en « Aucun prix » ou
        // aucune ligne) : choisir un type de paiement sans prix n'a pas de sens. Condition
        // évaluée en live par le DynamicForm (cf. FORM_VISIBILITY_PREDICATES).
        visible_when: { field: 'prices', predicate: FormVisibilityPredicate.HAS_PRICED_LINE },
      },
    }),
  ),
  // Règle de montant — acompte (DEPOSIT) ou frais d'annulation tardive (autres types payants) : UN
  // bloc composite (renderer `payment_amount_rule`) porté par ce champ, bascule « % du prix /
  // Montant fixe » + saisie adaptée. Il écrit lui-même `percentage` / `fixed_amount` (meta.hidden
  // ci-dessous). Ancre du panneau « avancé » du formulaire (cf. page catalogue). Visible seulement
  // avec un prix ET un type de paiement choisi, jamais sans encaissement (FREE).
  payment_amount_mode: Type.Optional(
    PrestationPaymentAmountModeSchema({
      title: 'INPUT_TITLE_PRO_PRESTATION_PAYMENT_RULE',
      meta: {
        input_type: 'payment_amount_rule',
        title_by: PRESTATION_PAYMENT_RULE_TITLE_BY,
        visible_when: [
          { field: 'prices', predicate: FormVisibilityPredicate.HAS_PRICED_LINE },
          { field: 'payment_type', predicate: FormVisibilityPredicate.IS_DEFINED },
        ],
        hidden_when: PRESTATION_PAYMENT_FREE_HIDDEN_WHEN,
      },
    }),
  ),
  // Pourcentage du prix (0-100) — saisi via le bloc `payment_amount_mode`, jamais rendu seul.
  percentage: Type.Optional(Type.Integer({ minimum: 0, maximum: 100, meta: { hidden: true } })),
  // Montant fixe en unités mineures, PAR unité facturée (personne × quantité), plafonné au prix de
  // la ligne à la réservation — saisi via le bloc `payment_amount_mode`. Requis (> 0) en mode
  // montant fixe : PRESTATION_FIXED_AMOUNT_REQUIRED (service).
  fixed_amount: Type.Optional(Type.Integer({ minimum: 0, meta: { hidden: true } })),
  clients_per_booking_max: Type.Optional(
    Type.Integer({
      minimum: 1,
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_CLIENTS_PER_BOOKING_MAX',
      title: 'INPUT_TITLE_PRO_PRESTATION_CLIENTS_PER_BOOKING_MAX',
      meta: { hidden: true },
    }),
  ),
  is_party_size_open: Type.Optional(
    Type.Boolean({
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_IS_PARTY_SIZE_OPEN',
      title: 'INPUT_TITLE_PRO_PRESTATION_IS_PARTY_SIZE_OPEN',
      meta: { hidden: true },
    }),
  ),
  required_team_members_count: Type.Optional(
    Type.Integer({
      minimum: 0,
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_REQUIRED_TEAM_MEMBERS_COUNT',
      title: 'INPUT_TITLE_PRO_PRESTATION_REQUIRED_TEAM_MEMBERS_COUNT',
      meta: { hidden: true },
    }),
  ),
  is_session: Type.Optional(
    Type.Boolean({
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_IS_SESSION',
      title: 'INPUT_TITLE_PRO_PRESTATION_IS_SESSION',
      meta: { hidden: true },
    }),
  ),
  max_session_capacity: Type.Optional(
    Type.Integer({
      minimum: 1,
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_MAX_SESSION_CAPACITY',
      title: 'INPUT_TITLE_PRO_PRESTATION_MAX_SESSION_CAPACITY',
      meta: { hidden: true },
    }),
  ),
  minimum_time_before_booking: PrestationMinimumTimeBeforeBookingSchema,
  horizon_bookable: PrestationHorizonBookableSchema,
  member_buffer_configs: Type.Optional(
    Type.Array(TeamMemberBufferConfigItemSchema, {
      meta: { hidden: true },
    }),
  ),
  // ── Vitrine « Course » (type RIDE uniquement) — cf. CreatePrestationRequestSchema.
  ride_show_in_classic_tunnel: Type.Optional(Type.Boolean({ meta: { hidden: true } })),
  ride_default_pickup_address: Type.Optional(AddressSchema),
  ride_default_dropoff_address: Type.Optional(AddressSchema),
  ride_pickup_address_locked: Type.Optional(Type.Boolean({ meta: { hidden: true } })),
  ride_dropoff_address_locked: Type.Optional(Type.Boolean({ meta: { hidden: true } })),
});
export type UpdatePrestationRequestObject = Static<typeof UpdatePrestationRequestSchema>;

export const PresignedBucketRequestSchema = Type.Object({
  type: Type.String(),
  filename: Type.String(),
});
export const PresignedBucketResponseSchema = Type.Object({
  put_url: Type.String(),
  get_url: Type.String(),
});
export type PresignedBucketResponseObject = Static<typeof PresignedBucketResponseSchema>;

/**
 * Spec de taille Thumbor acceptée par `/public-url` : `<largeur>x<hauteur>`
 * éventuellement suivi d'un filtre (cf. `withThumborQuality` → `800x0/filters:quality(76)`).
 * Le pattern borne ce que le client peut faire signer : la clé S3 est déjà libre,
 * mais la partie « traitement » du chemin Thumbor reste, elle, contrainte.
 */
export const THUMBOR_SIZE_SPEC_PATTERN = '^[0-9]+x[0-9]+(/filters:[a-zA-Z_]+\\([^/]*\\))?$';

export const PublicUrlRequestSchema = Type.Object({
  key: Type.String(),
  /**
   * Recadrage à appliquer aux URLs de `sized_urls` (préfixe Thumbor
   * `x1xy1:x2xy2/`). `url` reste TOUJOURS l'original non recadré : le builder
   * s'en sert pour re-cropper à partir de l'image entière.
   */
  crop: Type.Optional(CropDataSchema),
  /**
   * Tailles Thumbor à signer, telles que déclarées par `PictureSchema`
   * (`meta[THUMBOR_META_KEY]`). Permet à l'aperçu du builder d'afficher le
   * recadrage immédiatement, sans attendre la signature au read côté backend.
   */
  sizes: Type.Optional(
    Type.Array(Type.String({ pattern: THUMBOR_SIZE_SPEC_PATTERN }), { maxItems: 8, uniqueItems: true }),
  ),
});
export const PublicUrlResponseSchema = Type.Object({
  /** Original non recadré (`0x0`). */
  url: Type.String(),
  /**
   * URL signée (recadrée si `crop` fourni) pour chaque spec de `sizes`, dans le
   * même ordre. Une LISTE et pas un `Type.Record` : Orval génère
   * `{ [key: string]: unknown }` pour un record, ce qui perdrait le typage.
   */
  sized_urls: Type.Optional(Type.Array(Type.Object({ size: Type.String(), url: Type.String() }))),
});
export type PublicUrlResponseObject = Static<typeof PublicUrlResponseSchema>;

export const CreatePrestationSupplementRequestSchema = Type.Object({
  name: Type.String({
    meta: { ai: { kind: AiFieldKind.SERVICE_NAME } },
    placeholder: 'INPUT_PLACEHOLDER_PRO_SUPPLEMENT_NAME',
    title: 'INPUT_TITLE_PRO_SUPPLEMENT_NAME',
    minLength: 1,
    maxLength: maxStrLength,
  }),
  description: Type.String({
    meta: {
      input_type: 'rich_text',
      allowed_tags: ['h2', 'h3', 'b', 'i', 'u', 'a', 'ul', 'ol'],
      ai: { kind: AiFieldKind.SERVICE_DESCRIPTION },
    },
    placeholder: 'INPUT_PLACEHOLDER_PRO_SUPPLEMENT_DESC',
    title: 'INPUT_TITLE_PRO_SUPPLEMENT_DESC',
  }),
  price: Type.Number({
    format: 'int32',
    minimum: 0,
    placeholder: 'INPUT_PLACEHOLDER_PRO_SUPPLEMENT_PRICE',
    title: 'INPUT_TITLE_PRO_SUPPLEMENT_PRICE',
    meta: { is_price: true },
  }),
  duration_minutes: Type.Optional(
    Type.Integer({
      minimum: 0,
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_DURATION',
      title: 'INPUT_TITLE_PRO_PRESTATION_DURATION',
      meta: { is_duration_minutes: true },
    }),
  ),
  pro_resource_ids: Type.Optional(
    Type.Array(Type.Integer({ minimum: 1 }), {
      maxItems: 1,
      meta: {
        input_type: 'pro_resource_selector',
        filter_type: 'REUSABLE',
        filter_booking_mode: 'CONSTRAINT',
      },
      placeholder: 'INPUT_PLACEHOLDER_PRO_SUPPLEMENT_RESOURCE',
      title: 'INPUT_TITLE_PRO_SUPPLEMENT_RESOURCE',
    }),
  ),
});
export type PrestationSupplementRequestObject = Static<typeof CreatePrestationSupplementRequestSchema>;

export const PrestationSupplementResponseSchema = Type.Object({
  supplement_id: Type.Number(),
  name: Type.String(),
  price: Type.Optional(Type.Number()),
  price_string: Type.Optional(
    Type.String({
      meta: { is_price: true },
    }),
  ),
  description: Type.String(),
  duration_minutes: Type.Optional(Type.Number()),
  pro_resource_ids: Type.Optional(Type.Array(Type.Number())),
  /** Se compte au choix sur une ligne réservée (ni durée ni salle, cf. `isSupplementQuantitySelectable`). */
  quantity_selectable: Type.Optional(Type.Boolean()),
});
export type PrestationSupplementResponseObject = Static<typeof PrestationSupplementResponseSchema>;

export const PrestationSupplementListResponseSchema = Type.Object({
  total: Type.Number(),
  items: Type.Array(PrestationSupplementResponseSchema),
});
export type PrestationSupplementListResponseObject = Static<typeof PrestationSupplementListResponseSchema>;

/** Tri de la liste des suppléments (`/catalog/supplement`). */
export const SUPPLEMENT_SORTS: SortConfig = {
  fields: {
    // Seule définition de l'ordre personnalisé des suppléments : le backend en dérive
    // SUPPLEMENT_DISPLAY_ORDER (tunnel, site, carte cadeau, notifications…).
    manual: {
      label: 'SORT_FIELD_MANUAL',
      value_type: 'fixed',
      default_direction: SortDirection.ASC,
      order_by: (): Record<string, unknown>[] => [
        { sort_order: SortDirection.ASC },
        { supplement_id: SortDirection.ASC },
      ],
      api_description:
        "Ordre personnalisé réglé par le pro par glisser-déposer (celui du tunnel de réservation et du site). Sens unique (sort_dir ignoré). C'est l'ordre par défaut.",
    },
    name: {
      label: 'SORT_FIELD_NAME',
      value_type: 'text',
      default_direction: SortDirection.ASC,
      order_by: (direction: SortDirection): Record<string, unknown>[] => [{ name: direction }],
      api_description: 'Nom du supplément, par ordre alphabétique.',
    },
    price: {
      label: 'SORT_FIELD_PRICE',
      value_type: 'number',
      default_direction: SortDirection.ASC,
      order_by: (direction: SortDirection): Record<string, unknown>[] => [{ price: sortNullsLast(direction) }],
      api_description:
        'Prix du supplément, en centimes. Les suppléments sans prix sont toujours placés en dernier, quel que soit le sens.',
    },
  },
  default_field: 'manual',
  tiebreaker: 'supplement_id',
};

// Ordre d'affichage des suppléments (partout : back-office, tunnel, site, carte cadeau).
// `ordered_ids` peut n'être qu'une partie des suppléments du pro (liste paginée) : les ids
// envoyés sont permutés entre eux, les autres gardent leur place.
export const ReorderSupplementsRequestSchema = Type.Object({
  ordered_ids: Type.Array(Type.Integer({ minimum: 1 }), { minItems: 1 }),
});
export type ReorderSupplementsRequestObject = Static<typeof ReorderSupplementsRequestSchema>;

export const PrestationPriceResponseSchema = PriceOptionResponseSchema;
export const PrestationResponseSchema = Type.Object({
  prestation_id: Type.Number(),
  name: Type.String(),
  type: Type.Optional(Type.String()),
  slug: Type.String(),
  sort_order: Type.Number(),
  catalog_category_id: Type.Integer({
    minimum: 1,
    meta: { input_type: 'catalog_category_selector' },
    placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_CATEGORY',
    title: 'INPUT_TITLE_PRO_PRESTATION_CATEGORY',
  }),
  description: Type.String(),
  pictures: Type.Optional(
    Type.Array(
      Type.Object(
        {
          small_url: Type.String({
            format: 'uri',
            meta: { [THUMBOR_META_KEY]: '100x100' },
          }),
          medium_url: Type.String({
            format: 'uri',
            meta: { [THUMBOR_META_KEY]: '200x200' },
          }),
          large_url: Type.String({
            format: 'uri',
            meta: { [THUMBOR_META_KEY]: '400x400' },
          }),
        },
        {
          meta: { picture: true },
        },
      ),
    ),
  ),
  videos: Type.Optional(Type.Array(PrestationVideoSchema)),
  prices: Type.Optional(Type.Array(PrestationPriceResponseSchema)),
  color: Type.String(),
  payment_type: Type.Optional(Type.String()),
  // Règle de montant (acompte / frais d'annulation tardive) : les DEUX réglages sont renvoyés bruts,
  // `payment_amount_mode` dit lequel s'applique — le formulaire d'édition les relit tels quels.
  payment_amount_mode: Type.Optional(Type.String()),
  percentage: Type.Optional(Type.Number()),
  fixed_amount: Type.Optional(Type.Number()),
  supplements: Type.Optional(Type.Array(PrestationSupplementResponseSchema)),
  pro_resource_ids: Type.Optional(Type.Array(Type.Number())),
  member_buffer_configs: Type.Array(TeamMemberBufferConfigItemSchema),
  clients_per_booking_max: Type.Number(),
  is_party_size_open: Type.Boolean(),
  required_team_members_count: Type.Number(),
  is_session: Type.Boolean(),
  max_session_capacity: Type.Optional(Type.Number()),
  // Règles de réservation propres de la prestation, renvoyées brutes : `null` = hérite du réglage
  // général (pro_booking_params). Valeur effective : resolvePrestationMinBookingDelayHours /
  // resolvePrestationBookingHorizonDays (availabilities.ts).
  minimum_time_before_booking: Type.Optional(Type.Union([Type.Number(), Type.Null()])),
  horizon_bookable: Type.Optional(Type.Union([Type.Number(), Type.Null()])),
  // ── Vitrine « Course » (type RIDE uniquement) — cf. CreatePrestationRequestSchema.
  ride_show_in_classic_tunnel: Type.Optional(Type.Boolean()),
  ride_default_pickup_address: Type.Optional(AddressSchema),
  ride_default_dropoff_address: Type.Optional(AddressSchema),
  ride_pickup_address_locked: Type.Optional(Type.Boolean()),
  ride_dropoff_address_locked: Type.Optional(Type.Boolean()),
});
export type PrestationResponseObject = Static<typeof PrestationResponseSchema>;

/**
 * Prestation du catalogue pro (liste, fiche, réponses du create et du PUT) : la prestation, plus sa
 * réservabilité en ligne (P3, § 7.8), calculée avec les règles du tunnel sans sonde de créneaux. Absente pour
 * une course (flux dédié) ou si l'évaluation a échoué. Wrapper dédié, pour la même raison que ci-dessous.
 */
export const ProPrestationItemResponseSchema = Type.Object({
  ...PrestationResponseSchema.properties,
  bookability: Type.Optional(Type.Union(PRO_PRESTATION_BOOKABILITY_STATUSES.map((status) => Type.Literal(status)))),
});
export type ProPrestationItemResponseObject = Static<typeof ProPrestationItemResponseSchema>;

/**
 * Réponse du PUT prestation SEULEMENT : la prestation, plus l'impact réservabilité quand la
 * modification dégrade des plannings (M3-M5). Wrapper dédié — poser `bookability_impact` sur
 * `PrestationResponseSchema` le ferait remonter dans TOUS les schémas qui l'imbriquent (GET
 * liste, suppléments…), et Orval génèrerait un type d'impact pour chacun.
 */
export const PrestationWithBookabilityImpactResponseSchema = Type.Object({
  ...ProPrestationItemResponseSchema.properties,
  bookability_impact: Type.Optional(AvailabilityBookabilityImpactSchema),
});
export type PrestationWithBookabilityImpactResponseObject = Static<
  typeof PrestationWithBookabilityImpactResponseSchema
>;

export const ReorderPrestationsRequestSchema = Type.Object({
  ordered_ids: Type.Array(Type.Integer({ minimum: 1 }), { minItems: 1 }),
  moved_prestation_id: Type.Optional(Type.Integer({ minimum: 1 })),
  target_catalog_category_id: Type.Optional(Type.Integer({ minimum: 1 })),
});
export type ReorderPrestationsRequestObject = Static<typeof ReorderPrestationsRequestSchema>;

export const ProPrestationListResponseSchema = Type.Object({
  has_elements: Type.Boolean(),
  total: Type.Number(),
  items: Type.Array(ProPrestationItemResponseSchema),
});
export type ProPrestationListResponseObject = Static<typeof ProPrestationListResponseSchema>;

export const EmailCostEstimationRequestSchema = Type.Object({
  pro_tags: Type.Array(Type.Number(), {
    minItems: 0,
    maxItems: maxTagSelectable,
    meta: { input_type: 'pro_tag_selector' },
    placeholder: 'INPUT_PLACEHOLDER_CHOOSE',
    title: 'INPUT_TITLE_PRO_CUST_TAGS',
  }),
  // Code promo personnel joint à la campagne : l'envoi ne recontacte pas les clients dont le code est
  // déjà utilisé, l'estimation non plus.
  pro_promo_code_id: Type.Optional(Type.Integer({ minimum: 1 })),
});
export type EmailCostEstimationRequestObject = Static<typeof EmailCostEstimationRequestSchema>;

export const EmailCostEstimationResponseSchema = Type.Object({
  total_cost: Type.Number(),
  nb_recipient: Type.Number(),
});
export type EmailCostEstimationResponseObject = Static<typeof EmailCostEstimationResponseSchema>;

export const LIMIT_SMS_URL = 5;

export const SmsCostEstimationRequestSchema = Type.Object({
  pro_tags: Type.Array(Type.Number(), {
    minItems: 0,
    maxItems: maxTagSelectable,
    meta: { input_type: 'pro_tag_selector' },
    placeholder: 'INPUT_PLACEHOLDER_CHOOSE',
    title: 'INPUT_TITLE_PRO_CUST_TAGS',
  }),
  content: Type.String({
    meta: { multiline: true },
    placeholder: 'INPUT_PLACEHOLDER_PRO_SMS_CAMPAIGN_CONTENT',
    title: 'INPUT_TITLE_PRO_SMS_CAMPAIGN_CONTENT',
    maxLength: maxSmsTextSize,
  }),
  sender: Type.Optional(Type.String()),
  track_links: Type.Optional(Type.Boolean({ default: true })),
  // Code promo personnel joint à la campagne : l'envoi ne recontacte pas les clients dont le code est
  // déjà utilisé, l'estimation non plus.
  pro_promo_code_id: Type.Optional(Type.Integer({ minimum: 1 })),
});
export type SmsCostEstimationRequestObject = Static<typeof SmsCostEstimationRequestSchema>;

export const SmsCostEstimationResponseSchema = Type.Object({
  total_cost: Type.Number(),
  nb_recipient: Type.Number(),
  encoding: Type.String(),
  char_count: Type.Number(),
  sms_count: Type.Number(),
  max_chars: Type.Number(),
  chars_per_segment: Type.Number(),
  details: Type.Optional(
    Type.Array(
      Type.Object({
        iso_country_code: Type.String(),
        recipients_count: Type.Number(),
        sms_count: Type.Number(),
        cost: Type.Number(),
      }),
    ),
  ),
});
export type SmsCostEstimationResponseObject = Static<typeof SmsCostEstimationResponseSchema>;

export const ProInfosResponseSchema = Type.Object({
  ...UpsertProProfileRequestSchema.properties,
  slug: Type.String(),
  /** Identifiant immuable pour les widgets externes (booking, gift-card, ...). Cf. {@link public_widget_id} dans Prisma. */
  public_widget_id: Type.String(),
  sms_sender_name: Type.String(),
  sms_marketing_sender_name: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  email_sender_name: Type.Optional(Type.String()),
  email_contact: Type.Optional(Type.String({ format: 'email' })),
  pro_subscription: Type.Optional(ProInfosSubResponseSchema),
  pro_subscription_segment: Type.Optional(Type.Array(ProInfosSubSegmentResponseSchema)),
  pro_quota: Type.Optional(
    Type.Object({
      quota_email_left: Type.Number(),
      quota_sms_left: Type.Number(),
      credit_email_left: Type.Number(),
      credit_sms_left: Type.Number(),
      reset_cycle_anchor: Type.String({ format: 'date-time' }),
      monthly_quota_email: Type.Number(),
      monthly_quota_sms: Type.Number(),
    }),
  ),
  list_sms_accepted: Type.Array(SmsPricesAccepted),
  pro_tag: Type.Array(ProTagResponseSchema),
  pro_news_tags: Type.Array(
    Type.Object({
      pro_news_tag_id: Type.Number(),
      title: Type.String(),
      slug: Type.String(),
      color: Type.Optional(Type.String()),
    }),
  ),
  catalog_category: Type.Array(CategoryCatalogResponseSchema),
  supplements: Type.Array(PrestationSupplementResponseSchema),
  team_members: Type.Array(ProTeamMemberArrayContentResponseSchema),
  subscription_usage: Type.Optional(SubscriptionUsageSchema),
  pro_place: Type.Array(ProPlacesResponseSchema),
  // Fiches Google synchronisées du pro, reliées à un lieu ou non — `pro_place` ne rend
  // visibles que les premières, or une fiche non reliée reste publiable (articles GMB).
  google_locations: Type.Optional(Type.Array(SyncedGoogleLocationsResponseSchema)),
  // Un compte Google connecté ne garantit PAS une fiche publiable : le compte peut ne
  // gérer aucun établissement. Sans ce booléen, l'interface confondait « aucun compte
  // connecté » avec « compte connecté mais inexploitable » et invitait le pro à
  // reconnecter un compte déjà branché, sans issue possible.
  has_google_account: Type.Optional(Type.Boolean()),
  currency: Type.String({ maxLength: 3 }),
  country: Type.String({ maxLength: 10 }),
  locale: Type.String(),
  timezone: Type.String(),
  address: Type.Optional(AddressSchema),
  external_urls: Type.Optional(Type.Array(ExternalUrlSchema)),
  logo: Type.Optional(Type.Array(CroppableImageResponseSchema)),
  pictures: Type.Optional(Type.Array(CroppableImageResponseSchema)),
  /** Onboarding v4, lot 7 : le logo est un monogramme généré (initiales), remplaçable à tout moment. */
  logo_provisional: Type.Optional(Type.Boolean()),
  pro_booking_params: Type.Optional(UpsertProBookingParametersRequestSchema),
  // Libellé ville capté au signup (texte brut, non géocodé). Sert au wizard
  // d'onboarding pour amorcer l'autocomplete ville + la détection business.
  // Ne pas confondre avec `address.city` (posé après géocodage au wizard).
  signup_city: Type.Optional(Type.String()),
  // `false` quand l'account a été créé via signup self-serve sans password,
  // ou via invitation sans password set. Pilote l'affichage du nudge
  // « définir mon mot de passe » côté dashboard.
  account_has_password: Type.Boolean(),
  /** Prénom du titulaire du compte connecté (salutations : « Votre démo est prête, Inès »). */
  account_firstname: Type.Optional(Type.String()),
  // `ADDITIONAL_COMPANY` : entreprise créée depuis un compte existant, payante d'emblée
  // (écran « Activez votre nouvelle entreprise », redirection vers l'onboarding après paiement).
  origin: Type.Optional(Type.Enum(ProfessionalOrigin)),
  // Set quand le pro a terminé son flow d'import v1 → v2 = fin de la migration BETA
  // (cf. docs/to_implement/migration-beta-to-final-cutover-plan.md §2). Le frontend
  // masque l'entrée de menu + redirect /migration-v1 si cette valeur est non-null.
  v1_beta_migrated_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'v1_beta_migrated_at' } }),
  ),
});
export type ProInfosResponseObject = Static<typeof ProInfosResponseSchema>;

// Réponse du POST /app/pro-establishment/booking-parameters : les infos pro rafraîchies,
// plus l'impact réservabilité (M9-M11 : préavis, horizon, pas de grille) quand le nouveau
// réglage dégrade des plannings. Wrapper dédié pour ne pas polluer ProInfosResponseSchema.
export const UpsertProBookingParamsResponseSchema = Type.Object({
  pro_infos: ProInfosResponseSchema,
  bookability_impact: Type.Optional(AvailabilityBookabilityImpactSchema),
});
export type UpsertProBookingParamsResponseObject = Static<typeof UpsertProBookingParamsResponseSchema>;

export const AddPageWebsiteBuilderSchema = Type.Object({
  // Ordre d'affichage volontaire (du concret au détail) : type → nom → lien → emplacement.
  type: Type.String({
    placeholder: 'WEBSITE_BUILDER_PAGE_TYPE',
    title: 'WEBSITE_BUILDER_PAGE_TYPE',
    meta: {
      suggestions: Object.values(PageTypeEnum),
      disable_search: true,
      multiple: false,
    },
  }),
  title: Type.Optional(
    Type.String({
      placeholder: 'WEBSITE_BUILDER_PAGE_TITLE',
      title: 'WEBSITE_BUILDER_PAGE_TITLE',
      maxLength: maxStrLength,
      meta: {
        auto_fill_from: 'type',
        auto_fill_map: {}, // Dynamically injected with type→defaultName mapping
      },
    }),
  ),
  // Lien externe : visible uniquement quand le type sélectionné est « external-link ».
  // Une telle entrée n'a ni slug ni contenu — juste un libellé + une URL.
  // eslint-disable-next-line typebox-snake-case/typebox-object-keys-snake-case -- camelCase mirrors the WebsitePageInstance blob field
  externalUrl: Type.Optional(
    Type.String({
      placeholder: 'WEBSITE_BUILDER_PAGE_EXTERNAL_URL_PLACEHOLDER',
      title: 'WEBSITE_BUILDER_PAGE_EXTERNAL_URL',
      maxLength: maxStrLength,
      // Force le schéma http(s):// — une URL sans schéma serait interprétée
      // comme un chemin relatif par le navigateur (convention du codebase).
      pattern: '^https?://.+',
      meta: {
        input_type: 'url',
        visible_when: { field: 'type', value: PageTypeEnum.EXTERNAL_LINK },
      },
    }),
  ),
  slug: Type.Optional(
    Type.String({
      placeholder: 'WEBSITE_BUILDER_PAGE_SLUG',
      title: 'WEBSITE_BUILDER_PAGE_SLUG',
      maxLength: maxStrLength,
      // `prefix` (chemin du lien) injecté dynamiquement dans PagesEditor.
      // Masqué pour les liens externes (pas de chemin public).
      meta: {
        input_type: 'slug',
        linked_field: 'title',
        hidden_when: { field: 'type', value: PageTypeEnum.EXTERNAL_LINK },
      },
    }),
  ),
  parent: Type.String({
    placeholder: 'WEBSITE_BUILDER_PAGE_PARENT',
    title: 'WEBSITE_BUILDER_PAGE_PARENT',
    meta: {
      suggestions: [], //Dynamically injected
      disable_search: true,
      multiple: false,
    },
  }),
});

export const PageWebsiteBuilderSchema = Type.Object({
  title: Type.String({
    placeholder: 'WEBSITE_BUILDER_PAGE_TITLE',
    title: 'WEBSITE_BUILDER_PAGE_TITLE',
    maxLength: maxStrLength,
  }),
  slug: Type.String({
    placeholder: 'WEBSITE_BUILDER_PAGE_SLUG',
    title: 'WEBSITE_BUILDER_PAGE_SLUG',
    maxLength: maxStrLength,
    meta: { input_type: 'slug', linked_field: 'title' },
  }),
});

// Édition d'une entrée de menu « lien externe » : libellé affiché + URL cible.
// Pas de slug (l'entrée n'a pas de chemin public).
export const ExternalLinkPageWebsiteBuilderSchema = Type.Object({
  title: Type.String({
    placeholder: 'WEBSITE_BUILDER_PAGE_TITLE',
    title: 'WEBSITE_BUILDER_PAGE_TITLE',
    maxLength: maxStrLength,
  }),
  // eslint-disable-next-line typebox-snake-case/typebox-object-keys-snake-case -- camelCase mirrors the WebsitePageInstance blob field
  externalUrl: Type.String({
    placeholder: 'WEBSITE_BUILDER_PAGE_EXTERNAL_URL_PLACEHOLDER',
    title: 'WEBSITE_BUILDER_PAGE_EXTERNAL_URL',
    maxLength: maxStrLength,
    // Force le schéma http(s):// — cf. AddPageWebsiteBuilderSchema.
    pattern: '^https?://.+',
    meta: { input_type: 'url' },
  }),
});

// ─── Pro Website Schemas ─────────────────────────────────────────────

export const SaveWebsiteConfigRequestSchema = Type.Object({
  config: WebsiteConfigSchema,
});

// Réglages du site sortis du builder, persistés indépendamment du cycle
// draft/publish (appliqués immédiatement, modèle SEO service-areas).
// Clés camelCase volontaires : elles reflètent le blob WebsiteConfig
// (cf. le même eslint-disable en tête de WebsiteTypes.ts).
/* eslint-disable typebox-snake-case/typebox-object-keys-snake-case -- camelCase keys mirror the WebsiteConfig blob/API schema */
export const SaveTrackingConfigRequestSchema = Type.Object({
  tracking: TrackingConfigSchema,
});
export const SaveSiteConfigRequestSchema = Type.Object({
  siteConfig: SiteConfigSchema,
});
export const SaveRedirectsConfigRequestSchema = Type.Object({
  redirects: RedirectsConfigSchema,
});
export const SaveScriptsConfigRequestSchema = Type.Object({
  scripts: ScriptsConfigSchema,
});
export const WebsiteSettingsResponseSchema = Type.Object({
  tracking: Type.Optional(TrackingConfigSchema),
  siteConfig: Type.Optional(SiteConfigSchema),
  redirects: Type.Optional(RedirectsConfigSchema),
  scripts: Type.Optional(ScriptsConfigSchema),
});
/* eslint-enable typebox-snake-case/typebox-object-keys-snake-case */
export type WebsiteSettingsResponseObject = Static<typeof WebsiteSettingsResponseSchema>;

export const SearchDomainRequestSchema = Type.Object({
  q: Type.String({ minLength: 2, maxLength: 253 }),
});
export const SelectDomainRequestSchema = Type.Object({
  domain: Type.String({ minLength: 4, maxLength: 253 }),
  redirect_url: Type.String({ minLength: 1 }),
});
export const DnsSuggestionResponseSchema = Type.Object({
  domain: Type.String(),
  available: Type.Boolean(),
  transferable: Type.Optional(Type.Boolean()),
  price: Type.Optional(Type.Number()),
  currency: Type.Optional(Type.String()),
  included: Type.Optional(Type.Boolean()),
  price_string: Type.Optional(Type.String()),
  price_ht_string: Type.Optional(Type.String()),
  /** Tarif propre à OVH, hors catalogue : non achetable en self-service (→ support). */
  premium: Type.Optional(Type.Boolean()),
});
export const TransferDomainRequestSchema = Type.Object({
  domain: Type.String({ minLength: 4, maxLength: 253 }),
  auth_code: Type.String({ minLength: 1, maxLength: 255 }),
  redirect_url: Type.String({ minLength: 1 }),
});
export const ChangePrimaryDomainRequestSchema = Type.Object({
  new_primary_domain_id: Type.Number(),
  keep_old_as_redirect: Type.Boolean(),
});
export const AddSecondaryDomainRequestSchema = Type.Object({
  domain: Type.String({ minLength: 4, maxLength: 253 }),
  auth_code: Type.Optional(Type.String({ maxLength: 255 })),
  redirect_url: Type.String({ minLength: 1 }),
});
export const DnsDomainActionResponseSchema = Type.Object({
  success: Type.Optional(Type.Boolean()),
  domain: Type.Optional(Type.String()),
  link_payment: Type.Optional(Type.String()),
  // UUID du paiement créé (présent quand un paiement est requis) — permet le paiement
  // express (carte enregistrée) côté front. cf. _parseLinkPaymentFromUuid.
  payment_uuid: Type.Optional(Type.String()),
  needs_manual_action: Type.Optional(Type.Boolean()),
  // Prélèvement SEPA déjà lancé pour ce domaine (paiement PROCESSING) : AUCUN lien de paiement
  // (ce serait un second débit) — le domaine est activé à l'encaissement, sous quelques jours ouvrés.
  sepa_debit_in_progress: Type.Optional(Type.Boolean()),
});
export type DnsDomainActionResponseObject = Static<typeof DnsDomainActionResponseSchema>;

/**
 * Statut d'activation d'un domaine.
 * ⚠️IMPACT — miroir de l'enum Prisma `WebsiteDomainActivationStatus`
 * (packages/backend/prisma-pg/schema.prisma). Toute valeur ajoutée/renommée ici
 * doit l'être aussi côté Prisma (via une migration de rename), et inversement.
 */
export enum WebsiteDomainActivationStatus {
  NONE = 'NONE',
  DNS_ORDERED = 'DNS_ORDERED',
  DNS_RESERVED = 'DNS_RESERVED',
  DNS_CONFIGURED = 'DNS_CONFIGURED',
  PROPELSIA_CONFIGURED = 'PROPELSIA_CONFIGURED',
  K8S_CONFIGURED = 'K8S_CONFIGURED',
  HTTPS_CONFIGURED = 'HTTPS_CONFIGURED',
  TRANSFER_PENDING = 'TRANSFER_PENDING',
  TRANSFER_IN_PROGRESS = 'TRANSFER_IN_PROGRESS',
  TRANSFER_MANUAL_ACTION = 'TRANSFER_MANUAL_ACTION',
  TRANSFER_FAILED = 'TRANSFER_FAILED',
  PAYMENT_PENDING = 'PAYMENT_PENDING',
  DNS_ORDER_FAILED = 'DNS_ORDER_FAILED',
}

/** Statuts considérés comme « domaine actif / en ligne ». */
export const ACTIVE_WEBSITE_DOMAIN_STATUSES: readonly WebsiteDomainActivationStatus[] = [
  WebsiteDomainActivationStatus.HTTPS_CONFIGURED,
  WebsiteDomainActivationStatus.K8S_CONFIGURED,
  WebsiteDomainActivationStatus.PROPELSIA_CONFIGURED,
];

/**
 * Statut de la configuration DNS d'un domaine.
 * ⚠️IMPACT — miroir de l'enum Prisma `WebsiteDomainDnsStatus`
 * (packages/backend/prisma-pg/schema.prisma). Toute valeur ajoutée/renommée ici
 * doit l'être aussi côté Prisma (via une migration de rename), et inversement.
 */
export enum WebsiteDomainDnsStatus {
  NONE = 'NONE',
  ASKED = 'ASKED',
  DONE = 'DONE',
}

/**
 * Statut du cycle de renouvellement annuel d'un domaine.
 * ⚠️IMPACT — miroir de l'enum Prisma `WebsiteDomainRenewalStatus`
 * (packages/backend/prisma-pg/schema.prisma). Toute valeur ajoutée/renommée ici
 * doit l'être aussi côté Prisma (via une migration de rename), et inversement.
 */
export enum WebsiteDomainRenewalStatus {
  SCHEDULED = 'SCHEDULED',
  IN_DUNNING = 'IN_DUNNING',
  RENEWED = 'RENEWED',
  UNPAID = 'UNPAID',
  CANCELED = 'CANCELED',
  // Prélèvement SEPA du renouvellement lancé (`renewal_payment_ref`) : ni payé ni impayé.
  PROCESSING = 'PROCESSING',
}

export const WebsiteDomainResponseSchema = Type.Object({
  id: Type.Number(),
  domain: Type.String(),
  is_primary: Type.Boolean(),
  dns_status: Type.String(),
  activation_status: Type.String(),
  // Achat (ou transfert) réglé par un prélèvement SEPA lancé, pas encore encaissé : ni payé ni impayé,
  // le domaine s'active à l'encaissement — aucun second paiement à proposer (badge « Prélèvement SEPA
  // en cours »). Même prédicat que `DnsDomainActionResponseSchema.sepa_debit_in_progress`.
  purchase_sepa_debit_in_progress: Type.Boolean(),
  created_at: Type.String({ format: 'date-time' }),
  // Renouvellement annuel (cf. domain-renewal). Formaté côté backend (date + prix).
  auto_renew: Type.Boolean(),
  renewal_status: Type.String(),
  next_renewal_at_formatted: Type.Optional(Type.String()),
  // Prix annuel déjà formaté (ex. "14,90 €") ; absent si domaine offert.
  renewal_price_formatted: Type.Optional(Type.String()), // TTC (débit réel)
  renewal_price_ht_formatted: Type.Optional(Type.String()), // HT (affiché comme partout)
});
export type WebsiteDomainResponseObject = Static<typeof WebsiteDomainResponseSchema>;
export const WebsiteStatusResponseSchema = Type.Object({
  has_website: Type.Boolean(),
  is_published: Type.Boolean(),
  last_published_at: Type.Optional(Type.String({ format: 'date-time' })),
  last_published_at_formatted: Type.Optional(Type.String()),
  has_domain: Type.Boolean(),
  domain: Type.Optional(Type.String()),
  default_subdomain: Type.String(),
  domains: Type.Array(WebsiteDomainResponseSchema),
  dns_status: Type.Optional(Type.String()),
  activation_status: Type.Optional(Type.String()),
  unpublished_changes: Type.Boolean(),
  unpublished_changes_count: Type.Number(),
  // Groupes de modifications non publiées, à plat mais hiérarchisés : une ligne
  // sans `parent_key` est une section (thème, en-tête/pied, une page), les lignes
  // qui la référencent sont son détail dépliable. `label` est une clé de
  // traduction ; `label_param` porte la valeur dynamique non traduisible
  // (titre de page) à interpoler dedans.
  unpublished_changes_details: Type.Array(
    Type.Object({
      key: Type.String(),
      label: Type.String(),
      label_param: Type.Optional(Type.String()),
      count: Type.Number(),
      parent_key: Type.Optional(Type.String()),
    }),
  ),
});
export const RevertChangeGroupRequestSchema = Type.Object({
  group_key: Type.String(),
});
// ─── Link Binding Resolution Schemas ─────────────────────────────────

export const ResolveLinkBindingsRequestSchema = Type.Object({
  keys: Type.Array(Type.String()),
  pages: Type.Array(
    Type.Object({
      id: Type.String(),
      slug: Type.String(),
      type: Type.String(),
    }),
  ),
});
export const ResolveLinkBindingsResponseSchema = Type.Object({
  resolved: Type.Record(
    Type.String(),
    Type.Object({
      url: Type.String(),
      label: Type.String(),
    }),
  ),
});
export type ResolveLinkBindingsResponseObject = Static<typeof ResolveLinkBindingsResponseSchema>;

export const LinkBindingOptionSchema = Type.Object({
  key: Type.String(),
  label: Type.String(),
  group: Type.String(),
});

export const LinkBindingOptionsResponseSchema = Type.Object({
  options: Type.Array(LinkBindingOptionSchema),
});
export type LinkBindingOptionsResponseObject = Static<typeof LinkBindingOptionsResponseSchema>;

// ─── Vues « assistant » du site web (tools Pulse, lecture seule) ─────────
// Résumés compacts du site pensés pour un LLM : le blob builder complet est
// beaucoup trop volumineux et bruité pour un contexte de conversation. Cf.
// docs/to_implement/pulse-interne-mission/gap-tools.md (axe B2).

export const AssistantWebsiteBlockSummarySchema = Type.Object({
  /** Id d'instance du bloc dans la config (stable, sert de cible d'édition future). */
  instance_id: Type.String(),
  /** Id du composant builder (ex. HEADER_HERO, TEXT_BLOCK, FAQ…). */
  component: Type.String(),
  variant: Type.Optional(Type.String()),
  /** Bloc PARTAGÉ entre plusieurs pages (menu, pied de page…) : lisible mais NON modifiable via l'assistant. */
  is_shared: Type.Optional(Type.Boolean()),
});

export const AssistantWebsitePageSummarySchema = Type.Object({
  page_id: Type.String(),
  title: Type.String(),
  type: Type.String(),
  /** Chemin public (« / », « /prestations »…) — absent pour une entrée lien externe. */
  path: Type.Optional(Type.String()),
  /** URL publique complète de la page (base = domaine primaire ou sous-domaine par défaut). */
  url: Type.Optional(Type.String()),
  in_menu: Type.Boolean(),
  /** Titre de la page parente pour une sous-page. */
  parent_title: Type.Optional(Type.String()),
  has_seo_override: Type.Boolean(),
  external_url: Type.Optional(Type.String()),
  blocks: Type.Array(AssistantWebsiteBlockSummarySchema),
});

export const AssistantWebsitePagesResponseSchema = Type.Object({
  has_website: Type.Boolean(),
  is_published: Type.Boolean(),
  /** Base publique du site (https://domaine ou sous-domaine par défaut). */
  base_url: Type.String(),
  unpublished_changes: Type.Boolean(),
  pages: Type.Array(AssistantWebsitePageSummarySchema),
});
export type AssistantWebsitePagesResponseObject = Static<typeof AssistantWebsitePagesResponseSchema>;

export const AssistantWebsiteBlockContentSchema = Type.Object({
  instance_id: Type.String(),
  component: Type.String(),
  variant: Type.Optional(Type.String()),
  /** Bloc PARTAGÉ entre plusieurs pages (menu, pied de page…) : lisible mais NON modifiable via l'assistant. */
  is_shared: Type.Optional(Type.Boolean()),
  /**
   * Champs textuels du bloc, aplatis (`title`, `items[0].text`…), tronqués.
   * Les valeurs non textuelles (images, ids, bindings) sont exclues.
   */
  texts: Type.Record(Type.String(), Type.String()),
});

export const AssistantSeoFindingSchema = Type.Object({
  level: Type.Union([Type.Literal('critical'), Type.Literal('warning'), Type.Literal('info')]),
  /** Code stable du constat (ex. SITE_DESCRIPTION_MISSING) — utile aux évals. */
  code: Type.String(),
  /** Constat + recommandation en français, prêt à être reformulé par Pulse. */
  message: Type.String(),
  page_title: Type.Optional(Type.String()),
  page_path: Type.Optional(Type.String()),
});

export const AssistantSeoAuditResponseSchema = Type.Object({
  /** Score global 0-100 (100 = aucun constat pénalisant). */
  score: Type.Number(),
  pages_checked: Type.Number(),
  critical_count: Type.Number(),
  warning_count: Type.Number(),
  info_count: Type.Number(),
  findings: Type.Array(AssistantSeoFindingSchema),
});
export type AssistantSeoFindingObject = Static<typeof AssistantSeoFindingSchema>;
export type AssistantSeoAuditResponseObject = Static<typeof AssistantSeoAuditResponseSchema>;

export const AssistantWebsitePageContentResponseSchema = Type.Object({
  page_id: Type.String(),
  title: Type.String(),
  path: Type.Optional(Type.String()),
  url: Type.Optional(Type.String()),
  seo: Type.Optional(
    Type.Object({
      heading: Type.Optional(Type.String()),
      title: Type.Optional(Type.String()),
      description: Type.Optional(Type.String()),
    }),
  ),
  blocks: Type.Array(AssistantWebsiteBlockContentSchema),
});
export type AssistantWebsitePageContentResponseObject = Static<typeof AssistantWebsitePageContentResponseSchema>;

// ─── Écritures « assistant » du site web (B2 lot 2) ─────────────────────────
// Écritures SÉMANTIQUES uniquement : le LLM ne manipule jamais le blob builder.
// Les chemins de textes sont EXACTEMENT ceux renvoyés par la vue page-content
// (`texts`), ce qui rend la boucle lecture→écriture symétrique et vérifiable.

export const AssistantUpdateBlockTextsSchema = Type.Object({
  page: Type.String({
    minLength: 1,
    description: 'Page ciblée : id, chemin (« /contact ») ou titre — mêmes règles que la vue page-content.',
  }),
  instance_id: Type.String({
    minLength: 1,
    description: "Id d'instance du bloc, tel que renvoyé par les vues pages / page-content.",
  }),
  texts: Type.Record(Type.String(), Type.String({ maxLength: 2000 }), {
    minProperties: 1,
    description:
      'Map `chemin → nouveau texte`. Chemins = EXACTEMENT ceux du champ `texts` de la vue page-content (ex. `title`, `items[0].text`) ; chaque chemin doit déjà exister et pointer un champ texte.',
  }),
});
export type AssistantUpdateBlockTextsObject = Static<typeof AssistantUpdateBlockTextsSchema>;

export const AssistantUpdateBlockTextsResponseSchema = Type.Object({
  page_id: Type.String(),
  instance_id: Type.String(),
  /** Chemins effectivement mis à jour. */
  updated_paths: Type.Array(Type.String()),
  /** Contenu du bloc après modification (mêmes clés que la vue page-content). */
  block: AssistantWebsiteBlockContentSchema,
  /** La modification est enregistrée en BROUILLON — publication via le builder. */
  is_draft: Type.Boolean(),
});
export type AssistantUpdateBlockTextsResponseObject = Static<typeof AssistantUpdateBlockTextsResponseSchema>;

export const AssistantUpdatePageSeoSchema = Type.Object({
  page: Type.String({
    minLength: 1,
    description: 'Page ciblée : id, chemin (« /contact ») ou titre — mêmes règles que la vue page-content.',
  }),
  heading: Type.Optional(
    Type.String({ maxLength: 120, description: 'Titre H1 affiché en haut de la page (PAS la meta description).' }),
  ),
  meta_title: Type.Optional(
    Type.String({ maxLength: 120, description: 'Meta title SEO (balise <title> de la page).' }),
  ),
  meta_description: Type.Optional(
    Type.String({
      maxLength: 300,
      description:
        'Meta description SEO de la page — pour toute demande de « meta description », remplir CE champ (pas heading).',
    }),
  ),
});
export type AssistantUpdatePageSeoObject = Static<typeof AssistantUpdatePageSeoSchema>;

export const AssistantUpdatePageSeoResponseSchema = Type.Object({
  page_id: Type.String(),
  seo: Type.Object({
    heading: Type.Optional(Type.String()),
    title: Type.Optional(Type.String()),
    description: Type.Optional(Type.String()),
  }),
  is_draft: Type.Boolean(),
});
export type AssistantUpdatePageSeoResponseObject = Static<typeof AssistantUpdatePageSeoResponseSchema>;

//LONGDO faire call API pour véfirer si tt les tables sont dans le transform ?
//DEPLOYDO lister tte les tables
//⚠️IMPACT sequin configmap in sequin, you need to update transform to read data from DB and transform them into SearchDocument
//⚠️IMPACT frontend avec input de recherche FILTER_OPTIONS
export enum TABLE_PRO_SEARCH_ENUM {
  TEAM_MEMBER = 'team_member',
  PRO_CUSTOMER = 'pro_customer',
  PRO_PLACE = 'pro_place',
  PRESTATION = 'prestation',
  PACK = 'pack',
  VOUCHER = 'voucher',
}
export type TABLE_PRO_SEARCH_TO_SYNC = `${TABLE_PRO_SEARCH_ENUM}`;
export const TABLE_PRO_SEARCH_VALUES = Object.values(TABLE_PRO_SEARCH_ENUM) as TABLE_PRO_SEARCH_TO_SYNC[];

export const SEARCH_TYPE_PARSE_INFO: Record<TABLE_PRO_SEARCH_TO_SYNC, { schema: TObject; icon: string }> = {
  team_member: {
    schema: ProTeamMemberArrayContentResponseSchema,
    icon: 'User',
  },
  pro_customer: { schema: ProCustomerResponseSchema, icon: 'Users' },
  pro_place: { schema: ProPlacesResponseSchema, icon: 'MapPin' },
  prestation: { schema: PrestationResponseSchema, icon: 'Search' },
  pack: {
    schema: PackResponseSchema,
    icon: 'Package',
  },
  voucher: {
    schema: VoucherResponseSchema,
    icon: 'Ticket',
  },
};

export const ProSearchResultSchema = Type.Object({
  table_name: Type.String(),
  icon: Type.String(),
  data: Type.Unknown(),
});
export type ProSearchResultObject = Static<typeof ProSearchResultSchema>;

//⚠️IMPACT function parsing on sequin pro_search_index_transform
export interface SearchDocument {
  id: string;
  table_name: TABLE_PRO_SEARCH_TO_SYNC;
  data: Record<string, unknown>;
  data_text: string;
  data_ngrams: string;
}

export const AppointmentPrestationResponseSchema = CatalogSelectionResponseSchema;

export const AppointmentRideDetailsResponseSchema = Type.Object({
  appointment_ride_details_id: Type.Number(),
  trip_leg: Type.Enum(RideTripLeg),
  pickup_address: Type.Optional(AddressSchema),
  dropoff_address: Type.Optional(AddressSchema),
  distance_km: Type.Optional(Type.Number()),
  estimated_duration_min: Type.Optional(Type.Number()),
  // Multi-véhicules : PART DU GROUPE portée par ce véhicule.
  passenger_count: Type.Optional(Type.Number()),
  luggage_count: Type.Optional(Type.Number()),
  small_luggage_count: Type.Optional(Type.Integer({ minimum: 0 })),
  large_luggage_count: Type.Optional(Type.Integer({ minimum: 0 })),
  // Position du véhicule dans la réservation (absent = course mono-véhicule). Les deux legs d'un
  // même véhicule la partagent : c'est la clé d'appariement aller/retour (groupRideLegsByVehicle).
  vehicle_index: Type.Optional(Type.Number()),
  // Prix de CE véhicule (A/R compris), porté par le leg ALLER. Brut pour le champ d'édition du
  // pro, formaté par le backend pour l'affichage (règle « Price Handling »).
  vehicle_price_minor: Type.Optional(Type.Number()),
  vehicle_price_string: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'vehicle_price_minor' } })),
  // Classe de véhicule prévue quand aucune unité n'a été affectée (surbooking) : « Van à attribuer ».
  requested_vehicle_name: Type.Optional(Type.String()),
});
export type AppointmentRideDetailsResponseObject = Static<typeof AppointmentRideDetailsResponseSchema>;

export const ReservationBaseResponseSchema = Type.Object({
  reservation_id: Type.Number(),
  status: Type.Enum(ReservationStatus),
  cancelled_at: Type.Optional(Type.String({ format: 'date-time' })),
  cancelled_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'cancelled_at', with_time: true } }),
  ),
  cancellation_reason: Type.Optional(Type.String()),
  // Client non présenté (« lapin ») : horodatage posé par le pro, la réservation reste CONFIRMED.
  no_show_at: Type.Optional(Type.String({ format: 'date-time' })),
  no_show_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'no_show_at', with_time: true } }),
  ),
  // Canal d'entrée de la réservation (statistiques de provenance) ; absent = non renseigné.
  booking_source: Type.Optional(Type.Enum(BookingSource)),
  // Résa déplacée par le client, en attente de validation du pro (`confirm_every_booking`) :
  // horaire qu'il avait validé, pour lui permettre d'y revenir.
  pending_reschedule_previous_start: Type.Optional(Type.String({ format: 'date-time' })),
  pending_reschedule_previous_start_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'pending_reschedule_previous_start', with_time: true } }),
  ),
  note: Type.Optional(Type.String()),
  color: Type.Optional(Type.String()),
  total_price: Type.Optional(Type.Number()),
  total_price_string: Type.Optional(
    Type.String({
      meta: { is_price: true, source_key: 'total_price' },
    }),
  ),
  booking_cancellation_refund_info: Type.Optional(BookingCancellationRefundInfoSchema),
  // « Reste à encaisser » de la RÉSERVATION, tous paiements confondus (cf. backend
  // `computeReservationRemainingCents`) : snapshot du paiement de réservation (panier mixte, solde
  // après acompte, empreinte/CB non capturée) moins les autres paiements encaissés et les bons
  // déduits par le pro ; à défaut de snapshot, total − bons − encaissé. Absent si rien ne reste dû,
  // si la réservation est annulée ou si le reste est inconnu (devis, import v1 sans snapshot).
  remaining_amount_on_site: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'remaining_amount_on_site_raw' } }),
  ),
  remaining_amount_on_site_raw: Type.Optional(Type.Number()),
  // Somme NETTE (remboursements déduits) encaissée sur tous les paiements de la réservation.
  // Émis uniquement quand > 0 ; absent sinon, pour que le front retombe sur le prix (renseigné sur le
  // détail du RDV, pas sur la liste).
  collected_amount: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'collected_amount_raw' } })),
  collected_amount_raw: Type.Optional(Type.Integer()),
  professional_id: Type.Number(),
  pro_customer_id: Type.Optional(Type.Number()),
  pro_customer: Type.Optional(AppointmentProCustomerResponseSchema),
  created_at: Type.String({ format: 'date-time' }),
  created_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'created_at', with_time: true } }),
  ),
  updated_at: Type.String({ format: 'date-time' }),
});

export const AppointmentResourceResponseSchema = Type.Object({
  pro_resource_id: Type.Number(),
  name: Type.String(),
  category: ProResourceCategoryEnum,
  type: Type.Optional(ProResourceTypeEnum),
  quantity_consumed: Type.Number(),
  party_size: Type.Number(),
  is_client_selected: Type.Boolean(),
  capacity: Type.Number(),
  quantity: Type.Number(),
  is_exclusive: Type.Boolean(),
});

/**
 * Résumé d'un appointment frère au sein d'une réservation multi-créneaux.
 * Exposé uniquement par le détail d'un appointment (`GET /app/appointment/:id`)
 * quand la réservation contient plus d'un créneau — sert à la modal agenda pour
 * afficher la portée réservation (reprogrammation/annulation atomiques).
 */
export const ReservationAppointmentSummarySchema = Type.Object({
  appointment_id: Type.Number(),
  start_pro: Type.String({ format: 'date-time' }),
  start_pro_string: Type.String({
    format: 'date-time',
    meta: { is_short: true, with_time: true },
  }),
  end_pro: Type.String({ format: 'date-time' }),
  time_range_string: Type.String({
    meta: { source_keys: ['start_pro', 'end_pro'], format_as: 'time-range' },
  }),
  // Prestations complètes du créneau (nom, prix, suppléments, quantité) — même
  // forme que `AppointmentResponseSchema.prestations`, pour un affichage unifié
  // de toutes les prestations de la réservation dans la modal.
  prestations: Type.Optional(Type.Array(AppointmentPrestationResponseSchema)),
  team_member_ids: Type.Optional(Type.Array(Type.Number())),
  // Membres HISTORISÉS du créneau (nom au moment de la réservation), repli live.
  team_members: Type.Optional(Type.Array(ProTeamMemberArrayContentResponseSchema)),
  recurrence: Type.Enum(BookingRecurrence),
  recurrence_rrule: Type.Optional(Type.String()),
  recurrence_label: Type.Optional(Type.String()),
  // Legs de course (RIDE) portés par ce créneau : un aller-retour daté éclate en deux
  // appointments (aller / retour) — la modal agenda reconstitue ainsi le trajet complet
  // quel que soit le créneau ouvert, chaque leg daté par l'horaire de son créneau.
  ride_details: Type.Optional(Type.Array(AppointmentRideDetailsResponseSchema)),
  // Ressources HISTORISÉES du créneau (classe de véhicule d'une course, salle, matériel) —
  // snapshot `pro_resource_history`, repli live. Alimente « Véhicules de la réservation ».
  resources: Type.Optional(Type.Array(AppointmentResourceResponseSchema)),
});
export type ReservationAppointmentSummaryObject = Static<typeof ReservationAppointmentSummarySchema>;

export const AppointmentResponseSchema = Type.Object({
  appointment_id: Type.Number(),
  start: Type.String({
    format: 'date-time',
  }),
  start_string: Type.String({
    format: 'date-time',
    meta: { source_key: 'start', is_short: true, with_time: true },
  }),
  end: Type.String({
    format: 'date-time',
  }),
  end_string: Type.String({
    format: 'date-time',
    meta: { source_key: 'end', is_short: true, with_time: true },
  }),
  start_time_string: Type.String({
    meta: { source_key: 'start', format_as: 'time' },
  }),
  end_time_string: Type.String({
    meta: { source_key: 'end', format_as: 'time' },
  }),
  time_range_public_string: Type.String({
    meta: { source_keys: ['start', 'end'], format_as: 'time-range' },
  }),
  start_pro: Type.String({
    format: 'date-time',
  }),
  start_pro_string: Type.String({
    format: 'date-time',
    meta: { is_short: true, with_time: true },
  }),
  end_pro: Type.String({
    format: 'date-time',
  }),
  end_pro_string: Type.String({
    format: 'date-time',
    meta: { is_short: true, with_time: true },
  }),
  start_pro_time_string: Type.String({
    meta: { source_key: 'start_pro', format_as: 'time' },
  }),
  end_pro_time_string: Type.String({
    meta: { source_key: 'end_pro', format_as: 'time' },
  }),
  time_range_string: Type.String({
    meta: { source_keys: ['start_pro', 'end_pro'], format_as: 'time-range' },
  }),
  name: Type.Optional(Type.String()),
  note: Type.Optional(Type.String()),
  color: Type.Optional(Type.String()),
  is_busy: Type.Optional(Type.Boolean()),
  pro_customer: Type.Optional(AppointmentProCustomerResponseSchema),
  pro_place_id: Type.Optional(Type.Number()),
  // Lieu de la réservation = objet lieu complet (`ProPlacesResponseSchema` réutilisé, comme le
  // roster `team_members`). Résolu backend : label/adresse/kind = snapshot HISTORISÉ (ce que le
  // client a réservé) ; type_config (lien visio/tel actionnable)/opening_hours/etc. = état live.
  // `pro_place_id` top-level reste le live (sélecteur d'édition) ; `pro_place.*` = le lieu booké.
  pro_place: Type.Optional(ProPlacesResponseSchema),
  customer_address: Type.Optional(AddressSchema),
  prestations: Type.Optional(Type.Array(AppointmentPrestationResponseSchema)),
  recurrence: Type.Enum(BookingRecurrence),
  recurrence_index: Type.Optional(Type.Number()),
  // Règle RRULE canonique de la série + libellé humain formaté backend
  // (formatRecurrenceRuleLabel) — absents pour un RDV non récurrent.
  recurrence_rrule: Type.Optional(Type.String()),
  recurrence_label: Type.Optional(Type.String()),
  type: Type.Optional(Type.Enum(AppointmentType)),
  // `reservation_id` / `reservation` deviennent optionnels pour accepter les
  // events importés depuis un calendrier externe (Google, Outlook, …) :
  // `is_external_block=true` ⇒ pas de réservation rattachée. Pour un RDV Cfixe
  // standard ces deux champs sont toujours présents.
  reservation_id: Type.Optional(Type.Number()),
  reservation: Type.Optional(ReservationBaseResponseSchema),
  /** True si l'event vient d'un agenda externe (read-only côté Cfixe). */
  is_external_block: Type.Optional(Type.Boolean()),
  ride_details: Type.Optional(Type.Array(AppointmentRideDetailsResponseSchema)),
  // Course multi-véhicules : nombre de véhicules de la RÉSERVATION (absent = 1). Alimente le
  // badge « Véhicule i/N » de l'agenda et le choix de portée du dialogue d'annulation.
  ride_vehicle_count: Type.Optional(Type.Number()),
  // Remboursement SUGGÉRÉ (jamais automatique) si le pro retire le véhicule de CE créneau :
  // prix du véhicule au prorata de ce qui a été encaissé en ligne, plafonné au remboursable.
  // Peuplé par le détail seulement, quand la réservation porte plus d'un véhicule.
  ride_vehicle_refund_suggestion_raw: Type.Optional(Type.Number()),
  ride_vehicle_refund_suggestion: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'ride_vehicle_refund_suggestion_raw' } }),
  ),
  // Créneaux frères de la réservation (y compris le courant), triés par début.
  // Peuplé uniquement par le détail (`GET /app/appointment/:id`) quand la
  // réservation contient plus d'un appointment.
  reservation_appointments: Type.Optional(Type.Array(ReservationAppointmentSummarySchema)),
  // Nombre de créneaux (non supprimés) de la réservation — présent sur la liste
  // agenda pour que le drag & drop décide localement single-slot vs multi-slot
  // sans fetch du détail.
  reservation_appointment_count: Type.Optional(Type.Number()),
  team_member_ids: Type.Optional(Type.Array(Type.Number())),
  // Membres HISTORISÉS (nom au moment de la réservation) — résolus backend depuis
  // `team_member_history`, repli live. Afficher ces noms, ne pas relire le roster live.
  team_members: Type.Optional(Type.Array(ProTeamMemberArrayContentResponseSchema)),
  resources: Type.Optional(Type.Array(AppointmentResourceResponseSchema)),
  has_associated_vouchers: Type.Optional(Type.Boolean()),
  // true si au moins un paiement de la réservation est encore actif (ni CANCELLED ni EXPIRED).
  has_associated_payment: Type.Optional(Type.Boolean()),
  // Tous les paiements non supprimés de la réservation (annulés/expirés compris), du plus ancien
  // au plus récent. Optionnel : transformToSchema rend `[]` en null, le front lit `?? []`.
  payments: Type.Optional(Type.Array(AppointmentPaymentSummarySchema)),
  // Code promo appliqué par le client à la réservation (snapshot : paiement, sinon redemption).
  // Peuplé par le détail seulement. `promo_code_restored` : utilisation rendue au client après
  // une annulation dans les délais.
  promo_code: Type.Optional(Type.String()),
  promo_code_label: Type.Optional(Type.String()),
  promo_discount_raw: Type.Optional(Type.Number()),
  promo_discount: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'promo_discount_raw' } })),
  promo_code_restored: Type.Optional(Type.Boolean()),
  // Une remise s'applique à la réservation (total après remise < prix normal) : pastille « % » de l'agenda.
  has_discount: Type.Optional(Type.Boolean()),
  // Bloc Montant (lignes au prix normal, remises, total après remise). Peuplé par le détail seulement.
  reservation_amounts: Type.Optional(ReservationAmountsSchema),
  custom_field_answers: Type.Optional(Type.Array(BookingCustomFieldAnswerDisplaySchema)),
  instructions: Type.Optional(Type.Array(BookingInstructionPublicSchema)),
});

export type AppointmentResponseObject = Static<typeof AppointmentResponseSchema>;

export const ListAppointmentResponseSchema = Type.Object({
  has_elements: Type.Boolean(),
  total: Type.Number(),
  items: Type.Array(AppointmentResponseSchema),
});

export type ListAppointmentResponseObject = Static<typeof ListAppointmentResponseSchema>;

export const CreateReservationRequestSchema = Type.Object({
  note: Type.Optional(Type.String()),
  total_price: Type.Optional(Type.Number()),
  pro_customer_id: Type.Optional(Type.Number()),
  appointment_ids: Type.Array(Type.Number()),
});

export type CreateReservationRequestObject = Static<typeof CreateReservationRequestSchema>;

export const UpdateReservationRequestSchema = Type.Object({
  status: Type.Optional(Type.Enum(ReservationStatus)),
  note: Type.Optional(Type.String()),
  total_price: Type.Optional(Type.Number()),
  pro_customer_id: Type.Optional(Type.Number()),
  appointment_ids: Type.Optional(Type.Array(Type.Number())),
});

export type UpdateReservationRequestObject = Static<typeof UpdateReservationRequestSchema>;

export const ReservationResponseSchema = Type.Object({
  ...ReservationBaseResponseSchema.properties,
  appointments: Type.Optional(Type.Array(AppointmentResponseSchema)),
});

export type ReservationResponseObject = Static<typeof ReservationResponseSchema>;

export const ListReservationResponseSchema = Type.Object({
  has_elements: Type.Boolean(),
  total: Type.Number(),
  items: Type.Array(ReservationResponseSchema),
});

export type ListReservationResponseObject = Static<typeof ListReservationResponseSchema>;

// ─── Ride Price Calculator (Public) ───────────────────────────────────

/**
 * Composition demandée par le client : N unités d'une classe de véhicule. Multi-véhicules, cf.
 * docs/to_implement/ride-multi-vehicle-booking-plan.md. Prioritaire sur `vehicle_id`.
 */
export const RideVehicleCompositionItemSchema = Type.Object({
  vehicle_id: Type.Integer(),
  quantity: Type.Integer({ minimum: 1, maximum: RIDE_MAX_VEHICLES_PER_BOOKING }),
});
export type RideVehicleCompositionItemObject = Static<typeof RideVehicleCompositionItemSchema>;

// Champs multi-véhicules communs aux requêtes devis / pré-check / checkout. Absents des deux =
// chemin MONO strictement inchangé (même code, même empreinte de devis).
const RideVehicleCompositionRequestFields = {
  // Composition explicite (« 2 vans + 1 berline »). Prioritaire sur vehicle_id, qui est ignoré.
  vehicles: Type.Optional(
    Type.Array(RideVehicleCompositionItemSchema, { minItems: 1, maxItems: RIDE_MAX_VEHICLES_PER_BOOKING }),
  ),
  // Autorise la RÉPARTITION automatique du groupe sur plusieurs véhicules (carte « Automatique »).
  // Le serveur ne répartit que si aucune classe ne peut porter le groupe, ou si les classes
  // adaptées sont toutes indisponibles.
  allow_split: Type.Optional(Type.Boolean()),
};

export const RidePriceCalculatorRequestSchema = Type.Object({
  professional_slug: Type.String(),
  pickup_address: AddressSchema,
  dropoff_address: AddressSchema,
  pickup_datetime: Type.String({ format: 'date-time' }),
  trip_type: Type.Union([Type.Literal('ONE_WAY'), Type.Literal('ROUND_TRIP')]),
  return_datetime: Type.Optional(Type.String({ format: 'date-time' })),
  passenger_count: Type.Number({ minimum: 1 }),
  luggage_count: Type.Optional(Type.Number({ minimum: 0 })),
  small_luggage_count: Type.Optional(Type.Integer({ minimum: 0 })),
  large_luggage_count: Type.Optional(Type.Integer({ minimum: 0 })),
  // Véhicule précis — DOUBLE usage selon le flux :
  // - PUBLIC (tunnel en accès direct) : FILTRE de pool. Le devis ne considère que CE véhicule
  //   (réservable : actif, POOL, tarifé, lié à ≥ 1 formule Course) et facture la formule la
  //   moins chère pour ce trajet parmi les siennes. JAMAIS un bypass d'availability : la
  //   cotation reste availability-aware et le checkout re-verrouille. Ignoré si prestation_id
  //   est fourni (mode vitrine « Réserver ce trajet » : le pool de la formule fait foi).
  // - AGENDA (estimation authentifiée calculatePriceForPro) : cotation déterministe de CE
  //   véhicule, sans lecture de disponibilité (sémantique historique).
  vehicle_id: Type.Optional(Type.Integer()),
  // Prestation RIDE choisie dans le tunnel : les véhicules candidats viennent de son pool
  // (liens pro_resource_prestation en booking_mode POOL). Absent = flux legacy (tous véhicules).
  prestation_id: Type.Optional(Type.Number()),
  ...RideVehicleCompositionRequestFields,
});
export type RidePriceCalculatorRequestObject = Static<typeof RidePriceCalculatorRequestSchema>;

/**
 * Une ligne = UN véhicule du devis (multi-véhicules). Le prix et la décomposition sont ceux de ce
 * véhicule : prix minimum et forfaits de zone s'appliquent PAR VÉHICULE, jamais sur le total.
 */
export const RideQuoteVehicleLineSchema = Type.Object({
  position: Type.Integer({ minimum: 1 }),
  vehicle_id: Type.Integer(),
  vehicle_name: Type.String(),
  prestation_id: Type.Optional(Type.Integer()),
  // Part du groupe allouée à ce véhicule (somme des lignes = groupe demandé).
  passenger_count: Type.Integer({ minimum: 1 }),
  luggage_count: Type.Optional(Type.Integer({ minimum: 0 })),
  small_luggage_count: Type.Optional(Type.Integer({ minimum: 0 })),
  large_luggage_count: Type.Optional(Type.Integer({ minimum: 0 })),
  price_minor_unit: Type.Number(),
  formatted_price: Type.String(),
  route_price_minor_unit: Type.Optional(Type.Number()),
  approach_fees_minor_unit: Type.Optional(Type.Number()),
  handling_fees_minor_unit: Type.Optional(Type.Number()),
  waiting_fees_minor_unit: Type.Optional(Type.Number()),
  is_geofence_exception: Type.Optional(Type.Boolean()),
});
export type RideQuoteVehicleLineObject = Static<typeof RideQuoteVehicleLineSchema>;

export const RidePriceCalculatorResponseSchema = Type.Object({
  estimated_price_minor_unit: Type.Number(),
  formatted_price: Type.String(),
  distance_km: Type.Number(),
  estimated_duration_min: Type.Number(),
  formatted_duration: Type.String(),
  currency: Type.String(),
  approach_fees_minor_unit: Type.Optional(Type.Number()),
  formatted_approach_fees: Type.Optional(Type.String()),
  waiting_fees_minor_unit: Type.Optional(Type.Number()),
  formatted_waiting_fees: Type.Optional(Type.String()),
  handling_fees_minor_unit: Type.Optional(Type.Number()),
  formatted_handling_fees: Type.Optional(Type.String()),
  route_price_minor_unit: Type.Optional(Type.Number()),
  formatted_route_price: Type.Optional(Type.String()),
  is_geofence_exception: Type.Optional(Type.Boolean()),
  vehicle_name: Type.Optional(Type.String()),
  // Polyline encodé Google de l'itinéraire retenu pour le prix — tracé par la mini-carte du
  // tunnel (Maps JS `geometry.encoding`), sans appel Directions côté client.
  route_polyline: Type.Optional(Type.String()),
  // Politique de paiement de la formule EFFECTIVEMENT retenue par le devis (celle choisie, ou en
  // « peu importe » celle du véhicule affecté) : le tunnel s'en sert pour ses libellés (CTA, avis,
  // barre de confiance) — autoritaire quand aucune formule n'est présélectionnée côté client.
  payment_type: Type.Optional(PrestationPaymentTypeSchema()),
  // Détail paiement/remboursement — alimente la MÊME timeline que le tunnel booking classique
  // (composant `PaymentTimeline`). Calculé au devis à partir du prix + politique de la formule.
  payment_infos: Type.Optional(PaymentInfosSchema),
  cancellation_deadline: Type.Optional(Type.String()),
  cancellation_deadline_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'cancellation_deadline', with_time: true } }),
  ),
  // Empreinte OPAQUE des champs CONTRACTUELS du devis (montants, type de paiement, deadline
  // d'annulation, devise — jamais la durée/distance ni le véhicule, qui fluctuent sans impact
  // facturation). Le tunnel la renvoie telle quelle au checkout (expected_quote_fingerprint) :
  // si la recotation serveur produit une empreinte différente, le checkout refuse en 409
  // RIDE_QUOTE_CHANGED au lieu d'engager le client sur des conditions jamais affichées.
  quote_fingerprint: Type.Optional(Type.String()),
  // Formule (prestation RIDE) EFFECTIVEMENT retenue par le devis : celle choisie, ou en « peu
  // importe » / véhicule précis celle du véhicule coté. Le tunnel s'en sert pour n'afficher que les
  // champs personnalisés (booking_custom_field) applicables à la course réservée.
  prestation_id: Type.Optional(Type.Number()),
  // ─── Multi-véhicules ─── Absents = devis mono (1 véhicule), champs ci-dessus inchangés.
  // Présents dès que la requête portait `vehicles` ou `allow_split`. Les champs de premier niveau
  // (prix, décomposition, payment_infos, deadline) valent alors pour le TOTAL de la composition.
  vehicle_count: Type.Optional(Type.Integer({ minimum: 1 })),
  vehicles: Type.Optional(Type.Array(RideQuoteVehicleLineSchema)),
  // Formules de toutes les lignes : le tunnel filtre les champs personnalisés sur leur union.
  prestation_ids: Type.Optional(Type.Array(Type.Number())),
});
export type RidePriceCalculatorResponseObject = Static<typeof RidePriceCalculatorResponseSchema>;

// ─── Ride Config Schemas ──────────────────────────────────────────────

// Forfait de zone surchargé sur UNE plage horaire (unités mineures). Prioritaire sur le
// price_override du véhicule et sur le default_price de la zone.
export const TimeScheduleZonePriceSchema = Type.Object({
  pro_ride_zone_id: Type.Integer(),
  price: Type.Integer({ minimum: 0 }),
});
export type TimeScheduleZonePriceObject = Static<typeof TimeScheduleZonePriceSchema>;

export const TimeScheduleEntrySchema = Type.Object({
  day: Type.Union([Type.Number({ minimum: 0, maximum: 6 }), Type.Literal('PUBLIC_HOLIDAYS')]),
  start: Type.Number({ minimum: 0, maximum: 23 }),
  end: Type.Number({ minimum: 0, maximum: 23 }),
  base_rate_per_km: Type.Optional(Type.Number({ minimum: 0 })),
  return_rate_per_km: Type.Optional(Type.Number({ minimum: 0 })),
  handling_fees: Type.Optional(Type.Number({ minimum: 0 })),
  waiting_fees_hour_rate: Type.Optional(Type.Number({ minimum: 0 })),
  approach_rate_one_way_per_km: Type.Optional(Type.Number({ minimum: 0 })),
  approach_rate_two_way_per_km: Type.Optional(Type.Number({ minimum: 0 })),
  // Forfaits de zone propres à la plage (parité v1 : chaque tier horaire portait ses forfaits).
  // Pas de valeur `null` : le TypeBox pipe supprime les null optionnels (stripNullOptionalFields).
  zone_prices: Type.Optional(Type.Array(TimeScheduleZonePriceSchema)),
  // Zones liées au véhicule qui NE s'appliquent PAS sur cette plage (tarif au km à la place).
  disabled_zone_ids: Type.Optional(Type.Array(Type.Integer())),
});
export type TimeScheduleEntryObject = Static<typeof TimeScheduleEntrySchema>;

// Tarif dégressif : seuil kilométrique MARGINAL (cf. ride-distance-bands.ts). Au-delà de `from_km`,
// les km suivants sont facturés avec `discount_pct` % de réduction sur le taux actif.
export const DistanceBandSchema = Type.Object({
  from_km: Type.Integer({ minimum: DISTANCE_BAND_MIN_KM }),
  discount_pct: Type.Integer({ minimum: DISTANCE_BAND_DISCOUNT_MIN, maximum: DISTANCE_BAND_DISCOUNT_MAX }),
});
export type DistanceBandObject = Static<typeof DistanceBandSchema>;
export const DistanceBandsSchema = Type.Array(DistanceBandSchema, { maxItems: DISTANCE_BANDS_MAX });

export const RideConfigResponseSchema = Type.Object({
  pro_ride_config_id: Type.Number(),
  is_payment_on: Type.Boolean(),
  // La politique de paiement des courses n'est plus globale : elle vit sur CHAQUE prestation
  // « Course » (prestation.payment_type / prestation.percentage), comme les autres prestations.
  // `is_payment_on` reste l'interrupteur global du tunnel VTC.
  min_price: Type.Optional(Type.Number()),
  formatted_min_price: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'min_price' } })),
  min_waiting_minutes: Type.Optional(Type.Number()),
  max_waiting_minutes: Type.Optional(Type.Number()),
  approach_fees_enabled: Type.Boolean(),
  starting_point_lat: Type.Optional(Type.Number()),
  starting_point_lon: Type.Optional(Type.Number()),
  starting_point_place_id: Type.Optional(Type.String()),
  starting_point_radius_meters: Type.Optional(Type.Number()),
  avoid_tolls: Type.Boolean(),
  avoid_highways: Type.Boolean(),
  avoid_ferries: Type.Boolean(),
  pricing_preference: Type.String(),
  // Surbooking volontaire : flotte saturée → la course est quand même acceptée SANS véhicule
  // affecté (le pro l'attribue plus tard). Défaut false = comportement historique.
  // NB : la zone de couverture VTC ne vit PAS ici — elle est lue depuis les zones
  // d'intervention des lieux (pro_place.geo_json) rattachés aux disponibilités.
  accept_overbooking: Type.Boolean(),
  // Réservation multi-véhicules (groupe réparti sur plusieurs véhicules). Défaut true.
  multi_vehicle_enabled: Type.Boolean(),
  // Grille dégressive globale, héritée par les véhicules sans grille propre. Absent = aucune
  // (transformToSchema remplace un tableau vide par null).
  distance_bands: Type.Optional(DistanceBandsSchema),
});
export type RideConfigResponseObject = Static<typeof RideConfigResponseSchema>;

export const UpsertRideConfigRequestSchema = Type.Object({
  is_payment_on: Type.Boolean(),
  // La politique de paiement (type + acompte) est portée par la prestation « Course », plus par la
  // config globale : elle s'édite via le formulaire de prestation (payment_type / percentage).
  // Colonnes Int en base → Type.Integer : une décimale (ex. « 30.5 ») passait avec Type.Number puis
  // crashait Prisma (Float fourni pour un Int) en 500 ; Type.Integer la refuse en 422 propre.
  min_price: Type.Optional(Type.Integer()),
  min_waiting_minutes: Type.Optional(Type.Integer()),
  max_waiting_minutes: Type.Optional(Type.Integer()),
  approach_fees_enabled: Type.Boolean(),
  // lat/lon = colonnes Float → restent Type.Number ; le rayon est un Int.
  starting_point_lat: Type.Optional(Type.Number()),
  starting_point_lon: Type.Optional(Type.Number()),
  starting_point_place_id: Type.Optional(Type.String()),
  starting_point_radius_meters: Type.Optional(Type.Integer()),
  avoid_tolls: Type.Boolean(),
  avoid_highways: Type.Boolean(),
  avoid_ferries: Type.Boolean(),
  pricing_preference: Type.String(),
  // Optionnel (rétro-compat clients API) : absent → colonne inchangée (spread upsert).
  // Le formulaire pro l'envoie toujours.
  accept_overbooking: Type.Optional(Type.Boolean()),
  // Optionnel (rétro-compat clients API) : absent → colonne inchangée (spread upsert).
  multi_vehicle_enabled: Type.Optional(Type.Boolean()),
  // Grille dégressive globale. Absent → colonne inchangée ; [] → aucune grille globale.
  distance_bands: Type.Optional(DistanceBandsSchema),
});
export type UpsertRideConfigRequestObject = Static<typeof UpsertRideConfigRequestSchema>;

// ─── Ride Vehicle Pricing (éditeur pro) ───────────────────────────────
// Un pricing tier par véhicule (taux de base + fenêtres horaires jour/nuit/fériés)
// + zones tarifaires réutilisables associées au véhicule. Édité en full-replace.

// GeoJSON FeatureCollection dessiné via GeoJsonEditor. Stocké en Json Prisma —
// on ne valide pas la structure ici (Type.Any) mais on la persiste telle quelle.
const GeoJsonSchema = Type.Any();

// ─── Ride Zones (trajets forfaitaires réutilisables) ──────────────────
// Une zone = un TRAJET forfaitaire : zone de départ + zone d'arrivée (GeoJSON) + prix par
// défaut, au niveau du professionnel. Associée à N véhicules (pro_resource_ride_zone) avec
// prix surchargeable par véhicule. Ce schéma sert de body API ET de schéma de formulaire
// (pattern equipment) : les meta pilotent le rendu DynamicForm (is_price → PriceInput,
// geo_json → GeoJsonEditor), les title/placeholder se traduisent dans DYNAMIC_FORM.

export const RideZoneRequestSchema = Type.Object({
  name: Type.String({
    minLength: 1,
    maxLength: 255,
    title: 'INPUT_RIDE_ZONE_NAME_TITLE',
    placeholder: 'INPUT_RIDE_ZONE_NAME_PLACEHOLDER',
  }),
  default_price: Type.Integer({
    minimum: 0,
    title: 'INPUT_RIDE_ZONE_DEFAULT_PRICE_TITLE',
    placeholder: 'INPUT_RIDE_ZONE_DEFAULT_PRICE_PLACEHOLDER',
    meta: { is_price: true },
  }),
  polygon_start: Type.Any({
    title: 'INPUT_RIDE_ZONE_POLYGON_START_TITLE',
    placeholder: 'INPUT_RIDE_ZONE_POLYGON_START_PLACEHOLDER',
    meta: { input_type: 'geo_json' },
  }),
  polygon_end: Type.Any({
    title: 'INPUT_RIDE_ZONE_POLYGON_END_TITLE',
    placeholder: 'INPUT_RIDE_ZONE_POLYGON_END_PLACEHOLDER',
    meta: { input_type: 'geo_json' },
  }),
});
export type RideZoneRequestObject = Static<typeof RideZoneRequestSchema>;

export const RideZoneResponseSchema = Type.Object({
  pro_ride_zone_id: Type.Number(),
  name: Type.String(),
  // Unités mineures BRUTES : requises pour pré-remplir le champ prix de l'éditeur (input numérique).
  default_price: Type.Number(),
  // Prix formaté par le backend (devise + locale du pro) pour l'AFFICHAGE — règle « Price Handling » :
  // le frontend affiche cette chaîne, il ne reformate jamais default_price lui-même.
  default_price_string: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'default_price' } })),
  polygon_start: GeoJsonSchema,
  polygon_end: GeoJsonSchema,
  // Nombre de véhicules associés — affiché sur la ligne de liste.
  linked_vehicle_count: Type.Number(),
});
export type RideZoneResponseObject = Static<typeof RideZoneResponseSchema>;

export const RideZoneListResponseSchema = Type.Object({
  items: Type.Array(RideZoneResponseSchema),
  total: Type.Number(),
});
export type RideZoneListResponseObject = Static<typeof RideZoneListResponseSchema>;

// Lien véhicule ↔ zone du pricing : price_override absent = prix par défaut de la zone.
export const VehiclePricingZoneLinkRequestSchema = Type.Object({
  pro_ride_zone_id: Type.Integer(),
  price_override: Type.Optional(Type.Integer({ minimum: 0 })), // unités mineures
});
export type VehiclePricingZoneLinkRequestObject = Static<typeof VehiclePricingZoneLinkRequestSchema>;

export const VehiclePricingZoneLinkResponseSchema = Type.Object({
  pro_ride_zone_id: Type.Number(),
  name: Type.String(),
  default_price: Type.Number(), // unités mineures
  price_override: Type.Optional(Type.Number()), // unités mineures
});
export type VehiclePricingZoneLinkResponseObject = Static<typeof VehiclePricingZoneLinkResponseSchema>;

export const VehiclePricingRequestSchema = Type.Object({
  base_rate_per_km: Type.Number({ minimum: 0 }),
  return_rate_per_km: Type.Number({ minimum: 0 }),
  handling_fees: Type.Number({ minimum: 0 }),
  waiting_fees_hour_rate: Type.Number({ minimum: 0 }),
  approach_rate_one_way_per_km: Type.Number({ minimum: 0 }),
  approach_rate_two_way_per_km: Type.Number({ minimum: 0 }),
  // Prix minimum du véhicule (unités mineures, colonne Int → Integer). Absent = plancher global
  // de la config course ; renseigné, il le remplace (forfaits de zone compris).
  min_price: Type.Optional(Type.Integer({ minimum: 0 })),
  // Tarif dégressif du véhicule : INHERIT = grille de la config course, NONE = aucun, CUSTOM =
  // `distance_bands` (non vide). Mode absent → réglage actuel du véhicule conservé (un client qui
  // ignore le champ n'efface pas la grille).
  distance_bands_mode: Type.Optional(Type.Enum(RideDistanceBandsMode)),
  distance_bands: Type.Optional(DistanceBandsSchema),
  schedule: Type.Array(TimeScheduleEntrySchema),
  zones: Type.Array(VehiclePricingZoneLinkRequestSchema),
});
export type VehiclePricingRequestObject = Static<typeof VehiclePricingRequestSchema>;

export const VehiclePricingResponseSchema = Type.Object({
  pro_resource_pricing_tier_id: Type.Optional(Type.Number()),
  base_rate_per_km: Type.Number(),
  return_rate_per_km: Type.Number(),
  handling_fees: Type.Number(),
  waiting_fees_hour_rate: Type.Number(),
  approach_rate_one_way_per_km: Type.Number(),
  approach_rate_two_way_per_km: Type.Number(),
  min_price: Type.Optional(Type.Number()), // unités mineures, absent = plancher global
  distance_bands_mode: Type.Enum(RideDistanceBandsMode),
  distance_bands: DistanceBandsSchema, // grille propre (CUSTOM), triée par from_km ; [] sinon
  global_distance_bands: DistanceBandsSchema, // grille de la config course (affichée en INHERIT)
  schedule: Type.Array(TimeScheduleEntrySchema),
  zones: Type.Array(VehiclePricingZoneLinkResponseSchema),
});
export type VehiclePricingResponseObject = Static<typeof VehiclePricingResponseSchema>;

// Aperçu du tarif dégressif dans l'éditeur (valeurs NON enregistrées) : prix au km d'un aller
// simple, hors frais, avec et sans seuils, formatés par le backend (jamais de montant brut).
export const DistanceBandsPreviewRequestSchema = Type.Object({
  // Taux aller du véhicule ; absent (grille globale) = DISTANCE_BANDS_PREVIEW_EXAMPLE_RATE_MINOR.
  base_rate_per_km: Type.Optional(Type.Number({ minimum: 0 })),
  distance_bands: DistanceBandsSchema,
});
export type DistanceBandsPreviewRequestObject = Static<typeof DistanceBandsPreviewRequestSchema>;

export const DistanceBandsPreviewItemSchema = Type.Object({
  distance_km: Type.Integer(),
  price_string: Type.String({ meta: { is_price: true, source_key: 'price_minor' } }),
  linear_price_string: Type.String({ meta: { is_price: true, source_key: 'linear_price_minor' } }),
  is_discounted: Type.Boolean(),
});
export type DistanceBandsPreviewItemObject = Static<typeof DistanceBandsPreviewItemSchema>;

export const DistanceBandsPreviewResponseSchema = Type.Object({
  // Taux au km utilisé pour l'aperçu, formaté (celui du véhicule ou le taux d'exemple).
  rate_per_km_string: Type.String({ meta: { is_price: true, source_key: 'rate_per_km_minor' } }),
  items: Type.Array(DistanceBandsPreviewItemSchema),
});
export type DistanceBandsPreviewResponseObject = Static<typeof DistanceBandsPreviewResponseSchema>;

// ─── Ride Public Tunnel (client) ──────────────────────────────────────

// Prestation RIDE proposée dans le tunnel : le client choisit une « formule de course »
// (ex. Berline, Van) ; le système affectera un véhicule libre de son pool au checkout.
// pax/bagages = agrégats du pool (min des mins / max des maxes non-nuls) pour la garde capacité.
export const RidePublicPrestationSchema = Type.Object({
  prestation_id: Type.Number(),
  name: Type.String(),
  description: Type.Optional(Type.String()),
  // Politique de paiement PROPRE à cette formule (calquée sur la prestation) : chaque formule
  // Course porte sa propre politique. Dégradée en FREE si le pro ne peut pas encaisser en ligne
  // (pas de compte Connect capable) — le tunnel reste alors ouvert en « demande sans paiement ».
  payment_type: PrestationPaymentTypeSchema(),
  // Acompte (payment_type = DEPOSIT), exposé uniquement si l'encaissement en ligne est possible :
  // pourcentage du prix OU montant fixe par véhicule (unités mineures) — jamais les deux. Aucun
  // des deux → repli moteur (30 %).
  deposit_percentage: Type.Optional(Type.Number()),
  deposit_fixed_amount: Type.Optional(Type.Number()),
  pax_min: Type.Optional(Type.Number()),
  pax_max: Type.Optional(Type.Number()),
  small_luggage_min: Type.Optional(Type.Number()),
  small_luggage_max: Type.Optional(Type.Number()),
  large_luggage_min: Type.Optional(Type.Number()),
  large_luggage_max: Type.Optional(Type.Number()),
  // Trajet par défaut de la formule (mêmes champs que la carte « Course » du tunnel classique,
  // cf. BookingPrestationItemSchema) : permet au tunnel de préremplir les adresses quand le client
  // arrive en mode vitrine par deep-link `?rp=<id>` (fiche prestation d'un site custom, /book?mode=
  // ride&rp=) — sans lui, seule la carte du tunnel classique portait le trajet et le deep-link
  // ouvrait un formulaire vide. Une adresse `*_locked` n'est pas modifiable par le client.
  ride_default_pickup_address: Type.Optional(AddressSchema),
  ride_default_dropoff_address: Type.Optional(AddressSchema),
  ride_pickup_address_locked: Type.Optional(Type.Boolean()),
  ride_dropoff_address_locked: Type.Optional(Type.Boolean()),
});
export type RidePublicPrestationObject = Static<typeof RidePublicPrestationSchema>;

// Véhicule RÉSERVABLE exposé au tunnel pour la sélection « véhicule précis » par le client :
// actif, POOL, tarifé (BOOKABLE_RIDE_VEHICLE_WHERE) et lié à ≥ 1 formule Course — mêmes filtres
// que les candidats du devis, pour que tout véhicule listé soit effectivement cotable.
export const RidePublicVehicleSchema = Type.Object({
  pro_resource_id: Type.Number(),
  name: Type.String(),
  // Description « vitrine » du véhicule (texte brut, saisie pro) — affichée clampée sur la carte
  // du sélecteur du tunnel. Clé OMISE quand non renseignée (rendu inchangé côté tunnel).
  description: Type.Optional(Type.String()),
  pax_min: Type.Optional(Type.Number()),
  pax_max: Type.Optional(Type.Number()),
  small_luggage_min: Type.Optional(Type.Number()),
  small_luggage_max: Type.Optional(Type.Number()),
  large_luggage_min: Type.Optional(Type.Number()),
  large_luggage_max: Type.Optional(Type.Number()),
  // Visuels du véhicule — MÊME format signé que la liste pro-resource (ProResourceResponseSchema) :
  // objets CroppableImageResponse (url = clé S3 brute + crop, preview_url/original_url signés par
  // le branch `croppable_image` de transformToSchema). Le tunnel n'affiche que la 1re image
  // (preview_url) mais le tableau complet est exposé, comme partout ailleurs. Clé OMISE sans visuel.
  pictures: Type.Optional(Type.Array(CroppableImageResponseSchema)),
  // Formules Course (exposées dans `prestations`) servies par ce véhicule. En vitrine (formule imposée),
  // le tunnel teste « le groupe tient-il dans UN véhicule ? » sur les vrais véhicules de la formule, et
  // non sur ses bornes agrégées, qui disaient « tient » pour un pool aux capacités croisées.
  prestation_ids: Type.Optional(Type.Array(Type.Integer())),
});
export type RidePublicVehicleObject = Static<typeof RidePublicVehicleSchema>;

export const RidePublicConfigResponseSchema = Type.Object({
  professional_id: Type.Number(),
  professional_name: Type.String(),
  professional_logo: Type.Optional(Type.String()),
  currency: Type.String(),
  // Prestations RIDE avec ≥ 1 véhicule POOL réservable — SEUL flux du tunnel (le fallback
  // « choix véhicule » a été supprimé). Toujours présent ; vide = tunnel indisponible
  // (défensif : features.can_ride masque normalement l'onglet en amont).
  prestations: Type.Array(RidePublicPrestationSchema),
  // Véhicules réservables (dédupliqués, ordre sort_order puis id) : le client peut choisir un
  // véhicule PRÉCIS ou « peu importe » dans le tunnel en accès direct (vehicle_id des requêtes
  // publiques). Un véhicule multi-formules est facturé à la formule la moins chère du trajet.
  vehicles: Type.Optional(Type.Array(RidePublicVehicleSchema)),
  allowed_payment_methods: Type.Optional(Type.Array(Type.String())),
  // Le pro valide manuellement chaque réservation → après paiement la course reste en attente de
  // confirmation (le tunnel adapte son message de fin au lieu d'annoncer une course confirmée).
  requires_pro_confirmation: Type.Optional(Type.Boolean()),
  // Fuseau du pro : les heures « murales » saisies dans le tunnel sont interprétées dans CE fuseau
  // (comme au backend). Sans lui, la garde front « date dans le futur » utilise le fuseau du
  // navigateur → faux refus/passages dès que le client réserve depuis un autre fuseau que le pro.
  timezone: Type.Optional(Type.String()),
  // Champs personnalisés (questionnaire) du pro applicables aux courses : sans contrainte de
  // prestation ou liés à au moins une formule Course proposée, et sans contrainte de lieu (une
  // course n'a pas de lieu : un champ restreint à des lieux n'y apparaît jamais). MÊME source et même
  // schéma que le tunnel classique (BookingPrestationsResponseSchema.custom_fields) ; le tunnel
  // filtre ensuite sur la formule réservée (isBookingPerimeterApplicable).
  custom_fields: Type.Optional(Type.Array(BookingCustomFieldPublicSchema)),
  // ─── Multi-véhicules ─── Le tunnel n'affiche l'interface multi que si le multi est actif ET
  // que le plafond autorise ≥ 2 véhicules ; sinon rendu et requêtes strictement mono.
  multi_vehicle_enabled: Type.Optional(Type.Boolean()),
  max_vehicles_per_booking: Type.Optional(Type.Integer({ minimum: 1 })),
  // Capacité théorique maximale du pro (somme des plus grandes unités de flotte, dans la limite
  // du plafond) : borne les compteurs passagers / bagages du tunnel. Clé omise si une borne
  // manque sur un véhicule (= aucune contrainte de cet axe).
  max_group_passengers: Type.Optional(Type.Integer({ minimum: 1 })),
  max_group_small_luggage: Type.Optional(Type.Integer({ minimum: 0 })),
  max_group_large_luggage: Type.Optional(Type.Integer({ minimum: 0 })),
});
export type RidePublicConfigResponseObject = Static<typeof RidePublicConfigResponseSchema>;

export const RideContactSchema = Type.Object({
  firstname: Type.String({ maxLength: maxStrLength }),
  lastname: Type.String({ maxLength: maxStrLength }),
  email: Type.String({ format: 'email', maxLength: maxStrLength }),
  phone_number: Type.Optional(Type.String({ maxLength: 32, meta: { input_type: 'phone_number' } })),
  note: Type.Optional(Type.String({ maxLength: 1000 })),
});
export type RideContactObject = Static<typeof RideContactSchema>;

export const RideCheckoutRequestSchema = Type.Object({
  pickup_address: AddressSchema,
  dropoff_address: AddressSchema,
  pickup_datetime: Type.String({ format: 'date-time' }),
  trip_type: Type.Union([Type.Literal('ONE_WAY'), Type.Literal('ROUND_TRIP')]),
  return_datetime: Type.Optional(Type.String({ format: 'date-time' })),
  passenger_count: Type.Number({ minimum: 1 }),
  luggage_count: Type.Optional(Type.Number({ minimum: 0 })),
  small_luggage_count: Type.Optional(Type.Integer({ minimum: 0 })),
  large_luggage_count: Type.Optional(Type.Integer({ minimum: 0 })),
  // Canal d'entrée déclaré par le client HTTP (site, widget, lien direct…) ; les canaux internes
  // (agenda, assistant, import) sont refusés ici. Absent ⇒ ONLINE. cf. booking_source_namespace.ts.
  source: Type.Optional(PublicBookingSourceSchema),
  // Campagne email/SMS à l'origine de la réservation (lien marqué `cid`), vérifiée côté serveur
  // (campagne du pro, envoyée) et ignorée sans erreur sinon.
  campaign_id: Type.Optional(Type.Integer({ minimum: 1 })),
  // Prestation RIDE choisie. OPTIONNELLE : « peu importe » (le client ne choisit pas de formule)
  // → le serveur affecte le véhicule le moins cher disponible parmi toutes les formules et
  // enregistre la formule à laquelle il appartient. Fournie → l'affectation reste dans son pool.
  prestation_id: Type.Optional(Type.Number()),
  // Véhicule précis choisi par le client (« peu importe » = absent) : FILTRE de pool —
  // l'affectation ne considère que CE véhicule, availability re-verrouillée au checkout (jamais
  // un bypass). Ignoré si prestation_id est fourni. Le tunnel renvoie le MÊME vehicle_id que
  // celui du devis, pour que la recotation serveur (et son empreinte) reste identique.
  vehicle_id: Type.Optional(Type.Integer()),
  contact: RideContactSchema,
  // Empreinte du devis ACCEPTÉ par le client (champ quote_fingerprint de la réponse du
  // calculateur, renvoyé tel quel — contenu opaque). OPTIONNELLE pour la rétro-compatibilité
  // des clients API ; le tunnel l'envoie TOUJOURS. Fournie mais différente de la recotation
  // serveur → 409 RIDE_QUOTE_CHANGED : jamais d'engagement sur des conditions non affichées.
  expected_quote_fingerprint: Type.Optional(Type.String({ maxLength: 128 })),
  // Réponses aux champs personnalisés — MÊME forme que PreReservationRequestSchema.custom_field_answers
  // (valeurs sérialisées en chaîne). Persistées après création de la réservation, par son uuid.
  custom_field_answers: Type.Optional(Type.Array(BookingCustomFieldAnswerSchema)),
  ...RideVehicleCompositionRequestFields,
});
export type RideCheckoutRequestObject = Static<typeof RideCheckoutRequestSchema>;

export const RideCheckoutResponseSchema = Type.Object({
  reservation_uuid: Type.String(),
  payment_uuid: Type.Optional(Type.String()),
  requires_payment: Type.Boolean(),
  amount_minor_unit: Type.Number(),
  formatted_amount: Type.String(),
  // Nombre de véhicules réservés (absent = 1) : le récapitulatif de fin annonce « N véhicules ».
  vehicle_count: Type.Optional(Type.Integer({ minimum: 1 })),
  // ─── Suivi des conversions (`booking_confirmed`) ─── `amount_minor_unit` ne convient pas : c'est
  // le montant du paiement à régler ou à garantir en ligne (total pour un paiement plein, une
  // empreinte ou une carte enregistrée ; acompte ; 0 sans paiement en ligne).
  // Total TTC de la course en centimes (le tunnel course n'applique ni bon ni carte cadeau).
  net_total_raw: Type.Optional(Type.Number()),
  // Montant réellement débité en ligne à la réservation, en centimes : paiement plein ou acompte,
  // 0 pour une empreinte, une carte enregistrée ou une course réglée sur place.
  amount_to_pay_today_raw: Type.Optional(Type.Number()),
  // Devise du pro (code ISO 4217, tel que stocké sur le professionnel).
  currency: Type.Optional(Type.String()),
  // Formule (prestation RIDE) retenue. Absents quand les véhicules d'une réservation
  // multi-véhicules relèvent de formules différentes.
  prestation_id: Type.Optional(Type.Number()),
  prestation_name: Type.Optional(Type.String()),
});
export type RideCheckoutResponseObject = Static<typeof RideCheckoutResponseSchema>;

// ─── Ride Availability Pre-check (Public) ─────────────────────────────
// Pré-check CONSULTATIF appelé par le tunnel à la saisie de l'heure : « au moins un véhicule
// du pool est-il libre ? ». Sans verrou ni transaction — le checkout reste l'autorité
// (revalidation sous FOR UPDATE). Ne bloque jamais le CTA côté tunnel.

export const RideAvailabilityCheckRequestSchema = Type.Object({
  // OPTIONNELLE : formule choisie → on évalue son pool ; « peu importe » (absente) → on évalue
  // tous les véhicules des formules Course (disponible dès qu'un véhicule est libre).
  prestation_id: Type.Optional(Type.Number()),
  // Véhicule précis choisi (« peu importe » = absent) : le pré-check n'évalue que CE véhicule.
  // Ignoré si prestation_id est fourni (le pool de la formule fait foi).
  vehicle_id: Type.Optional(Type.Integer()),
  pickup_datetime: Type.String({ format: 'date-time' }),
  trip_type: Type.Union([Type.Literal('ONE_WAY'), Type.Literal('ROUND_TRIP')]),
  return_datetime: Type.Optional(Type.String({ format: 'date-time' })),
  passenger_count: Type.Number({ minimum: 1 }),
  luggage_count: Type.Optional(Type.Number({ minimum: 0 })),
  small_luggage_count: Type.Optional(Type.Integer({ minimum: 0 })),
  large_luggage_count: Type.Optional(Type.Integer({ minimum: 0 })),
  // Durée estimée du trajet (issue d'un devis précédent) pour dimensionner les fenêtres
  // d'occupation. Absente → 60 min par défaut.
  estimated_duration_min: Type.Optional(Type.Integer({ minimum: 1, maximum: 1440 })),
  // Point de prise en charge (OPTIONNEL, les DEUX coordonnées ou aucune) : confronté aux zones
  // d'intervention (pro_place.geo_json) des lieux portés par les fenêtres de dispo couvrantes →
  // cause PICKUP_OUTSIDE_ZONE. Absent : check purement temporel (comportement historique).
  pickup_latitude: Type.Optional(Type.Number({ minimum: -90, maximum: 90 })),
  pickup_longitude: Type.Optional(Type.Number({ minimum: -180, maximum: 180 })),
  ...RideVehicleCompositionRequestFields,
});
export type RideAvailabilityCheckRequestObject = Static<typeof RideAvailabilityCheckRequestSchema>;

// Cause d'indisponibilité du pré-check : fenêtres d'ouverture (OUTSIDE_OPENING_HOURS),
// délai minimum avant réservation (TOO_SOON), horizon de réservation (TOO_FAR), aucun
// véhicule libre du pool (NO_VEHICLE), aucun chauffeur (membre actif) libre (NO_DRIVER —
// jamais contourné par le surbooking véhicules), ou prise en charge hors des zones
// d'intervention (PICKUP_OUTSIDE_ZONE — seulement si la requête porte le point de prise en
// charge). Le tunnel affiche un message dédié par cause.
export const RideUnavailabilityReasonSchema = Type.Union([
  Type.Literal('OUTSIDE_OPENING_HOURS'),
  Type.Literal('TOO_SOON'),
  Type.Literal('TOO_FAR'),
  Type.Literal('NO_VEHICLE'),
  Type.Literal('NO_DRIVER'),
  Type.Literal('PICKUP_OUTSIDE_ZONE'),
  // Multi-véhicules : aucune composition dans la limite ne peut accueillir le groupe.
  Type.Literal('GROUP_TOO_LARGE'),
]);
export type RideUnavailabilityReasonObject = Static<typeof RideUnavailabilityReasonSchema>;

export const RideAvailabilityCheckResponseSchema = Type.Object({
  available: Type.Boolean(),
  // Leg indisponible (aller ou retour) quand available = false et qu'un seul leg bloque.
  unavailable_leg: Type.Optional(Type.Enum(RideTripLeg)),
  // Optionnel (rétro-compat) : absent = comportement historique (indispo véhicule).
  reason: Type.Optional(RideUnavailabilityReasonSchema),
  // Surbooking (pro_ride_config.accept_overbooking) : flotte saturée mais la demande sera
  // quand même acceptée — le tunnel affiche une note informative non bloquante.
  // Optionnel (rétro-compat) : absent = comportement historique.
  overbooking: Type.Optional(Type.Boolean()),
  // Multi-véhicules : nombre MINIMAL de véhicules nécessaires au groupe (absent = 1).
  vehicle_count: Type.Optional(Type.Integer({ minimum: 1 })),
});
export type RideAvailabilityCheckResponseObject = Static<typeof RideAvailabilityCheckResponseSchema>;
